import { DocButton, StorageIndicator, deleteEntityDocuments, useDocCounts } from './DocumentPanel'
import PrintDocument from './PrintDocument'
import React, { useState, useEffect, useCallback, useRef } from 'react'
import * as XLSX from 'xlsx'
import { supabase } from '../lib/supabase'
import { useProjectAuth } from './useProjectAuth'
import { useAuth } from '../context/AuthContext'
import { getCached, setCached, invalidate } from '../lib/queryCache'
import { APPROVAL_LIMITS, getRequiredApprover } from '../lib/approvalLimits'
import { normSC, resolveDirectKey, resolveIndirectKey, isDirectPO, buildCostCodeMaps } from './budgetVsCommitment'
// Live Forecast — official Source of Truth (ADD_Forecast_Architecture.md
// SSOT). resolveCurrentForecastState() internally calls getOrCreateDraft(),
// which may INSERT a new forecast_snapshots row if no Draft exists yet for
// this project/source/period -- this is a documented, intentional side
// effect shared by every consumer of this function (Dashboard.jsx and
// CostControl.jsx both call it from read-only screens the same way),
// not something specific to this screen. See buildCostControlData.js's own
// "SIDE EFFECT NOTE" comment on resolveCurrentForecastState() for the
// authoritative statement of this.
import { resolveCurrentForecastState, buildForecastNodeIndex } from '../lib/buildCostControlData'

async function logAudit(entityId, entityType, action, actorName, comment, prevStatus, newStatus, projectId) {
  try {
    await supabase.from('audit_trail').insert({
      entity_id: entityId, entity_type: entityType, action,
      actor_name: actorName, comment, previous_status: prevStatus,
      new_status: newStatus, project_id: projectId,
    })
  } catch(e) { console.error('audit log failed', e) }
}

// Phase PO-3 (architectural consolidation): normSC, resolveDirectKey,
// resolveIndirectKey, isDirectPO now live exclusively in
// budgetVsCommitment.js (imported above) — this used to have local
// duplicate definitions here, byte-for-byte identical to a copy also
// found in PrintDocument.jsx. See that module for the single source of
// truth and full rationale comments.

// Phase PO-3: fetches and caches the full cost_codes tree for a project
// (id, code, level, parent_id only -- lightweight), used to resolve both
// Direct (Sub Code -> Resource Code) and Indirect (Cost Code L1) keys.
// Cached via the existing queryCache module (1-minute TTL) since this
// tree changes rarely within a session.
async function getCachedCostCodeMaps(projectId) {
  const cacheKey = `po3_cost_codes_tree::${projectId}`
  let tree = getCached(cacheKey)
  if (!tree) {
    const { data } = await supabase.from('cost_codes')
      .select('id, code, level, parent_id')
      .eq('project_id', projectId)
    tree = data || []
    setCached(cacheKey, tree)
  }
  return buildCostCodeMaps(tree)
}

const fmt = (n) => n ? Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'
const fmtM = (n) => { const v = Number(n||0); if(v>=1e6) return (v/1e6).toFixed(2)+'M'; if(v>=1e3) return (v/1e3).toFixed(1)+'K'; return v.toFixed(2) }

const XIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>)
const CheckIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-4 h-4"><polyline points="20 6 9 17 4 12"/></svg>)
const PlusIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>)
const SearchIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35" strokeLinecap="round"/></svg>)
const TrashIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" strokeLinecap="round" strokeLinejoin="round"/></svg>)
const ChevronIcon = ({ open }) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className={`w-4 h-4 transition-transform ${open?'rotate-180':''}`}><polyline points="6 9 12 15 18 9"/></svg>)
const DownloadIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" strokeLinecap="round" strokeLinejoin="round"/></svg>)
const UploadIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" strokeLinecap="round" strokeLinejoin="round"/></svg>)

const Toast = ({ message, type, onClose }) => (
  <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-2xl text-sm font-medium ${type==='success'?'bg-[#1A7A4A] text-white':'bg-red-600 text-white'}`}>
    {type==='success'?<CheckIcon/>:<XIcon/>}<span>{message}</span>
    <button onClick={onClose} className="ml-2 opacity-70 hover:opacity-100"><XIcon/></button>
  </div>
)

const statusColors = {
  draft: 'bg-[#EDE7DA] text-[#6B655C] border-[#DDD9D0]',
  approved: 'bg-[#DDD9D0] text-[#1A7A4A] border-[#1A7A4A]/30',
  closed: 'bg-[#DDD9D0] text-[#1C1A18] border-purple-200',
  cancelled: 'bg-red-100 text-red-700 border-red-200',
}

// Approval workflow stage colors -- distinct concept from status
// (operational/financial state e.g. approved/closed/cancelled),
// displayed alongside it, never in place of it.
const stageColors = { draft:'bg-gray-100 text-gray-700', cost_control:'bg-blue-100 text-blue-700', director:'bg-purple-100 text-purple-700', approved:'bg-green-100 text-green-700', rejected:'bg-red-100 text-red-700' }

// Explicit per-column widths for the main PO table, shared via
// <colgroup> between the separate sticky-header table and the
// scrolling body table so both compute identical column widths --
// without this, the browser's default table layout algorithm could
// size columns slightly differently in each table (since each table
// lays out independently based on its own cell content), breaking
// header/body alignment. 14 columns total: checkbox + 13 header
// labels (PO Number..Inc.VAT + unlabeled Actions). Sums to exactly
// the table's existing 1550px minWidth.
const poColWidths = [44, 130, 170, 90, 90, 110, 100, 120, 100, 100, 100, 110, 110, 176]

// Explicit per-column widths for the Rental Equipment PO main table --
// separate from poColWidths (Material PO) since this table has a
// completely different 8-column set. Equipment and Actions get extra
// room since both can contain multiple joined values/buttons
// (equipment_type list joined with ', ', multiple approval action
// buttons) that would otherwise compress awkwardly.
const repColWidths = [44, 140, 180, 140, 220, 130, 100, 200]

const poTypeColors = {
  standard: 'bg-[#DDD9D0] text-[#1A7A4A] border-[#1A7A4A]/30',
  complex: 'bg-[#DDD9D0] text-[#1A7A4A] border-[#DDD9D0]',
  equipment: 'bg-amber-100 text-amber-700 border-amber-200',
  service: 'bg-[#DDD9D0] text-[#1C1A18] border-purple-200',
}

// ── PO Modal ──────────────────────────────────────────────────────────────────
const POModal = ({ po, project, costCodes, subCodesList, analysisCodes, cachedLines, onSave, onClose, currentUserName, can }) => {
  const [saving, setSaving] = useState(false)
  const stage = po?.approval_stage || 'draft'
  // Once a PO leaves draft/rejected, nobody except cost_control can touch it — and even
  // cost_control is restricted to sub_code / cost_code only, in any post-draft stage (including approved).
  const isLockedForProcurement = !!po && !(can('PO.Submit') ? ['draft', 'rejected'].includes(stage) : can('PO.ApproveCostControl'))
  const isCostControlPartial = !!po && can('PO.ApproveCostControl') && !can('PO.Submit') && ['cost_control', 'director', 'approved'].includes(stage)
  const isReadOnly = isLockedForProcurement // fully read-only modal for any non-cost_control role post-submit
  const emptyLine = () => ({
    _id: Math.random(), ref_code: '', sub_code: '', description: '', unit: '',
    qty: '', unit_rate: '', amount_inc_vat: '', cost_code_id: '', boq_item_id: null,
    addendum_no: po?.pending_addendum_no || null,
  })
  const [form, setForm] = useState(po ? {
    ...po,
    // Addendum drafts (po.__isAddendumDraft, no po.id) carry their
    // inherited line items in po.__addendumLines, stripped of any old
    // id — these become the STARTING lines the user edits, exactly as
    // cachedLines does for a real existing PO below. A normal Edit of
    // an existing PO still gets lines:[] here, filled in by the
    // useEffect right after (cachedLines or a fresh DB fetch).
    lines: po.__isAddendumDraft && po.__addendumLines ? po.__addendumLines.map(l => ({ ...l, _id: Math.random() })) : [],
  } : {
    po_number: '', vendor: '', po_type: 'standard', cost_type: 'direct',
    sub_code: '', contract_ref: '', issue_date: new Date().toISOString().split('T')[0],
    status: 'draft', vat_pct: 15, notes: '', cost_code_id: '', logistics_type: 'oceanarium',
    currency_multiplier: 1,
    // Transaction currency defaults to the project's own currency (rate=1,
    // no conversion needed) until the user picks a different one.
    currency: project?.currency || 'SAR',
    exchange_rate_applied: 1,
    lines: [emptyLine()]
  })
  const [linesLoading, setLinesLoading] = useState(!!po && !po.__isAddendumDraft)
  const [selectedLines, setSelectedLines] = useState([])
  const [currencies, setCurrencies] = useState([])
  const UNIT_OPTIONS = ['TON','M2','M3','KG','LM','NO','LS','M','HR','DAY','L','BAG','SET','ROLL','PC']

  useEffect(() => {
    supabase.from('currencies').select('code,name,symbol').eq('is_active', true).order('code')
      .then(({ data }) => setCurrencies(data || []))
  }, [])

  // Auto-resolve the exchange rate whenever the transaction currency
  // changes on a NEW PO (an existing PO keeps its own historical
  // exchange_rate_applied -- never re-resolved live, per PART 7/16).
  useEffect(() => {
    if (po) return
    if (!project?.currency) return
    if (form.currency === project.currency) {
      setForm(f => f.exchange_rate_applied === 1 ? f : { ...f, exchange_rate_applied: 1 })
      return
    }
    supabase.rpc('get_exchange_rate', { p_from: form.currency, p_to: project.currency }).then(({ data }) => {
      if (data != null) setForm(f => ({ ...f, exchange_rate_applied: data }))
    })
  }, [po, project?.currency, form.currency])

  // Brand-new PO only (never an existing PO being edited, and never an
  // addendum draft -- an addendum inherits its parent PO's own vat_pct,
  // already present in `po`, not the project's current configuration).
  // The `15` initializer above is only ever the empty-form placeholder
  // before this resolves; this effect overwrites it with the project's
  // actual configured rate the instant it loads, exactly once.
  useEffect(() => {
    if (po) return
    if (!project?.id) return
    supabase.rpc('get_project_vat_config', { p_project_id: project.id }).then(({ data }) => {
      const row = data?.[0]
      if (row?.vat_treatment === 'standard' && row.vat_rate != null) {
        setForm(f => ({ ...f, vat_pct: row.vat_rate }))
      } else if (row?.vat_treatment === 'zero_rated') {
        setForm(f => ({ ...f, vat_pct: 0 }))
      }
      // exempt / not_applicable / no configuration: leave the placeholder
      // as-is -- the user must set it explicitly, never silently forced.
    })
  }, [po, project?.id])

  useEffect(() => {
    setSelectedLines([])
    // Addendum drafts already have their starting lines set directly in
    // the useState initializer above — nothing to fetch, and no po.id
    // exists yet to fetch by anyway (it's not a saved PO).
    if (po?.__isAddendumDraft) return
    if (!po?.id) return
    if (cachedLines && cachedLines.length > 0) {
      const loaded = cachedLines.map(l => ({ ...l, _id: l.id }))
      setForm(f => ({ ...f, lines: loaded }))
      setLinesLoading(false)
      return
    }
    setLinesLoading(true)
    supabase.from('po_line_items').select('id,po_id,ref_code,sub_code,description,cost_code_id,unit,qty,unit_rate,amount_exc_vat,amount_inc_vat,addendum_no').eq('po_id', po.id).order('created_at').then(({ data }) => {
      const loaded = (data || []).map(l => ({ ...l, _id: l.id }))
      setForm(f => ({ ...f, lines: loaded.length ? loaded : [emptyLine()] }))
      setLinesLoading(false)
    })
  }, [po?.id])

  const h = (k, v) => setForm(f => ({ ...f, [k]: v }))
  const hLine = (idx, k, v) => setForm(f => {
    const lines = [...f.lines]
    const targets = (selectedLines.includes(idx) && selectedLines.length > 1) ? selectedLines : [idx]
    targets.forEach(i => {
      lines[i] = { ...lines[i], [k]: v }
      if (k === 'qty' || k === 'unit_rate') {
        const qty = parseFloat(k==='qty'?v:lines[i].qty)||0
        const rate = parseFloat(k==='unit_rate'?v:lines[i].unit_rate)||0
        lines[i].amount_exc_vat_calc = qty * rate
        lines[i].amount_inc_vat = (qty * rate * (1 + (parseFloat(f.vat_pct)||15)/100)).toFixed(2)
      }
    })
    return { ...f, lines }
  })
  const toggleLineSelect = (idx) => setSelectedLines(s => s.includes(idx) ? s.filter(i=>i!==idx) : [...s, idx])
  const toggleSelectAllLines = () => setSelectedLines(s => s.length === form.lines.length ? [] : form.lines.map((_,i)=>i))

  const addLine = () => setForm(f => ({ ...f, lines: [...f.lines, emptyLine()] }))
  const removeLine = (idx) => { setSelectedLines([]); setForm(f => ({ ...f, lines: f.lines.filter((_,i)=>i!==idx) })) }

  const multiplier = parseFloat(form.currency_multiplier) || 1
  const totalExcVat = form.lines.reduce((s,l) => s + ((parseFloat(l.qty)||0)*(parseFloat(l.unit_rate)||0)), 0)
  const vatAmt = totalExcVat * (parseFloat(form.vat_pct)||15) / 100
  const totalIncVat = totalExcVat + vatAmt
  const totalExcVatSAR = totalExcVat * multiplier
  const totalIncVatSAR = totalIncVat * multiplier

  const [vendorSearch, setVendorSearch] = useState(form.vendor||'')
  const [vendorResults, setVendorResults] = useState([])
  const [showVendorDD, setShowVendorDD] = useState(false)
  const [dupWarning, setDupWarning] = useState(null)
  const vendorRef = React.useRef(null)
  const vendorSearchTimer = React.useRef(null)

  // Vendor typeahead search — debounced
  function searchVendors(q) {
    if (vendorSearchTimer.current) clearTimeout(vendorSearchTimer.current)
    if (!q || q.length < 2) { setVendorResults([]); return }
    vendorSearchTimer.current = setTimeout(async () => {
      const { data } = await supabase.from('vendors').select('id,trade_name,legal_name,vat_number,payment_terms')
        .or(`trade_name.ilike.%${q}%,legal_name.ilike.%${q}%`).eq('is_approved',true).limit(8)
      setVendorResults(data||[])
    }, 300)
  }

  function selectVendor(v) {
    h('vendor', v.trade_name)
    h('vendor_id', v.id)
    setVendorSearch(v.trade_name)
    setShowVendorDD(false)
    setVendorResults([])
    checkDuplicate(v.trade_name, form.sub_code)
  }

  // Duplicate check
  async function checkDuplicate(vendor, subCode) {
    if (!vendor || !project?.id) return
    const { data } = await supabase.from('purchase_orders')
      .select('po_number,status').eq('project_id',project.id)
      .ilike('vendor', vendor).in('status',['draft','approved'])
    const dups = (data||[]).filter(p => !po || p.po_number !== po.po_number)
    if (dups.length > 0) setDupWarning(`⚠️ Similar PO already exists for this vendor: ${dups.map(d=>d.po_number).join(', ')}`)
    else setDupWarning(null)
  }

  return (
    <div style={{ position:'fixed', inset:0, zIndex:300, background:'#F3F4F6', overflowY:'auto', fontFamily:'inherit' }}>
      {/* ── Full Page Header ── */}
      <div style={{ background:'#fff', borderBottom:'1px solid #E5E7EB', padding:'14px 24px', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:10 }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <button onClick={onClose}
            style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'5px 10px', borderRadius:6, border:'1px solid #E5E7EB', background:'#fff', color:'#6B7280', fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>
            ← Back
          </button>
          <div>
            <h1 style={{ fontSize:17, fontWeight:800, color:'#0F1017' }}>{po ? `Edit ${po.po_number}` : 'New Purchase Order'}</h1>
            <p style={{ fontSize:11, color:'#6B7280' }}>{project?.name}</p>
          </div>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button onClick={onClose} style={{ padding:'6px 14px', borderRadius:6, border:'1px solid #E5E7EB', background:'#fff', color:'#6B7280', fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>Cancel</button>
          <button onClick={async ()=>{
              if (saving || isReadOnly) return
              setSaving(true)
              try { await onSave(form, totalExcVatSAR, totalIncVatSAR, { multiplier, originalExcVat: totalExcVat, originalIncVat: totalIncVat }) }
              finally { setSaving(false) }
            }}
            disabled={saving || isReadOnly}
            style={{ padding:'6px 16px', borderRadius:6, background: (saving || isReadOnly) ? '#9CA3AF' : '#1A7A4A', color:'#fff', fontSize:12, fontWeight:600, cursor: (saving || isReadOnly) ?'not-allowed':'pointer', border:'none', fontFamily:'inherit' }}>
            {isReadOnly ? '🔒 Locked' : (saving ? 'Saving...' : (po ? 'Save Changes' : 'Create PO'))}
          </button>
        </div>
      </div>

      <div style={{ maxWidth:960, margin:'0 auto', padding:'20px 24px', display:'flex', flexDirection:'column', gap:16 }}>

        {isLockedForProcurement && (
          <div style={{ padding:'10px 14px', background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:8, fontSize:12, color:'#991B1B', fontWeight:600 }}>
            🔒 This PO has been submitted for approval and cannot be edited now. It will become editable again only if it is Rejected and returns to Draft status.
          </div>
        )}
        {isCostControlPartial && (
          <div style={{ padding:'10px 14px', background:'#EFF6FF', border:'1px solid #BFDBFE', borderRadius:8, fontSize:12, color:'#1E40AF', fontWeight:600 }}>
            ℹ️ At this stage, you can only edit the Sub Code and Cost Code. Quantity and Rate are locked.
          </div>
        )}

        <fieldset disabled={isReadOnly} style={{ display:'contents' }}>

        {/* Duplicate Warning */}
        {dupWarning && (
          <div style={{ padding:'10px 14px', background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:8, fontSize:12, color:'#92400E', fontWeight:600 }}>
            {dupWarning}
          </div>
        )}

        {/* ── Section 1: PO Header ── */}
        <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:10, padding:20 }}>
          <div style={{ fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.1em', color:'#9CA3AF', marginBottom:14, paddingBottom:8, borderBottom:'1px solid #F3F4F6' }}>Purchase Order Details</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 pb-6 border-b border-[#E8E4DC]">
          <div>
            <label className="block text-xs font-medium text-[#6B655C] mb-1">PO Number *</label>
            <input value={form.po_number} onChange={e=>h('po_number',e.target.value)} placeholder="PO-2025-001"
              className="w-full border border-[#DDD9D0] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]"/>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6B655C] mb-1">PO Date</label>
            <input type="date" value={form.issue_date||''} onChange={e=>h('issue_date',e.target.value)}
              className="w-full border border-[#DDD9D0] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]"/>
          </div>
          <div style={{ position:'relative' }} ref={vendorRef}>
            <label style={{ display:'block', fontSize:11, fontWeight:600, color:'#6B7280', marginBottom:4 }}>Supplier / Vendor *</label>
            <input value={vendorSearch}
              onChange={e=>{ setVendorSearch(e.target.value); h('vendor',e.target.value); searchVendors(e.target.value); setShowVendorDD(true) }}
              onFocus={()=>{ if(vendorSearch.length>=2) setShowVendorDD(true) }}
              onBlur={()=>setTimeout(()=>setShowVendorDD(false),200)}
              placeholder="Search vendor name…"
              className="w-full border border-[#DDD9D0] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]"/>
            {showVendorDD && vendorResults.length > 0 && (
              <div style={{ position:'absolute', top:'calc(100% + 4px)', left:0, right:0, background:'#fff', border:'1px solid #E5E7EB', borderRadius:8, boxShadow:'0 8px 24px rgba(0,0,0,.1)', zIndex:50, maxHeight:200, overflowY:'auto' }}>
                {vendorResults.map(v => (
                  <div key={v.id} onMouseDown={()=>selectVendor(v)}
                    style={{ padding:'8px 12px', cursor:'pointer', borderBottom:'1px solid #F3F4F6', fontSize:12 }}
                    onMouseEnter={e=>e.currentTarget.style.background='#F9FAFB'}
                    onMouseLeave={e=>e.currentTarget.style.background='#fff'}>
                    <div style={{ fontWeight:600, color:'#0F1017' }}>{v.trade_name}</div>
                    <div style={{ fontSize:10, color:'#9CA3AF' }}>{v.vat_number} · {v.payment_terms}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6B655C] mb-1">Status</label>
            <select value={form.status} onChange={e=>h('status',e.target.value)}
              className="w-full border border-[#DDD9D0] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]">
              {/* #1: a NEW PO (including an Addendum draft) cannot be created as 'approved' — approval happens only through the approval workflow.
                  An existing PO (form.id) keeps the full list, so existing approved POs still display and behave exactly as before. */}
              {(form.id ? ['draft','approved','closed','cancelled'] : ['draft','closed','cancelled']).map(s=><option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6B655C] mb-1">PO Type *</label>
            <select value={form.po_type} onChange={e=>h('po_type',e.target.value)}
              className="w-full border border-[#DDD9D0] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]">
              <option value="standard">Material Standard</option>
              <option value="complex">Material Complex</option>
              <option value="equipment">Equipment</option>
              <option value="service">Service</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6B655C] mb-1">Cost Type *</label>
            <select value={form.cost_type} onChange={e=>h('cost_type',e.target.value)}
              className="w-full border border-[#DDD9D0] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]">
              <option value="direct">Direct</option>
              <option value="indirect">Indirect</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6B655C] mb-1">Type (Log / Ocn)</label>
            <select value={form.logistics_type||'oceanarium'} onChange={e=>h('logistics_type',e.target.value)}
              className="w-full border border-[#DDD9D0] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]">
              <option value="oceanarium">Ocn — Oceanarium</option>
              <option value="logistics">Log — Logistics</option>
            </select>
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-[#6B655C] mb-1">Sub Code (PO Level)</label>
            <select value={form.sub_code||''} onChange={e=>{
              const code = e.target.value
              const sub = subCodesList.find(s=>s.code===code)
              h('sub_code', code)
              if(sub) { h('description', sub.description); h('cost_code_id', sub.costCodeId || '') }
            }}
              className="w-full border border-[#DDD9D0] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]">
              <option value="">— Optional —</option>
              {subCodesList.map(s=><option key={s.id} value={s.code}>{s.code} — {s.description}</option>)}
            </select>
            {/* Cost Code + Resource Code are derived automatically from the selected Sub Code — never entered manually */}
            {form.sub_code && (() => {
              const sel = subCodesList.find(s => s.code === form.sub_code)
              if (!sel) return null
              return (
                <div className="mt-1.5 space-y-0.5">
                  <p className="text-xs text-[#6B655C]">
                    Cost Code: <span className="font-mono font-semibold text-[#1A7A4A]">{sel.costCode}</span> — {sel.costCodeDescription}
                  </p>
                  {sel.resourceCode && (
                    <p className="text-xs text-purple-700">
                      Resource Code: <span className="font-mono font-semibold">{sel.resourceCode}</span> — {sel.resourceCodeDescription}
                    </p>
                  )}
                </div>
              )
            })()}
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6B655C] mb-1">Contract Ref</label>
            <input value={form.contract_ref||''} onChange={e=>h('contract_ref',e.target.value)}
              className="w-full border border-[#DDD9D0] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]"/>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6B655C] mb-1">VAT %</label>
            <input type="number" value={form.vat_pct||15} onChange={e=>h('vat_pct',e.target.value)}
              className="w-full border border-[#DDD9D0] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]"/>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6B655C] mb-1">Currency</label>
            <select value={form.currency || project?.currency || 'SAR'} onChange={e=>h('currency', e.target.value)}
              disabled={!!po}
              className="w-full border border-[#DDD9D0] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]">
              {currencies.map(c => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
            </select>
            {form.currency && project?.currency && form.currency !== project.currency && (
              <p className="text-xs mt-1" style={{ color:'#1A7A4A' }}>
                Rate: 1 {form.currency} = {form.exchange_rate_applied} {project.currency}
              </p>
            )}
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-[#6B655C] mb-1">Notes</label>
            <input value={form.notes||''} onChange={e=>h('notes',e.target.value)}
              className="w-full border border-[#DDD9D0] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]"/>
          </div>
        </div>

        </div>

        {/* ── Section 2: Line Items ── */}
        <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:10, padding:20 }}>
        <div className="mb-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-[#2E2C29]">Line Items</h4>
            {!isCostControlPartial && (
              <button onClick={addLine} className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-[#1A7A4A] border border-[#DDD9D0] rounded-lg hover:bg-[#EDE7DA]">
                <PlusIcon/> Add Line
              </button>
            )}
          </div>
          {linesLoading ? (
            <div className="flex items-center justify-center py-8 text-[#9A948C]">
              <div className="w-5 h-5 border-2 border-[#1A7A4A]/50 border-t-[#1A7A4A] rounded-full animate-spin mr-2"/>
              Loading line items...
            </div>
          ) : (
          <>
          <datalist id="unit-options">
            {UNIT_OPTIONS.map(u => <option key={u} value={u}/>)}
          </datalist>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-[#F5F1EA]">
                <tr>
                  <th className="px-2 py-2">
                    <input type="checkbox" checked={selectedLines.length===form.lines.length && form.lines.length>0} onChange={toggleSelectAllLines}/>
                  </th>
                  {['Ref Code','Sub Code','Sub Desc','Item Description','Cost Code','Unit','Qty','Unit Rate','Exc.VAT','Inc.VAT',''].map(h=>(
                    <th key={h} className="text-left px-2 py-2 font-medium text-[#6B655C] whitespace-nowrap">{h}</th>
                  ))}
                </tr>
                {selectedLines.length > 1 && (
                  <tr>
                    <td colSpan={12} className="px-2 py-1 text-[11px] font-medium" style={{background:'#FFF3E0',color:'#B06D00'}}>
                      {selectedLines.length} بنود محددة — أي تعديل هيتطبق عليهم كلهم
                    </td>
                  </tr>
                )}
              </thead>
              <tbody className="divide-y divide-[#E8E4DC]">
                {form.lines.map((line, idx) => (
                  <tr key={line._id||idx} style={selectedLines.includes(idx)?{background:'#FFF8E8'}:undefined}>
                    <td className="px-2 py-1">
                      <input type="checkbox" checked={selectedLines.includes(idx)} onChange={()=>toggleLineSelect(idx)}/>
                    </td>
                    <td className="px-2 py-1">
                      <input value={line.ref_code||''} onChange={e=>hLine(idx,'ref_code',e.target.value)} placeholder="MAT-001" disabled={isCostControlPartial}
                        className="w-24 border border-[#DDD9D0] rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#1A7A4A] disabled:bg-[#F3F4F6] disabled:cursor-not-allowed"/>
                    </td>
                    <td className="px-2 py-1">
                      <select value={line.sub_code||''} onChange={e=>{
                        const code = e.target.value
                        const sub = subCodesList.find(s=>s.code===code)
                        hLine(idx,'sub_code',code)
                        if (sub) hLine(idx,'cost_code_id',sub.costCodeId || '')
                      }}
                        className="w-36 border border-[#DDD9D0] rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#1A7A4A]">
                        <option value="">— Optional —</option>
                        {/* show current value as option even if subCodesList not yet loaded */}
                        {line.sub_code && !subCodesList.find(s=>s.code===line.sub_code) && (
                          <option value={line.sub_code}>{line.sub_code}</option>
                        )}
                        {subCodesList.map(s=><option key={s.id} value={s.code}>{s.code}</option>)}
                      </select>
                    </td>
                    <td className="px-2 py-1">
                      <span className="text-xs text-[#6B655C] px-1">
                        {subCodesList.find(s=>s.code===line.sub_code)?.description || (line.sub_code ? <span className="italic text-[#9A948C]">loading…</span> : '—')}
                      </span>
                    </td>
                    <td className="px-2 py-1">
                      <input value={line.description||''} onChange={e=>hLine(idx,'description',e.target.value)} placeholder="Item description" disabled={isCostControlPartial}
                        className="w-48 border border-[#DDD9D0] rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#1A7A4A] disabled:bg-[#F3F4F6] disabled:cursor-not-allowed"/>
                      {line.addendum_no && (
                        <span className="ml-1 px-1.5 py-0.5 text-[9px] font-bold uppercase rounded bg-orange-100 text-orange-700">Addendum #{line.addendum_no}</span>
                      )}
                    </td>
                    <td className="px-2 py-1">
                      {/* Cost Code is derived automatically from the selected Sub Code — no longer a manual dropdown */}
                      <span className="text-xs font-mono text-[#1A7A4A] font-semibold px-1">
                        {(() => {
                          const sub = subCodesList.find(s=>s.code===line.sub_code)
                          return sub?.costCode || costCodes.find(c=>c.id===line.cost_code_id)?.code || '—'
                        })()}
                      </span>
                      {(() => {
                        const sub = subCodesList.find(s=>s.code===line.sub_code)
                        return sub?.resourceCode ? (
                          <div className="text-[10px] text-purple-700 mt-0.5">RES: {sub.resourceCode}</div>
                        ) : null
                      })()}
                    </td>
                    <td className="px-2 py-1">
                      <input list="unit-options" value={line.unit||''} onChange={e=>hLine(idx,'unit',e.target.value)} placeholder="TON" disabled={isCostControlPartial}
                        className="w-16 border border-[#DDD9D0] rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#1A7A4A] disabled:bg-[#F3F4F6] disabled:cursor-not-allowed"/>
                    </td>
                    <td className="px-2 py-1">
                      <input type="number" value={line.qty||''} onChange={e=>hLine(idx,'qty',e.target.value)} disabled={isCostControlPartial}
                        className="w-20 border border-[#DDD9D0] rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#1A7A4A] disabled:bg-[#F3F4F6] disabled:cursor-not-allowed"/>
                    </td>
                    <td className="px-2 py-1">
                      <input type="number" value={line.unit_rate||''} onChange={e=>hLine(idx,'unit_rate',e.target.value)} disabled={isCostControlPartial}
                        className="w-24 border border-[#DDD9D0] rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#1A7A4A] disabled:bg-[#F3F4F6] disabled:cursor-not-allowed"/>
                    </td>
                    <td className="px-2 py-1 text-right font-medium text-[#1A7A4A]">
                      {fmt((parseFloat(line.qty)||0)*(parseFloat(line.unit_rate)||0))}
                    </td>
                    <td className="px-2 py-1 text-right text-[#6B655C]">
                      {fmt((parseFloat(line.qty)||0)*(parseFloat(line.unit_rate)||0)*(1+(parseFloat(form.vat_pct)||15)/100))}
                    </td>
                    <td className="px-2 py-1">
                      {form.lines.length > 1 && !isCostControlPartial && (
                        <button onClick={()=>removeLine(idx)} className="text-red-400 hover:text-red-600"><TrashIcon/></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-[#F5F1EA] border-t-2 border-[#DDD9D0]">
                <tr>
                  <td colSpan={9} className="px-2 py-2 text-xs font-bold text-[#6B655C] uppercase">Total (Original Currency)</td>
                  <td className="px-2 py-2 text-right text-sm font-bold text-[#1A7A4A]">{fmt(totalExcVat)}</td>
                  <td className="px-2 py-2 text-right text-sm font-bold text-[#6B655C]">{fmt(totalIncVat)}</td>
                  <td/>
                </tr>
                {multiplier !== 1 && (
                  <tr style={{ background:'#FFF3E0' }}>
                    <td colSpan={9} className="px-2 py-2 text-xs font-bold uppercase" style={{ color:'#B06D00' }}>
                      Total SAR (×{multiplier})
                    </td>
                    <td className="px-2 py-2 text-right text-sm font-bold" style={{ color:'#B06D00' }}>{fmt(totalExcVatSAR)}</td>
                    <td className="px-2 py-2 text-right text-sm font-bold" style={{ color:'#B06D00' }}>{fmt(totalIncVatSAR)}</td>
                    <td/>
                  </tr>
                )}
              </tfoot>
            </table>
          </div>
          </>
          )}
        </div>

        </div>

        </fieldset>

        {/* ── Section 3: Financial Summary ── */}
        <div style={{ background:'#0F1017', borderRadius:10, padding:'16px 20px', display:'flex', gap:24, alignItems:'center', flexWrap:'wrap' }}>
          {[
            { label:'Total Exc. VAT ('+(form.currency||'SAR')+')', value:(form.currency||'SAR')+' '+fmt(totalExcVat), color:'#fff' },
            { label:'VAT ('+( form.vat_pct||15)+'%)', value:(form.currency||'SAR')+' '+fmt(vatAmt), color:'#FBBF24' },
            { label:'Total Inc. VAT ('+(form.currency||'SAR')+')', value:(form.currency||'SAR')+' '+fmt(totalIncVat), color:'#4ADE80' },
            ...(form.currency && project?.currency && form.currency !== project.currency
              ? [{ label:`Project Amount (${project.currency})`, value: project.currency+' '+fmt(+(totalExcVat * (parseFloat(form.exchange_rate_applied)||1)).toFixed(2)), color:'#38BDF8' }]
              : []),
          ].reduce((acc,s,i)=>{
            if(i>0) acc.push(<div key={'d'+i} style={{ width:1, height:30, background:'rgba(255,255,255,.1)' }}/>)
            acc.push(<div key={s.label}><div style={{ fontSize:9, fontWeight:700, textTransform:'uppercase', letterSpacing:'.1em', color:'rgba(255,255,255,.38)', marginBottom:3 }}>{s.label}</div><div style={{ fontSize:18, fontWeight:800, color:s.color, fontVariantNumeric:'tabular-nums' }}>{s.value}</div></div>)
            return acc
          }, [])}
        </div>
      </div>
    </div>
  )
}

// ── PO Detail View ────────────────────────────────────────────────────────────
const PODetail = ({ po, onClose, onEdit, costCodes, subCodesList }) => {
  const [lines, setLines] = useState([])
  useEffect(() => {
    supabase.from('po_line_items').select('id,po_id,ref_code,sub_code,description,cost_code_id,unit,qty,unit_rate,amount_exc_vat,amount_inc_vat,addendum_no').eq('po_id',po.id).order('created_at').then(({data})=>setLines(data||[]))
  }, [po.id])
  const ccMap = {}
  costCodes.forEach(c => { ccMap[c.id] = { code: c.code, description: c.description } })

  const totalExcVat = lines.reduce((s,l)=>s+(parseFloat(l.amount_exc_vat)||0),0)
  const totalIncVat = lines.reduce((s,l)=>s+(parseFloat(l.amount_inc_vat)||0),0)
  const invoicedAmt = lines.reduce((s,l)=>s+(parseFloat(l.amount_exc_vat)||0),0) // issued qty * unit price

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}/>
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-lg font-semibold text-[#1C1A18]">{po.po_number}</h3>
            <p className="text-sm text-[#6B655C]">{po.vendor} · {po.issue_date}</p>
          </div>
          <div className="flex gap-2">
            <button onClick={onEdit} className="px-3 py-1.5 text-xs text-[#1A7A4A] border border-[#DDD9D0] rounded-lg hover:bg-[#EDE7DA]">Edit</button>
            <button onClick={onClose} className="text-[#9A948C] hover:text-[#6B655C]"><XIcon/></button>
          </div>
        </div>

        {/* PO Info */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5 p-4 bg-[#F5F1EA] rounded-xl text-xs">
          <div><span className="text-[#9A948C]">PO Type:</span> <span className={`ml-1 px-2 py-0.5 rounded-full border text-xs font-medium ${poTypeColors[po.po_type]||''}`}>{po.po_type}</span></div>
          <div><span className="text-[#9A948C]">Cost Type:</span> <span className="ml-1 font-medium text-[#2E2C29]">{po.cost_type}</span></div>
          <div><span className="text-[#9A948C]">Approval Stage:</span> <span className={`ml-1 px-2 py-0.5 rounded-full border text-xs font-medium ${stageColors[po.approval_stage||'draft']||''}`}>{po.approval_stage||'draft'}</span></div>
          <div><span className="text-[#9A948C]">Status:</span> <span className={`ml-1 px-2 py-0.5 rounded-full border text-xs font-medium ${statusColors[po.status]||''}`}>{po.status}</span></div>
          <div><span className="text-[#9A948C]">Sub Code:</span> <span className="ml-1 font-medium text-[#2E2C29]">{po.sub_code||'—'}</span></div>
          {(() => { const sub = (subCodesList||[]).find(s=>s.code===po.sub_code); return sub ? (
            <>
              <div><span className="text-[#9A948C]">Cost Code:</span> <span className="ml-1 font-medium text-[#1A7A4A] font-mono">{sub.costCode}</span></div>
              {sub.resourceCode && <div><span className="text-[#9A948C]">Resource Code:</span> <span className="ml-1 font-medium text-purple-700 font-mono">{sub.resourceCode}</span></div>}
            </>
          ) : null })()}
          <div><span className="text-[#9A948C]">Contract Ref:</span> <span className="ml-1 font-medium text-[#2E2C29]">{po.contract_ref||'—'}</span></div>
          <div><span className="text-[#9A948C]">VAT %:</span> <span className="ml-1 font-medium text-[#2E2C29]">{po.vat_pct||15}%</span></div>
          <div><span className="text-[#9A948C]">Total Exc.VAT:</span> <span className="ml-1 font-semibold text-[#1A7A4A]">{fmt(po.total_exc_vat)}</span></div>
          <div><span className="text-[#9A948C]">Total Inc.VAT:</span> <span className="ml-1 font-semibold text-[#2E2C29]">{fmt(po.total_inc_vat)}</span></div>
        </div>

        {/* Progress */}
        <div className="grid grid-cols-3 gap-3 mb-5">
          <div className="bg-white border border-[#DDD9D0] rounded-xl p-3">
            <div className="text-xs text-[#6B655C] mb-1">Delivered Value</div>
            <div className="text-lg font-bold text-[#1A7A4A]">{fmtM(po.delivered_value||0)}</div>
            <div className="text-xs text-[#9A948C]">{po.total_exc_vat>0?((po.delivered_value||0)/po.total_exc_vat*100).toFixed(1):0}% of PO</div>
          </div>
          <div className="bg-white border border-[#DDD9D0] rounded-xl p-3">
            <div className="text-xs text-[#6B655C] mb-1">Issued Value (Actual Cost)</div>
            <div className="text-lg font-bold text-[#1A7A4A]">{fmtM(po.issued_value||0)}</div>
            <div className="text-xs text-[#9A948C]">{po.total_exc_vat>0?((po.issued_value||0)/po.total_exc_vat*100).toFixed(1):0}% of PO</div>
          </div>
          <div className="bg-white border border-[#DDD9D0] rounded-xl p-3">
            <div className="text-xs text-[#6B655C] mb-1">Paid Value (Cash Out)</div>
            <div className="text-lg font-bold text-amber-600">{fmtM(po.paid_value||0)}</div>
            <div className="text-xs text-[#9A948C]">{po.total_inc_vat>0?((po.paid_value||0)/po.total_inc_vat*100).toFixed(1):0}% of PO Inc.VAT</div>
          </div>
        </div>

        {/* Line Items */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#EDE7DA] border-b border-[#DDD9D0]">
              <tr>{['Ref Code','Sub Code','Description','Cost Code','Resource Code','Resource Description','Unit','Qty','Unit Rate','Exc.VAT','Inc.VAT'].map(h=>(
                <th key={h} className="text-left px-3 py-2 text-xs font-semibold text-[#6B655C] uppercase whitespace-nowrap">{h}</th>
              ))}</tr>
            </thead>
            <tbody className="divide-y divide-[#E8E4DC]">
              {lines.map(l=>{
                const sub = (subCodesList||[]).find(s=>s.code===l.sub_code)
                return (
                <tr key={l.id} className="hover:bg-[#F5F1EA]">
                  <td className="px-3 py-2 font-mono text-xs text-[#1A7A4A]">{l.ref_code||'—'}</td>
                  <td className="px-3 py-2 font-mono text-xs text-[#1A7A4A] font-semibold">{l.sub_code||'—'}</td>
                  <td className="px-3 py-2 text-[#2E2C29]">
                    {l.description}
                    {l.addendum_no && (
                      <span className="ml-2 px-1.5 py-0.5 text-[9px] font-bold uppercase rounded bg-orange-100 text-orange-700 align-middle">Addendum #{l.addendum_no}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-[#6B655C]">{sub?.costCode ? `${sub.costCode} — ${sub.costCodeDescription}` : (ccMap[l.cost_code_id] ? `${ccMap[l.cost_code_id].code} — ${ccMap[l.cost_code_id].description}` : (l.cost_code_id ? <span className="text-amber-600 font-mono">{l.cost_code_id.slice(0,8)}…</span> : '—'))}</td>
                  <td className="px-3 py-2 text-xs font-mono text-purple-700">{sub?.resourceCode || '—'}</td>
                  <td className="px-3 py-2 text-xs text-purple-700">{sub?.resourceCodeDescription || '—'}</td>
                  <td className="px-3 py-2 text-xs text-[#6B655C]">{l.unit||'—'}</td>
                  <td className="px-3 py-2 text-right text-xs">{fmt(l.qty)}</td>
                  <td className="px-3 py-2 text-right text-xs">{fmt(l.unit_rate)}</td>
                  <td className="px-3 py-2 text-right font-semibold text-[#1A7A4A]">{fmt(l.amount_exc_vat)}</td>
                  <td className="px-3 py-2 text-right text-[#6B655C]">{fmt(l.amount_inc_vat)}</td>
                </tr>
                )
              })}
            </tbody>
            <tfoot className="bg-[#F5F1EA] border-t-2 border-[#DDD9D0]">
              <tr>
                <td colSpan={7} className="px-3 py-2 text-xs font-bold text-[#6B655C] uppercase">Total</td>
                <td className="px-3 py-2 text-right text-sm font-bold text-[#1A7A4A]">{fmt(totalExcVat)}</td>
                <td className="px-3 py-2 text-right text-sm font-bold text-[#6B655C]">{fmt(totalIncVat)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {po.notes && <div className="mt-4 p-3 bg-amber-50 rounded-xl text-xs text-amber-700"><span className="font-semibold">Notes: </span>{po.notes}</div>}
      </div>
    </div>
  )
}

// ── Convert to Subcontract Modal ──────────────────────────────────────────────
const ConvertModal = ({ po, form, setForm, costCodes, onConfirm, onClose, converting }) => {
  const hf = (k, v) => setForm(f => ({ ...f, [k]: v }))
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}/>
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-base font-semibold text-[#1C1A18]">Convert to Subcontract</h3>
            <p className="text-xs text-[#9A948C] mt-0.5">{po.po_number} · {po.vendor}</p>
          </div>
          <button onClick={onClose} className="text-[#9A948C] hover:text-[#6B655C]"><XIcon/></button>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-[#6B655C] mb-1">Contract No *</label>
            <input value={form.contract_no||''} onChange={e=>hf('contract_no',e.target.value)} placeholder="SC-0001"
              className="w-full border border-[#DDD9D0] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]"/>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6B655C] mb-1">Contractor Name *</label>
            <input value={form.contractor_name||''} onChange={e=>hf('contractor_name',e.target.value)}
              className="w-full border border-[#DDD9D0] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]"/>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6B655C] mb-1">Contract Value</label>
            <input type="number" value={form.contract_value||''} onChange={e=>hf('contract_value',e.target.value)}
              className="w-full border border-[#DDD9D0] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]"/>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6B655C] mb-1">VAT %</label>
            <input type="number" value={form.vat_pct||0} onChange={e=>hf('vat_pct',e.target.value)}
              className="w-full border border-[#DDD9D0] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]"/>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6B655C] mb-1">Contract Date</label>
            <input type="date" value={form.contract_date||''} onChange={e=>hf('contract_date',e.target.value)}
              className="w-full border border-[#DDD9D0] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]"/>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6B655C] mb-1">Retention %</label>
            <input type="number" value={form.retention_pct||0} onChange={e=>hf('retention_pct',e.target.value)}
              className="w-full border border-[#DDD9D0] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]"/>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6B655C] mb-1">Advance %</label>
            <input type="number" value={form.advance_pct||0} onChange={e=>hf('advance_pct',e.target.value)}
              className="w-full border border-[#DDD9D0] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]"/>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6B655C] mb-1">Payment Terms (days)</label>
            <input type="number" value={form.payment_terms||30} onChange={e=>hf('payment_terms',e.target.value)}
              className="w-full border border-[#DDD9D0] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]"/>
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-[#6B655C] mb-1">Cost Code</label>
            <select value={form.cost_code_id||''} onChange={e=>hf('cost_code_id',e.target.value)}
              className="w-full border border-[#DDD9D0] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]">
              <option value="">— Select —</option>
              {costCodes.map(c=><option key={c.id} value={c.id}>{c.code} — {c.description}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-[#6B655C] mb-1">Scope</label>
            <input value={form.scope||''} onChange={e=>hf('scope',e.target.value)}
              className="w-full border border-[#DDD9D0] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]"/>
          </div>
        </div>
        <div className="flex gap-3 mt-5 pt-4 border-t border-[#E8E4DC]">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-[#DDD9D0] rounded-xl text-sm font-medium text-[#6B655C] hover:bg-[#F5F1EA]">Cancel</button>
          <button onClick={onConfirm} disabled={converting}
            className="flex-1 px-4 py-2 bg-[#1C1A18] rounded-xl text-sm font-medium text-white hover:bg-[#2E2C29] disabled:opacity-50">
            {converting ? 'Converting...' : '→ Convert to Subcontract'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function PurchaseOrders({ initProject }) {
  // PO table horizontal-scroll sync: ref is the single source of truth
  // for scroll position -- the floating nav control never keeps its
  // own independent scroll state, it only reads/writes
  // poTableScrollRef.current.scrollLeft, exactly mirroring the real
  // table's native horizontal scrollbar. hasOverflow/scrollRatio only
  // drive the floating control's visibility/thumb position, they never
  // gate the table's own overflow-x-auto scrolling.
  const poTableScrollRef = useRef(null)
  // The floating nav is position:fixed (per the single "always visible
  // near the table, anchored to the viewport" choice) so it must be
  // fixed relative to the true viewport, not the app's collapsible
  // sidebar (width varies -- see App.jsx). poTableCardRef/poNavLeftRight
  // track the table card's actual left/right screen position so the
  // fixed bar lines up with the table itself rather than spanning the
  // full window width including the sidebar.
  const poTableCardRef = useRef(null)
  const [poNavLeftRight, setPoNavLeftRight] = useState({ left: 0, right: 0 })
  const [poScrollState, setPoScrollState] = useState({ hasOverflow: false, atStart: true, atEnd: true, ratio: 0 })
  const updatePoScrollState = useCallback(() => {
    const el = poTableScrollRef.current
    if (!el) return
    const hasOverflow = el.scrollWidth > el.clientWidth + 1
    const maxScroll = el.scrollWidth - el.clientWidth
    const ratio = maxScroll > 0 ? el.scrollLeft / maxScroll : 0
    setPoScrollState({
      hasOverflow,
      atStart: el.scrollLeft <= 1,
      atEnd: maxScroll <= 1 || el.scrollLeft >= maxScroll - 1,
      ratio,
    })
    // Recompute the fixed nav bar's left/right screen offsets from the
    // table card's actual bounding box, so it lines up with the table
    // (not the full window) regardless of the app sidebar's current
    // collapsed/expanded width.
    if (poTableCardRef.current) {
      const rect = poTableCardRef.current.getBoundingClientRect()
      setPoNavLeftRight({ left: rect.left, right: window.innerWidth - rect.right })
    }
  }, [])
  const poScrollBy = (dir) => {
    const el = poTableScrollRef.current
    if (!el) return
    el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.75), behavior: 'smooth' })
  }
  const poScrollToRatio = (ratio) => {
    const el = poTableScrollRef.current
    if (!el) return
    const maxScroll = el.scrollWidth - el.clientWidth
    el.scrollLeft = ratio * maxScroll
  }

  // Rental Equipment PO table scroll/nav state -- completely separate
  // refs/state/handlers from the Material PO ones above (repTableScrollRef
  // vs poTableScrollRef etc.), same proven pattern, own table/tab.
  const repTableScrollRef = useRef(null)
  const repTableCardRef = useRef(null)
  const [repNavLeftRight, setRepNavLeftRight] = useState({ left: 0, right: 0 })
  const [repScrollState, setRepScrollState] = useState({ hasOverflow: false, atStart: true, atEnd: true, ratio: 0 })
  const updateRepScrollState = useCallback(() => {
    const el = repTableScrollRef.current
    if (!el) return
    const hasOverflow = el.scrollWidth > el.clientWidth + 1
    const maxScroll = el.scrollWidth - el.clientWidth
    const ratio = maxScroll > 0 ? el.scrollLeft / maxScroll : 0
    setRepScrollState({
      hasOverflow,
      atStart: el.scrollLeft <= 1,
      atEnd: maxScroll <= 1 || el.scrollLeft >= maxScroll - 1,
      ratio,
    })
    if (repTableCardRef.current) {
      const rect = repTableCardRef.current.getBoundingClientRect()
      setRepNavLeftRight({ left: rect.left, right: window.innerWidth - rect.right })
    }
  }, [])
  const repScrollBy = (dir) => {
    const el = repTableScrollRef.current
    if (!el) return
    el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.75), behavior: 'smooth' })
  }
  const repScrollToRatio = (ratio) => {
    const el = repTableScrollRef.current
    if (!el) return
    const maxScroll = el.scrollWidth - el.clientWidth
    el.scrollLeft = ratio * maxScroll
  }
  const [projects, setProjects] = useState([])
  const [selectedProject, setSelectedProject] = useState(null)
  useEffect(() => { if (initProject) setSelectedProject(initProject) }, [initProject?.id])
  const [pos, setPOs] = useState([])
  const [contractPoIds, setContractPoIds] = useState(new Set())
  const docCounts = useDocCounts(pos.map(p => p.id))
  const [costCodes, setCostCodes] = useState([])
  const [analysisCodes, setAnalysisCodes] = useState([])
  const [subCodesList, setSubCodesList] = useState([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [analysisFilter, setAnalysisFilter] = useState('all') // 'all' | analysis code string
  const [modal, setModal] = useState(null) // null | 'new' | po object

  /**
   * Opens the SAME POModal editor used for editing an existing PO — no
   * separate simplified Addendum form, per the approved plan. The
   * modal object passed here has NO id (so handleSave's `if(form.id)`
   * branch takes the insert path, not update), original_po_id
   * pre-filled from the contract's currently-approved PO, and a
   * handful of commercial fields carried over as a starting point —
   * the user still has full editing capability (line items, cost
   * codes, quantities, rates) exactly as they would for any other PO.
   * __isAddendumDraft distinguishes this from a real saved PO object
   * being edited (which always has an id) purely for routing which
   * POModal instance renders — it is never sent to the database.
   */
  /**
   * Opens the SAME POModal editor used for editing an existing PO — no
   * separate simplified Addendum form, per the approved plan. FIXED
   * (this revision): the original implementation only carried over a
   * handful of top-level fields, leaving the user to re-enter every
   * line item from scratch. Now mirrors the pattern used for editing an
   * existing PO (`setModal(po)` + `cachedLines={poLines[modal.id]}`):
   * the entire original PO object is spread in, and its line items are
   * pre-loaded into poLines under this DRAFT's own key so POModal's
   * cachedLines prop (keyed by modal.id — which is undefined here,
   * intentionally, see below) picks them up the same way it would for
   * a real saved PO.
   *
   * id and po_number are explicitly cleared (not spread from
   * originalPo) so handleSave's insert path is taken (a new PO), not
   * an update to the original PO.
   */
  function openAddendumPO(originalPo) {
    const inheritedLines = (poLines[originalPo.id] || []).map(l => {
      const { id, ...lineWithoutId } = l
      return lineWithoutId
    })
    const suggestedNumber = originalPo.po_number ? `${originalPo.po_number}-ADD` : ''
    setModal({
      ...originalPo,
      __isAddendumDraft: true,
      id: undefined,
      status: 'draft', // #1: an Addendum is a NEW PO — it starts as draft and never inherits the original's 'approved'
      po_number: suggestedNumber,
      original_po_id: originalPo.id,
      contract_ref: originalPo.contract_ref || originalPo.po_number || '',
      notes: `Addendum to ${originalPo.po_number}${originalPo.notes ? ' — ' + originalPo.notes : ''}`,
      __addendumLines: inheritedLines, // consumed by POModal's init below
    })
  }
  const [detailPO, setDetailPO] = useState(null)
  const [toast, setToast] = useState(null)
  const [expandedRows, setExpandedRows] = useState({})
  const [poSelected, setPoSelected] = useState([])
  const [poBulkBusy, setPoBulkBusy] = useState(false)
  const [poBulkActionType, setPoBulkActionType] = useState('approve')
  const [poLines, setPoLines] = useState({}) // cache: po.id -> lines array
  const [convertPO, setConvertPO] = useState(null) // po to convert
  const [convertForm, setConvertForm] = useState({})
  const [converting, setConverting] = useState(false)
  const [convertedIds, setConvertedIds] = useState({}) // po.id -> subcontract_no
  const [pipelineStage, setPipelineStage] = useState('all')
  const [detailPanel, setDetailPanel] = useState(null) // po for slide-in panel
  const [printPO, setPrintPO] = useState(null) // po id to print
  const [activeTab, setActiveTab] = useState('purchase_orders')

  // ── Equipment Costs state ──
  const [budgetRates, setBudgetRates] = useState({})
  const [budgetQtys, setBudgetQtys] = useState({})
  const [committedQtys, setCommittedQtys] = useState({})
  // Phase PO-3: line-level key resolution (Resource Code / Legacy / Unknown
  // for Direct, Cost Code for Indirect) — keyed by po_line_items.id, used
  // by the Budget vs Commitment display to group/label correctly.
  const [lineKeyResolution, setLineKeyResolution] = useState({})
  // Cache invalidation signal -- bumped by loadPOs() after every write
  // (save/approve/reject/delete), so any code that already calls loadPOs()
  // automatically triggers a Budget vs Commitment recompute with zero
  // additional wiring per call site.
  const [budgetRefreshTick, setBudgetRefreshTick] = useState(0)
  const invalidateBudgetCache = useCallback(() => setBudgetRefreshTick(t => t + 1), [])
  // Forecast Value card (SAR) — Live Forecast via resolveCurrentForecastState()
  // (official Source of Truth, see import comment above). The Snapshot
  // selector approach previously here was architecturally wrong: it
  // compared a frozen historical Snapshot EAC against Live Commitment,
  // mixing two different points in time. Historical Release-mode
  // comparison (Snapshot EAC vs Snapshot Commitment) is a separate,
  // not-yet-implemented concern -- this is Live-only.
  const [forecastByResource, setForecastByResource] = useState({}) // { normSC(resource_code): { resolvedEac, isOwner, hasEffectiveForecast } }
  // Committed Value (exc VAT) — separate from committedQtys (qty basis,
  // Card 1). Same Resource Code aggregation, same status filter, but
  // amount_exc_vat instead of qty.
  const [committedValueByResource, setCommittedValueByResource] = useState({})
  const [budgetValueByResource, setBudgetValueByResource] = useState({}) // Card 3: Budget Value (SAR), Direct only
  const [repPOs,          setRepPOs]         = useState([])
  const [repContractPoIds, setRepContractPoIds] = useState(new Set())
  const [repLines,        setRepLines]       = useState({})   // { po_id: [line, ...] }
  const [repCostCodes,    setRepCostCodes]   = useState([])
  const [repSubCodes,     setRepSubCodes]    = useState([])
  // Rental Equipment PO comparison cards -- computed once per project,
  // same pattern as budgetQtys/committedQtys/forecastByResource for the
  // regular PO tab, kept fully separate (rep-prefixed) so the original
  // Purchase Orders tab logic is never touched.
  const [repBudgetQtyByResource, setRepBudgetQtyByResource] = useState({})
  const [repBudgetValueByResource, setRepBudgetValueByResource] = useState({})
  const [repCommittedQtyByResource, setRepCommittedQtyByResource] = useState({})
  const [repCommittedValueByResource, setRepCommittedValueByResource] = useState({})
  const [repForecastByResource, setRepForecastByResource] = useState({})
  // Indirect fallback (no resourceCode) -- same source/key as the
  // original PO tab's indirect maps (tender_indirect_items by cost_code),
  // computed once here for Rental Equipment lines that have no Resource
  // Code and must compare against their Cost Code instead.
  const [repIndirectBudgetByCostCode, setRepIndirectBudgetByCostCode] = useState({})
  const [repIndirectCommittedByCostCode, setRepIndirectCommittedByCostCode] = useState({})
  // Single-item selector for the comparison cards (replaces the crowded
  // "show all cards" approach) -- one selector per modal, independent
  // state so neither tab affects the other.
  const [repSelectedCardKey, setRepSelectedCardKey] = useState(null)
  const [selectedCardKey, setSelectedCardKey] = useState(null)
  const [repLoading,      setRepLoading]     = useState(false)
  const [repModal,        setRepModal]       = useState(null) // null | 'new' | po object
  // Currency options for the Rental PO modal's currency <select>. `currencies` is declared
  // only inside POModal, so this (main) component needs its own copy of the same lookup.
  const [currencies, setCurrencies] = useState([])
  useEffect(() => {
    supabase.from('currencies').select('code,name,symbol').eq('is_active', true).order('code')
      .then(({ data }) => setCurrencies(data || []))
  }, [])
  const [repShowUpload,   setRepShowUpload]  = useState(false)
  const [repUploadRows,   setRepUploadRows]  = useState([])
  const [repUnmatched,    setRepUnmatched]   = useState([]) // rows with no cost_code match, need manual pick
  const [repHadUnmatched, setRepHadUnmatched] = useState(false)
  const [repPendingGroups, setRepPendingGroups] = useState(null) // groups ready to insert once unmatched resolved
  const [repUploading,    setRepUploading]   = useState(false)
  const [repUploadProgress, setRepUploadProgress] = useState({ done: 0, total: 0 })
  const [repPipelineStage, setRepPipelineStage] = useState('all')
  const [repSearch,       setRepSearch]      = useState('')
  const [repSelected,     setRepSelected]    = useState([])
  const [repBulkBusy,     setRepBulkBusy]    = useState(false)
  const [repBulkActionType, setRepBulkActionType] = useState('approve')
  const [repRejectPO,     setRepRejectPO]    = useState(null)
  const [repRejectReason, setRepRejectReason] = useState('')
  const [repViewPO,       setRepViewPO]      = useState(null)
  const [repPrintPO,      setRepPrintPO]     = useState(null) // po id string
  const [repLinesModal,   setRepLinesModal]  = useState(null)
  const repFileRef = React.useRef()
  // REP_APPROVAL_LIMITS removed — now imported from lib/approvalLimits.js
  // (single shared source, per the approved decision; this was
  // previously a separate, identically-valued duplicate of the
  // Material path's APPROVAL_LIMITS below).
  const repEmptyLine = () => ({ _id: Math.random(), equipment_type:'', description:'', cost_code_id:'', quantity:'', unit:'Hours', rate:'', total_cost:'', total_inc_vat:'', addendum_no: repModal?.pending_addendum_no || null })
  // vat_pct here is only a transient placeholder shown for the instant
  // before repOpenNew()'s get_project_vat_config() call resolves and
  // overwrites it -- it is never the value actually saved unless the
  // project genuinely has no VAT configuration row.
  const repEmptyForm = () => ({ po_number:'', vendor:'', cost_code_id:'', issue_date: new Date().toISOString().split('T')[0], notes:'', vat_pct:15, lines:[repEmptyLine()] })
  const fmtRep = (n) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n || 0)

  function parsePeriodMonth(raw) {
    if (!raw) return null
    if (raw instanceof Date) return raw.toISOString().slice(0, 10)
    const s = String(raw).trim()
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10)
    const monthNames = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec']
    const m = s.match(/([a-zA-Z]+)[\s\-\/]+(\d{4})/)
    if (m) { const idx = monthNames.indexOf(m[1].toLowerCase().slice(0,3)); if (idx >= 0) return `${m[2]}-${String(idx+1).padStart(2,'0')}-01` }
    return null
  }

  useEffect(() => {
    // load already-converted complex POs
    if (!selectedProject) return
    supabase.from('subcontracts').select('po_id, contract_no').eq('project_id', selectedProject.id).then(({data}) => {
      const map = {}
      ;(data||[]).forEach(s => { if(s.po_id) map[s.po_id] = s.contract_no })
      setConvertedIds(map)
    })
  }, [selectedProject])

  // ── Equipment Costs functions ──
  // Line Items Modal state
  const [linesModal, setLinesModal] = useState(null) // po object

  // Cost Control Audit tab state (formerly "Sub Code Search") — reconciliation
  // layer over the Budget Comparison cards' own resolved data, per approved
  // design. No calculation logic lives here; every number comes from the
  // same resolveDirectKey/resolveIndirectKey/isDirectPO/resolveCurrentForecastState
  // calls the cards themselves use.
  const [auditMode, setAuditMode] = useState('sub_code') // 'sub_code' | 'resource_code' | 'cost_code' | 'po_number'
  const [auditValue, setAuditValue] = useState('')
  const [auditResult, setAuditResult] = useState(null) // { mode, po?, cards: [...] } | { notFound: true }
  const [auditLoading, setAuditLoading] = useState(false)

  // ── buildAuditCard() — pure reconciliation, zero new calculation logic.
  // Every value here is produced by re-calling the SAME resolution
  // functions and querying the SAME source tables the Budget Comparison
  // cards' own useEffect uses, so results are guaranteed to reconcile
  // exactly for the same Resource Code / Cost Code -- this is a read/view
  // layer over that logic, not a parallel implementation of it.
  async function buildAuditCard(key, kind, label, isIndirect, maps) {
    if (kind === 'unknown') return { key, kind, label, isIndirect, unmapped: true }

    // ── Budget Evidence ──
    let budgetRows = [], budgetTotal = 0
    if (kind !== 'legacy') {
      if (!isIndirect) {
        const { data } = await supabase.from('tender_direct_items')
          .select('id, row_type, description, resource_code, qty, rate')
          .eq('project_id', selectedProject.id)
          .in('row_type', ['material','labour','subcontract','equipment'])
        budgetRows = (data||[]).filter(r => normSC(r.resource_code) === key)
        budgetTotal = budgetRows.reduce((s,r)=>s+(parseFloat(r.qty)||0),0)
      } else {
        const { data } = await supabase.from('tender_indirect_items')
          .select('id, description, cost_code, tender_budget')
          .eq('project_id', selectedProject.id)
        budgetRows = (data||[]).filter(r => normSC(r.cost_code) === key)
        budgetTotal = budgetRows.reduce((s,r)=>s+(parseFloat(r.tender_budget)||0),0)
      }
    }

    // ── Commitment Evidence — same status filter ('approved','closed') and
    // same resolveDirectKey/resolveIndirectKey the cards use.
    const { data: allLines } = await supabase.from('po_line_items')
      .select('id, po_id, sub_code, cost_code_id, qty, amount_exc_vat, purchase_orders!inner(project_id, po_number, vendor, status, cost_type)')
      .eq('purchase_orders.project_id', selectedProject.id)
      .in('purchase_orders.status', ['approved','closed'])
    const commLines = (allLines||[]).filter(l => {
      const lineIsIndirect = !isDirectPO(l.purchase_orders)
      if (lineIsIndirect !== isIndirect) return false
      const r = isIndirect ? resolveIndirectKey(l.cost_code_id, l.sub_code, maps) : resolveDirectKey(l.sub_code, maps)
      return r.key === key
    })
    const commQty = isIndirect ? null : commLines.reduce((s,l)=>s+(parseFloat(l.qty)||0),0)
    const commValue = commLines.reduce((s,l)=>s+(parseFloat(l.amount_exc_vat)||0),0)

    // ── Forecast Evidence — resolveCurrentForecastState() is source-aware
    // (direct/indirect); calling it with 'indirect' is the same official
    // Live Forecast SSOT, not a new capability. buildForecastNodeIndex()
    // flattens every tree depth into one flat Map uniformly, so the same
    // normSC(code) lookup works for both sources without new traversal logic.
    let forecastNode = null
    if (kind !== 'legacy') {
      const tree = await resolveCurrentForecastState(selectedProject.id, isIndirect ? 'indirect' : 'direct')
      const index = buildForecastNodeIndex(tree)
      for (const [code, node] of index.entries()) {
        if (normSC(code) === key) { forecastNode = node; break }
      }
    }

    const budgetRemaining = kind === 'legacy' ? null : budgetTotal - (isIndirect ? commValue : commQty)
    const forecastRemaining = forecastNode ? forecastNode.resolvedEac - commValue : null
    const overBudget = budgetRemaining != null && (isIndirect ? commValue : commQty) > budgetTotal
    const overForecast = forecastRemaining != null && commValue > forecastNode.resolvedEac

    return { key, kind, label, isIndirect, budgetRows, budgetTotal, commLines, commQty, commValue,
      forecastNode, budgetRemaining, forecastRemaining, overBudget, overForecast }
  }

  const runAudit = async () => {
    if (!auditValue.trim() || !selectedProject?.id) return
    setAuditLoading(true)
    setAuditResult(null)
    const maps = await getCachedCostCodeMaps(selectedProject.id)

    if (auditMode === 'po_number') {
      const { data: po } = await supabase.from('purchase_orders')
        .select('id, po_number, vendor, status, cost_type')
        .eq('project_id', selectedProject.id).eq('po_number', auditValue.trim()).maybeSingle()
      if (!po) { setAuditResult({ notFound: true }); setAuditLoading(false); return }
      const { data: lines } = await supabase.from('po_line_items').select('*').eq('po_id', po.id)
      const isIndirect = !isDirectPO(po)
      const resolvedLines = (lines||[]).map(l => ({
        line: l,
        r: isIndirect ? resolveIndirectKey(l.cost_code_id, l.sub_code, maps) : resolveDirectKey(l.sub_code, maps),
      }))
      const uniqueKeys = [...new Set(resolvedLines.map(x => x.r.key || `__unknown__${x.r.label}`))]
      const cards = []
      for (const uk of uniqueKeys) {
        const sample = resolvedLines.find(x => (x.r.key || `__unknown__${x.r.label}`) === uk)
        cards.push(await buildAuditCard(sample.r.key, sample.r.kind, sample.r.label, isIndirect, maps))
      }
      setAuditResult({ mode: 'po_number', po, cards })
    } else {
      let key, kind, label
      const isIndirectMode = auditMode === 'cost_code'
      if (auditMode === 'sub_code') {
        const r = resolveDirectKey(auditValue, maps)
        key = r.key; kind = r.kind; label = r.label
      } else if (auditMode === 'resource_code') {
        key = normSC(auditValue); kind = 'resource'; label = auditValue
      } else {
        key = normSC(auditValue); kind = 'costcode'; label = auditValue
      }
      const card = await buildAuditCard(key, kind, label, isIndirectMode, maps)
      setAuditResult({ mode: auditMode, cards: [card] })
    }
    setAuditLoading(false)
  }


  const [repLoadedForProject, setRepLoadedForProject] = useState(null)
  useEffect(() => {
    if (selectedProject?.id && activeTab === 'rental_equipment' && repLoadedForProject !== selectedProject.id) {
      loadRepPOs(selectedProject.id)
      setRepLoadedForProject(selectedProject.id)
    }
  }, [selectedProject?.id, activeTab])

  // Rental Equipment PO comparison cards -- Budget/Commitment/Forecast,
  // recomputed alongside repLoadedForProject (same trigger, independent
  // state). Reuses repSubCodes.resourceCode/costCode directly (no
  // resolveDirectKey() re-call, per approved design) and the same
  // tender_direct_items / resolveCurrentForecastState() sources the
  // regular PO cards use -- entirely separate state, zero impact on the
  // original Purchase Orders tab.
  useEffect(() => {
    if (!selectedProject?.id || activeTab !== 'rental_equipment' || !repSubCodes.length) return
    let cancelled = false
    ;(async () => {
      const scByCode = {}
      repSubCodes.forEach(s => { scByCode[normSC(s.code)] = s })

      // ── Budget (Direct, same source as the regular PO cards) ──────
      const { data: tRows } = await supabase.from('tender_direct_items')
        .select('resource_code, row_type, qty, unit_rate')
        .eq('project_id', selectedProject.id)
        .in('row_type', ['material', 'labour', 'subcontract', 'equipment'])
      const budgetQtyMap = {}, budgetValueMap = {}
      ;(tRows || []).forEach(r => {
        const k = normSC(r.resource_code)
        if (!k) return
        budgetQtyMap[k] = (budgetQtyMap[k] || 0) + (parseFloat(r.qty) || 0)
        budgetValueMap[k] = (budgetValueMap[k] || 0) + ((parseFloat(r.qty) || 0) * (parseFloat(r.unit_rate) || 0))
      })

      // ── Commitment (from rental_equipment_po_line_items itself) ──
      // Rental Equipment approval status has no 'closed' value (confirmed
      // by inspection) -- 'approved' only, unlike the regular PO tab's
      // ('approved','closed').
      const { data: repCommLines } = await supabase.from('rental_equipment_po_line_items')
        .select('equipment_type, quantity, total_cost, rental_equipment_pos!inner(project_id, approval_stage)')
        .eq('rental_equipment_pos.project_id', selectedProject.id)
        .eq('rental_equipment_pos.approval_stage', 'approved')
      const commQtyMap = {}, commValueMap = {}
      ;(repCommLines || []).forEach(l => {
        const sc = scByCode[normSC(l.equipment_type)]
        const k = sc?.resourceCode ? normSC(sc.resourceCode) : null
        if (!k) return
        commQtyMap[k] = (commQtyMap[k] || 0) + (parseFloat(l.quantity) || 0)
        commValueMap[k] = (commValueMap[k] || 0) + (parseFloat(l.total_cost) || 0)
      })

      // ── Forecast (Live, same official SSOT as the regular PO cards) ──
      const tree = await resolveCurrentForecastState(selectedProject.id, 'direct')
      const index = buildForecastNodeIndex(tree)
      const forecastMap = {}
      for (const [code, node] of index.entries()) {
        const k = normSC(code)
        if (!k) continue
        forecastMap[k] = { resolvedEac: node.resolvedEac, hasEffectiveForecast: node.hasEffectiveForecast }
      }

      // ── Indirect fallback (no resourceCode -> compare by costCode) ──
      // Same source/key as the original PO tab's indirect maps:
      // tender_indirect_items.tender_budget by cost_code (Budget), and
      // this PO's own committed amount aggregated by costCode (Commitment
      // -- value only, no qty concept for Indirect, matching the existing
      // system-wide convention).
      const { data: iRows } = await supabase.from('tender_indirect_items')
        .select('cost_code, tender_budget')
        .eq('project_id', selectedProject.id)
      const indirectBudgetMap = {}
      ;(iRows || []).forEach(r => {
        const k = normSC(r.cost_code)
        if (!k) return
        indirectBudgetMap[k] = (indirectBudgetMap[k] || 0) + (parseFloat(r.tender_budget) || 0)
      })
      const indirectCommMap = {}
      ;(repCommLines || []).forEach(l => {
        const sc = scByCode[normSC(l.equipment_type)]
        if (sc?.resourceCode) return // has a real Resource Code -- already counted in commValueMap above
        const k = sc?.costCode ? normSC(sc.costCode) : null
        if (!k) return
        indirectCommMap[k] = (indirectCommMap[k] || 0) + (parseFloat(l.total_cost) || 0)
      })

      if (cancelled) return
      setRepBudgetQtyByResource(budgetQtyMap)
      setRepBudgetValueByResource(budgetValueMap)
      setRepCommittedQtyByResource(commQtyMap)
      setRepCommittedValueByResource(commValueMap)
      setRepForecastByResource(forecastMap)
      setRepIndirectBudgetByCostCode(indirectBudgetMap)
      setRepIndirectCommittedByCostCode(indirectCommMap)
    })()
    return () => { cancelled = true }
  }, [selectedProject?.id, activeTab, repSubCodes])

  const loadRepPOs = useCallback(async (pid) => {
    setRepLoading(true)
    const [{ data: posData, error: posErr }, { data: linesData }, { data: ccData }, { data: eqContractsData }] = await Promise.all([
      supabase.from('rental_equipment_pos').select('*,cost_codes(code,description)').eq('project_id', pid).order('created_at', { ascending: false }),
      supabase.from('rental_equipment_po_line_items').select('id,po_id,equipment_type,description,cost_code_id,quantity,unit,rate,total_cost,total_inc_vat,addendum_no,rental_equipment_pos!inner(project_id)').eq('rental_equipment_pos.project_id', pid).order('created_at'),
      supabase.from('cost_codes').select('id,code,description,level,parent_id').eq('project_id', pid).order('code'),
      // Loaded so the UI can distinguish "this draft PO is an Original PO
      // that already has a real Contract" (Create Addendum should show)
      // from "this is just a brand-new, never-approved PO" (it should
      // not) — approval_stage/status alone can't make this distinction,
      // since Revoke intentionally clears them back to draft/null.
      supabase.from('equipment_contracts').select('po_id').eq('project_id', pid),
    ])
    if (posErr) { showToast('Failed to load: ' + posErr.message, 'error'); setRepLoading(false); return }
    setRepPOs(posData || [])
    setRepContractPoIds(new Set((eqContractsData || []).map(c => c.po_id).filter(Boolean)))
    const allCC = ccData || []
    const byId = {}
    allCC.forEach(c => { byId[c.id] = c })
    setRepCostCodes(allCC.filter(c => parseInt(c.level) === 1))
    setRepSubCodes(allCC.filter(c => parseInt(c.level) === 2).map(sub => {
      const parent = byId[sub.parent_id]
      let costCode = null, resourceCode = null
      if (parent && parseInt(parent.level) === 4) {
        resourceCode = parent
        costCode = byId[parent.parent_id] || null
      } else if (parent && parseInt(parent.level) === 1) {
        costCode = parent
      }
      return {
        ...sub,
        costCodeId: costCode?.id || sub.parent_id || null, // fallback preserves old direct-parent behavior
        costCode: costCode?.code || null,
        costCodeDescription: costCode?.description || null,
        resourceCode: resourceCode?.code || null,
        resourceCodeDescription: resourceCode?.description || null,
      }
    }))
    const grouped = {}
    ;(posData || []).forEach(p => { grouped[p.id] = [] })
    ;(linesData || []).forEach(l => { if (grouped[l.po_id] !== undefined) grouped[l.po_id].push(l) })
    setRepLines(grouped)
    setRepLoading(false)
  }, [])

  function repRecalcTotals(lines, vatPct) {
    const totalExc = lines.reduce((s, l) => s + (parseFloat(l.total_cost) || 0), 0)
    const totalInc = lines.reduce((s, l) => s + (parseFloat(l.total_inc_vat) || (parseFloat(l.total_cost)||0) * (1 + (parseFloat(vatPct)||0)/100)), 0)
    return { totalExc, totalInc }
  }

  async function repOpenNew() {
    const base = repEmptyForm()
    // Transaction currency defaults to the project's own currency
    // (rate=1) until the user picks a different one -- same convention
    // as the Normal PO flow above.
    setRepModal({ ...base, currency: selectedProject?.currency || 'SAR', exchange_rate_applied: 1 })
    if (selectedProject?.id) {
      const { data } = await supabase.rpc('get_project_vat_config', { p_project_id: selectedProject.id })
      const row = data?.[0]
      if (row?.vat_treatment === 'standard' && row.vat_rate != null) {
        setRepModal(m => m ? { ...m, vat_pct: row.vat_rate } : m)
      } else if (row?.vat_treatment === 'zero_rated') {
        setRepModal(m => m ? { ...m, vat_pct: 0 } : m)
      }
    }
  }

  // Auto-resolve the exchange rate whenever repModal's currency changes
  // on a NEW Rental PO (repModal.id present means an existing PO being
  // edited -- its historical rate is never re-resolved live).
  useEffect(() => {
    if (!repModal || repModal.id) return
    if (!selectedProject?.currency) return
    if (repModal.currency === selectedProject.currency) {
      setRepModal(m => m && m.exchange_rate_applied !== 1 ? { ...m, exchange_rate_applied: 1 } : m)
      return
    }
    if (!repModal.currency) return
    supabase.rpc('get_exchange_rate', { p_from: repModal.currency, p_to: selectedProject.currency }).then(({ data }) => {
      if (data != null) setRepModal(m => m ? { ...m, exchange_rate_applied: data } : m)
    })
  }, [repModal?.id, repModal?.currency, selectedProject?.currency])

  /**
   * Opens the SAME repModal editor used for a normal new Equipment PO
   * (repOpenNew above) — no separate simplified Addendum form, per the
   * approved plan. The only difference is original_po_id is pre-filled
   * from the contract's currently-approved PO, plus a handful of
   * commercial fields carried over as a starting point (vendor,
   * cost_code_id) — the user still has full editing capability, exactly
   * as they would creating any other Equipment PO.
   */
  /**
   * Opens the SAME repModal editor used for editing an existing
   * Equipment PO — no separate simplified Addendum form, per the
   * approved plan. FIXED (this revision): the original implementation
   * only carried over 3 fields (vendor/cost_code_id/contract_ref),
   * leaving the user to re-enter po_number and every line item from
   * scratch — not what "full existing PO editor" was meant to provide.
   * Now mirrors repOpenEdit's own pattern (line 938 above) almost
   * exactly: the ENTIRE original PO object is spread in as a starting
   * point, including its line items (copied from repLines, stripped of
   * their id/_id so they save as NEW rows, not updates to the original
   * PO's own lines) — the user edits from a complete, realistic
   * starting point exactly as they would editing any PO, then adjusts
   * only what actually changed for this Addendum.
   *
   * id and po_number are explicitly cleared (not spread from
   * originalPo) so handleSave's `if (form.id)` branch takes the INSERT
   * path (a new PO), not the UPDATE path (which would overwrite the
   * original PO itself) — and so po_number isn't silently duplicated
   * across two PO records.
   */
  function repOpenAddendumPO(originalPo) {
    const lines = (repLines[originalPo.id] || []).map(l => {
      const sc = repSubCodes.find(s => s.code === l.equipment_type)
      const { id, _id, ...lineWithoutId } = l
      return { ...lineWithoutId, sub_code_id: sc ? sc.id : '' }
    })
    // Suggest a PO number derived from the original — never reused
    // verbatim (that would collide with the original PO's own record),
    // but the user isn't left staring at an empty required field either.
    // Fully editable, same as every other inherited field here.
    const suggestedNumber = originalPo.po_number ? `${originalPo.po_number}-ADD` : ''
    setRepModal({
      ...originalPo,
      id: undefined,
      po_number: suggestedNumber,
      lines,
      original_po_id: originalPo.id,
      // Fallback if the original PO never had a contract_ref recorded
      // (e.g. created directly from an RFQ Award, which doesn't always
      // populate it) — the Addendum should still reference SOMETHING
      // traceable back to its origin, so the original PO's own number
      // is used as a visible fallback rather than leaving this blank
      // with no indication why.
      contract_ref: originalPo.contract_ref || originalPo.po_number || '',
      notes: `Addendum to ${originalPo.po_number}${originalPo.notes ? ' — ' + originalPo.notes : ''}`,
    })
  }
  function repOpenEdit(po) {
    const lines = (repLines[po.id] || []).map(l => {
      const sc = repSubCodes.find(s => s.code === l.equipment_type)
      return { ...l, _id: l.id, sub_code_id: sc ? sc.id : '' }
    })
    setRepModal({ ...po, lines })
  }
  function repHLine(idx, k, v) {
    setRepModal(m => {
      const lines = [...m.lines]
      const line = { ...lines[idx], [k]: v }
      if (['quantity','rate'].includes(k)) {
        const qty = parseFloat(line.quantity) || 0
        const rate = parseFloat(line.rate) || 0
        const exc = qty * rate
        line.total_cost = exc ? exc.toFixed(2) : ''
        const vat = exc * (parseFloat(m.vat_pct||15) / 100)
        line.total_inc_vat = exc ? (exc + vat).toFixed(2) : ''
      }
      lines[idx] = line
      return { ...m, lines }
    })
  }
  function repAddLine() { setRepModal(m => ({ ...m, lines: [...m.lines, repEmptyLine()] })) }
  function repRemoveLine(idx) { setRepModal(m => ({ ...m, lines: m.lines.filter((_,i) => i!==idx) })) }

  async function repSavePO() {
    if (!repModal.po_number || !repModal.vendor) { showToast('PO Number و Vendor مطلوبين', 'error'); return }
    const validLines = repModal.lines.filter(l => l.equipment_type && l.total_cost)
    if (!validLines.length) { showToast('لازم بند واحد على الأقل', 'error'); return }
    const { totalExc, totalInc } = repRecalcTotals(validLines, repModal.vat_pct)

    // Recomputed locally -- repSavePO() is a standalone function outside
    // the JSX render scope where this flag was originally defined
    // (inside the modal's IIFE, alongside repIsLockedForProcurement).
    // Same exact condition, kept in sync manually since they can't share
    // a single declaration across scopes.
    const repStage = repModal?.approval_stage || 'draft'
    const repIsCostControlPartial = !!repModal.id && can('PO.ApproveCostControl') && !can('PO.Submit') && ['cost_control','director','approved'].includes(repStage)

    // Cost Control partial-edit path (mirrors the approved regular-PO
    // design): touches ONLY cost_code_id at header level and
    // sub_code_id/equipment_type/cost_code_id at line level, via
    // targeted UPDATEs keyed by each row's real database id -- NEVER
    // the DELETE+INSERT replacement used by the full-edit path below.
    // No other header or line field is written. Fails safely (explicit
    // error, no silent partial success) if any existing line lacks a
    // real database id -- Cost Control's UI never allows adding new
    // lines, so this should not occur in normal use.
    if (repModal.id && repIsCostControlPartial) {
      const linesWithoutRealId = repModal.lines.filter(l => l.equipment_type && !l.id)
      if (linesWithoutRealId.length > 0) {
        showToast('تعذر الحفظ: بعض البنود بدون معرف حقيقي في قاعدة البيانات. أعد تحميل الصفحة وحاول مرة أخرى.', 'error')
        return
      }

      const { error: hdrErr } = await supabase.from('rental_equipment_pos').update({
        cost_code_id: repModal.cost_code_id || null,
      }).eq('id', repModal.id)
      if (hdrErr) { showToast('فشل التحديث: ' + hdrErr.message, 'error'); return }

      for (const line of repModal.lines) {
        if (!line.id) continue // new/blank rows -- Cost Control's UI never allows adding lines
        const { error: lineErr } = await supabase.from('rental_equipment_po_line_items').update({
          equipment_type: line.equipment_type || null,
          cost_code_id: line.cost_code_id || null,
        }).eq('id', line.id)
        if (lineErr) { showToast('فشل تحديث البند: ' + lineErr.message, 'error'); return }
      }

      showToast('تم تحديث الـ PO')
      setRepModal(null); loadRepPOs(selectedProject.id)
      return
    }

    // Currency: total_exc_vat/total_inc_vat remain the ORIGINAL
    // transaction-currency amounts (repRecalcTotals computes from the raw
    // line values, no conversion applied) -- project_currency_amount is
    // the separate, explicitly-converted field. An existing PO (repModal.id
    // present) keeps its own already-resolved exchange_rate_applied,
    // never re-resolved live.
    const repRateApplied = parseFloat(repModal.exchange_rate_applied) || 1
    const repProjectCurrencyAmount = +(totalExc * repRateApplied).toFixed(2)
    const payload = {
      project_id: selectedProject.id, po_number: repModal.po_number, vendor: repModal.vendor,
      po_type: 'equipment', cost_code_id: repModal.cost_code_id || null,
      contract_ref: repModal.contract_ref || null, issue_date: repModal.issue_date || null,
      vat_pct: parseFloat(repModal.vat_pct) || 15, total_exc_vat: totalExc, total_inc_vat: totalInc,
      currency: repModal.currency || 'SAR',
      exchange_rate_applied: repRateApplied,
      project_currency_amount: repProjectCurrencyAmount,
      notes: repModal.notes || null,
      original_po_id: repModal.original_po_id || null,
    }
    let poId = repModal.id
    if (poId) {
      const { error } = await supabase.from('rental_equipment_pos').update(payload).eq('id', poId)
      if (error) { showToast('فشل التحديث: ' + error.message, 'error'); return }
      await supabase.from('rental_equipment_po_line_items').delete().eq('po_id', poId)
    } else {
      const { data, error } = await supabase.from('rental_equipment_pos').insert([{ ...payload, status:'draft', approval_stage:'draft' }]).select().single()
      if (error) { showToast('فشل الإنشاء: ' + error.message, 'error'); return }
      poId = data.id
    }
    const lineRows = validLines.map(l => ({
      po_id: poId, equipment_type: l.equipment_type, description: l.description || null, cost_code_id: l.cost_code_id || null,
      quantity: parseFloat(l.quantity) || null, unit: l.unit || 'Hours',
      rate: parseFloat(l.rate) || null, total_cost: parseFloat(l.total_cost) || 0,
      total_inc_vat: parseFloat(l.total_inc_vat) || null,
    }))
    const { error: lineErr } = await supabase.from('rental_equipment_po_line_items').insert(lineRows)
    if (lineErr) { showToast('فشل حفظ البنود: ' + lineErr.message, 'error'); return }
    showToast(repModal.id ? 'تم تحديث الـ PO' : 'تم إنشاء الـ PO')
    setRepModal(null); loadRepPOs(selectedProject.id)
  }

  async function repDeletePO(id) {
    if (!window.confirm('حذف هذا الـ PO؟')) return
    await supabase.from('rental_equipment_po_line_items').delete().eq('po_id', id)
    await supabase.from('rental_equipment_pos').delete().eq('id', id)
    showToast('تم الحذف'); loadRepPOs(selectedProject.id)
  }

  
  const repGetRequiredApprover = getRequiredApprover

  async function repHandleApproval(po, action, reason = '', { skipReload = false, skipToast = false } = {}) {
    const stage = po.approval_stage || 'draft'
    const amount = parseFloat(po.total_exc_vat) || 0
    const requiredApprover = repGetRequiredApprover(amount)
    let update = {}
    if (action === 'submit') {
      update = { approval_stage: 'cost_control' }
    } else if (action === 'approve') {
      if (stage === 'cost_control') {
        if (requiredApprover === 'cost_control') {
          update = { approval_stage: 'approved', status: 'approved', cc_approved_by: currentUserName, cc_approved_at: new Date().toISOString(), pending_addendum_no: null }
        } else {
          update = { approval_stage: 'director', cc_approved_by: currentUserName, cc_approved_at: new Date().toISOString() }
        }
      } else if (stage === 'director') {
        update = { approval_stage: 'approved', status: 'approved', dir_approved_by: currentUserName, dir_approved_at: new Date().toISOString(), pending_addendum_no: null }
      }
    } else if (action === 'reject') {
      update = { approval_stage: 'rejected', status: 'rejected', rejection_reason: reason }
    } else if (action === 'resubmit') {
      update = { approval_stage: 'draft', status: 'draft', rejection_reason: null }
    } else if (action === 'revoke') {
      update = { approval_stage: 'draft', status: 'draft', cc_approved_by: null, cc_approved_at: null, dir_approved_by: null, dir_approved_at: null }
    }
    if (!Object.keys(update).length) return
    const { error } = await supabase.from('rental_equipment_pos').update(update).eq('id', po.id)
    if (error) { showToast('فشل: ' + error.message, 'error'); return }
    // Addendum PO fully approved — same logic as the Material path
    // above (handlePOApproval), applied to equipment_contracts instead
    // of subcontracts.
    if (update.approval_stage === 'approved' && po.original_po_id) {
      const { data: contract } = await supabase.from('equipment_contracts')
        .select('id, contract_value').eq('po_id', po.original_po_id).maybeSingle()
      if (contract) {
        const valueChange = parseFloat(po.total_exc_vat) || 0
        const newContractValue = (parseFloat(contract.contract_value) || 0) + valueChange
        const { data: lastAddendum } = await supabase.from('subcontract_addendums')
          .select('addendum_no').eq('contract_id', contract.id).eq('contract_type', 'equipment')
          .order('addendum_no', { ascending: false }).limit(1).maybeSingle()
        const nextAddendumNo = (lastAddendum?.addendum_no || 0) + 1
        await supabase.from('subcontract_addendums').insert({
          contract_id: contract.id, contract_type: 'equipment', addendum_no: nextAddendumNo,
          description: `Addendum PO ${po.po_number} (fully approved)`,
          value_change: valueChange, new_contract_value: newContractValue,
          addendum_date: new Date().toISOString().slice(0, 10),
          created_by: currentUserName, addendum_po_id: po.id,
        })
        await supabase.from('equipment_contracts').update({ contract_value: newContractValue }).eq('id', contract.id)
      }
    }
    await logAudit(po.id, 'rental_equipment_po', action, currentUserName, reason, po.approval_stage, update.approval_stage, selectedProject.id)
    if (!skipToast) showToast('تم التحديث')
    if (!skipReload) loadRepPOs(selectedProject.id)
  }

  function repGetApprovalButtons(po) {
    const stage = po.approval_stage || 'draft'
    const amount = parseFloat(po.total_exc_vat) || 0
    const requiredApprover = repGetRequiredApprover(amount)
    const btns = []
    if (stage === 'draft' && can('PO.Submit')) btns.push({ label: 'Submit for Approval', action: 'submit', color: 'bg-blue-600 text-white' })
    if (stage === 'cost_control' && can('PO.ApproveCostControl')) {
      btns.push({ label: '✓ Approve', action: 'approve', color: 'bg-green-600 text-white' })
      btns.push({ label: '✗ Reject', action: 'reject', color: 'bg-red-100 text-red-700' })
    }
    if (stage === 'director' && can('PO.ApproveDirector')) {
      btns.push({ label: '✓ Final Approve', action: 'approve', color: 'bg-green-600 text-white' })
      btns.push({ label: '✗ Reject', action: 'reject', color: 'bg-red-100 text-red-700' })
    }
    if (stage === 'rejected' && can('PO.Submit')) {
      btns.push({ label: '↻ Resubmit', action: 'resubmit', color: 'bg-blue-600 text-white' })
    }
    if (stage === 'approved') {
      const finalApprover = po.dir_approved_by ? 'director' : 'cost_control'
      if ((finalApprover === 'director' && can('PO.ApproveDirector')) || (finalApprover === 'cost_control' && can('PO.ApproveCostControl'))) {
        btns.push({ label: '↺ Revoke Approval', action: 'revoke', color: 'bg-amber-100 text-amber-800' })
      }
    }
    return { btns, requiredApprover }
  }

  function repDownloadTemplate() {
    const ws = XLSX.utils.json_to_sheet([
      { 'po number':'2505670', 'Supplier Name':'Fatima International', 'Cost Code':'41.006110', 'cost code Description':'PMV General Use', 'subcode':'telehander', 'Equipment Description':'Telehandler 10T Manitou', 'Quantity':1062, 'unit':'Hours', 'rate':59.15, 'Total exc VAT':62813.5, 'Total inc VAT':72235.53 },
    ])
    const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, 'po equipment'); XLSX.writeFile(wb, 'rental_equipment_po_template.xlsx')
  }

  function repHandleFileUpload(e) {
    const file = e.target.files[0]; if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => {
      try { const wb = XLSX.read(ev.target.result, { type: 'binary' }); const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]); if (rows.length === 0) return; setRepUploadRows(rows) } catch(err) { console.error(err) }
    }
    reader.readAsBinaryString(file)
  }

  function repPrepareUpload() {
    function norm(row) {
      const clean = s => String(s||'').trim().toLowerCase().replace(/[\s_\-]+/g,'')
      const get = (...labels) => { for (const l of labels) { const found = Object.keys(row).find(k => clean(k)===clean(l)); if (found!==undefined && row[found]!==undefined && row[found]!=='') return row[found] } return '' }
      return {
        po_number: String(get('po number','po_number','PO')).trim(),
        vendor: String(get('Supplier Name','vendor','supplier')).trim(),
        cost_code: String(get('Cost Code','cost_code')).trim(),
        equipment_type: String(get('subcode','equipment_type','sub_code','sub code')).trim(),
        description: String(get('Equipment Description','description')).trim(),
        quantity: get('Quantity','quantity'),
        unit: String(get('unit')).trim() || 'Hours',
        rate: get('rate'),
        total_exc_vat: get('Total exc VAT','total_exc_vat','Total cost'),
        total_inc_vat: get('Total inc VAT','total_inc_vat'),
      }
    }
    const ccNorm = s => {
      const str = String(s||'').trim()
      const asNum = parseFloat(str)
      return !isNaN(asNum) && str !== '' ? asNum.toString() : str.replace(/^0+(?=\d)/,'')
    }
    const scNorm = s => String(s||'').trim().toLowerCase().replace(/[\s_\-]+/g,'')
    const byPo = {}
    let skipped = 0
    const unmatchedList = []
    for (const raw of repUploadRows) {
      const r = norm(raw)
      if (!r.po_number || !r.equipment_type) { skipped++; continue }
      const cc = repCostCodes.find(c => ccNorm(c.code) === ccNorm(r.cost_code))
      const sc = repSubCodes.find(s => scNorm(s.code) === scNorm(r.equipment_type))
      if (!byPo[r.po_number]) byPo[r.po_number] = { po_number: r.po_number, vendor: r.vendor, cost_code_id: cc?.id || null, lines: [] }
      const total = parseFloat(r.total_exc_vat) || 0
      const lineObj = {
        _uid: Math.random(), equipment_type: sc ? sc.code : r.equipment_type, sub_code_id: sc ? sc.id : '',
        description: r.description || '',
        cost_code_id: sc ? (sc.costCodeId || cc?.id || null) : (cc?.id || null),
        cost_code_raw: r.cost_code, quantity: parseFloat(r.quantity) || null, unit: r.unit,
        rate: parseFloat(r.rate) || null, total_cost: total,
        total_inc_vat: parseFloat(r.total_inc_vat) || null,
      }
      byPo[r.po_number].lines.push(lineObj)
      if (!cc && r.cost_code) unmatchedList.push({ po_number: r.po_number, equipment_type: r.equipment_type, cost_code_raw: r.cost_code, _uid: lineObj._uid })
    }
    if (skipped) showToast(`تم تجاهل ${skipped} صف (بدون PO Number أو Equipment Type)`, 'error')
    setRepPendingGroups(byPo)
    if (unmatchedList.length) {
      setRepUnmatched(unmatchedList)
      setRepHadUnmatched(true)
    } else {
      repRunUpload(byPo)
    }
  }

  function repResolveUnmatched(uid, costCodeId) {
    setRepPendingGroups(groups => {
      const updated = { ...groups }
      Object.values(updated).forEach(g => {
        g.lines = g.lines.map(l => l._uid === uid ? { ...l, cost_code_id: costCodeId } : l)
      })
      return updated
    })
    setRepUnmatched(list => list.filter(u => u._uid !== uid))
  }

  useEffect(() => {
    // لما تتحل كل الأكواد الغير متطابقة (وكانت فعلاً موجودة)، كمّل الرفع تلقائيًا
    if (repHadUnmatched && repPendingGroups && repUnmatched.length === 0 && !repUploading) {
      setRepHadUnmatched(false)
      repRunUpload(repPendingGroups)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repUnmatched])

  async function repConfirmUpload() {
    if (repUnmatched.length) { showToast('لازم تحل كل الأكواد الغير متطابقة الأول', 'error'); return }
    await repRunUpload(repPendingGroups)
  }

  async function repRunUpload(groups) {
    if (!can('PO.Create')) {
      showToast('Bulk upload is only available to Procurement.', 'error')
      return
    }
    const poGroups = Object.values(groups)

    // Block upload for PO numbers that already exist and are not draft/rejected
    const poNumbers = poGroups.map(g => g.po_number)
    const { data: existing } = await supabase
      .from('rental_equipment_pos')
      .select('id, po_number, approval_stage')
      .eq('project_id', selectedProject.id)
      .in('po_number', poNumbers)
    const blocked = (existing || []).filter(e => !['draft','rejected'].includes(e.approval_stage || 'draft'))
    if (blocked.length) {
      showToast(`Upload blocked — these PO numbers are already submitted/approved and can't be re-uploaded: ${blocked.map(b=>b.po_number).join(', ')}`, 'error')
      return
    }

    setRepUploading(true); setRepUploadProgress({ done: 0, total: poGroups.length })
    let success = 0, failed = 0
    const uploadErrors = []
    const existingMap = {}
    ;(existing || []).forEach(e => { existingMap[e.po_number] = e })
    // Resolved once for the whole batch, not per-row -- every PO created
    // by this upload belongs to the same selectedProject.
    let uploadVatMultiplier = 1.15
    {
      const { data: vatRow } = await supabase.rpc('get_project_vat_config', { p_project_id: selectedProject.id })
      const row = vatRow?.[0]
      if (row?.vat_treatment === 'standard' && row.vat_rate != null) uploadVatMultiplier = 1 + row.vat_rate / 100
      else if (row?.vat_treatment === 'zero_rated') uploadVatMultiplier = 1
    }
    for (let i = 0; i < poGroups.length; i++) {
      const g = poGroups[i]
      const totalExc = g.lines.reduce((s,l)=>s+(l.total_cost||0),0)
      const totalInc = g.lines.reduce((s,l)=>s+(l.total_inc_vat||l.total_cost*uploadVatMultiplier||0),0)
      const existingPO = existingMap[g.po_number]
      let poId

      if (existingPO) {
        // PO number already exists (draft/rejected) — update it in place instead of creating a duplicate
        const { error: updErr } = await supabase.from('rental_equipment_pos').update({
          vendor: g.vendor, cost_code_id: g.cost_code_id, status: 'draft', approval_stage: 'draft',
          total_exc_vat: totalExc, total_inc_vat: totalInc,
        }).eq('id', existingPO.id)
        if (updErr) { failed++; uploadErrors.push(`${g.po_number}: ${updErr.message}`); setRepUploadProgress({ done: i+1, total: poGroups.length }); continue }
        poId = existingPO.id
        await supabase.from('rental_equipment_po_line_items').delete().eq('po_id', poId)
      } else {
        const { data, error } = await supabase.from('rental_equipment_pos').insert([{
          project_id: selectedProject.id, po_number: g.po_number, vendor: g.vendor,
          po_type: 'equipment', cost_code_id: g.cost_code_id, status: 'draft', approval_stage: 'draft',
          vat_pct: (uploadVatMultiplier - 1) * 100, total_exc_vat: totalExc, total_inc_vat: totalInc,
        }]).select().single()
        if (error) { failed++; uploadErrors.push(`${g.po_number}: ${error.message}`); setRepUploadProgress({ done: i+1, total: poGroups.length }); continue }
        poId = data.id
      }

      const lineRows = g.lines.map(({_uid, cost_code_raw, sub_code_id, ...l}) => ({ ...l, po_id: poId }))
      const { error: lineErr } = await supabase.from('rental_equipment_po_line_items').insert(lineRows)
      if (lineErr) { failed++; uploadErrors.push(`${g.po_number} (lines): ${lineErr.message}`) } else success++
      setRepUploadProgress({ done: i+1, total: poGroups.length })
    }
    setRepUploading(false)
    showToast(uploadErrors.length ? `تم رفع ${success} — فشل ${failed}: ${uploadErrors[0]}` : `تم رفع ${success} PO بنجاح`, uploadErrors.length ? 'error' : 'success')
    setRepUploadRows([]); setRepPendingGroups(null); setRepUnmatched([]); setRepShowUpload(false)
    if (repFileRef.current) repFileRef.current.value = ''
    loadRepPOs(selectedProject.id)
  }

  function repExportToExcel() {
    const rows = []
    repFilteredPOs.forEach(po => {
      const lines = repLines[po.id] || []
      if (!lines.length) { rows.push({ 'PO Number': po.po_number, 'Vendor': po.vendor, 'Cost Code': po.cost_codes?.code||'', 'Sub Code':'', 'Equipment Description':'', 'Quantity':'', 'Unit':'', 'Rate':'', 'Total exc VAT': po.total_exc_vat, 'Total inc VAT': po.total_inc_vat, 'Status': po.approval_stage }) }
      lines.forEach(l => rows.push({ 'PO Number': po.po_number, 'Vendor': po.vendor, 'Cost Code': po.cost_codes?.code||'', 'Sub Code': l.equipment_type, 'Equipment Description': l.description||'', 'Quantity': l.quantity, 'Unit': l.unit, 'Rate': l.rate, 'Total exc VAT': l.total_cost, 'Total inc VAT': l.total_inc_vat, 'Status': po.approval_stage }))
    })
    const ws = XLSX.utils.json_to_sheet(rows); const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, 'Rental Equipment POs'); XLSX.writeFile(wb, 'rental_equipment_pos.xlsx')
  }

  const repStageCounts = {
    all: repPOs.length,
    draft: repPOs.filter(p => (p.approval_stage||'draft')==='draft').length,
    cost_control: repPOs.filter(p => p.approval_stage==='cost_control').length,
    director: repPOs.filter(p => p.approval_stage==='director').length,
    approved: repPOs.filter(p => p.approval_stage==='approved').length,
    rejected: repPOs.filter(p => p.approval_stage==='rejected').length,
  }

  const repFilteredPOs = repPOs.filter(p => {
    if (repPipelineStage !== 'all' && (p.approval_stage||'draft') !== repPipelineStage) return false
    if (repSearch && !(p.po_number?.toLowerCase().includes(repSearch.toLowerCase()) || p.vendor?.toLowerCase().includes(repSearch.toLowerCase()))) return false
    return true
  })

  // Re-check horizontal overflow for the Rental Equipment table's
  // floating nav whenever the visible row count changes or the window
  // resizes -- same pattern as the Material PO effect above.
  useEffect(() => {
    updateRepScrollState()
    const el = repTableScrollRef.current
    if (!el) return
    const ro = new ResizeObserver(updateRepScrollState)
    ro.observe(el)
    if (repTableCardRef.current) ro.observe(repTableCardRef.current)
    window.addEventListener('resize', updateRepScrollState)
    window.addEventListener('scroll', updateRepScrollState, true)
    return () => { ro.disconnect(); window.removeEventListener('resize', updateRepScrollState); window.removeEventListener('scroll', updateRepScrollState, true) }
  }, [repFilteredPOs.length, updateRepScrollState])

  function repToggleSelect(id) { setRepSelected(s => s.includes(id) ? s.filter(x=>x!==id) : [...s, id]) }
  function repToggleSelectAll() { setRepSelected(s => s.length === repFilteredPOs.length ? [] : repFilteredPOs.map(p=>p.id)) }
  async function repBulkAction(actionType) {
    if (repBulkBusy || !repSelected.length) return
    setRepBulkBusy(true)

    if (actionType === 'delete') {
      if (!window.confirm(`حذف ${repSelected.length} PO محدد؟`)) { setRepBulkBusy(false); return }
      await supabase.from('rental_equipment_po_line_items').delete().in('po_id', repSelected)
      await supabase.from('rental_equipment_pos').delete().in('id', repSelected)
      showToast(`تم حذف ${repSelected.length} PO`)
      setRepSelected([])
      await loadRepPOs(selectedProject.id)
      setRepBulkBusy(false)
      return
    }

    let reason = ''
    if (actionType === 'reject') {
      reason = prompt('سبب الرفض (لكل الـPOs المحددة) — مطلوب، لن يتم الرفض بدون سبب:') || ''
      if (reason === '') { setRepBulkBusy(false); showToast('تم الإلغاء — لم يتم إدخال سبب الرفض', 'error'); return }
    }
    if (actionType === 'revoke') {
      if (!window.confirm(`Revoke approval for ${repSelected.length} PO(s) and return them to Draft?`)) { setRepBulkBusy(false); return }
    }

    let acted = 0, skipped = 0
    for (const id of repSelected) {
      const po = repPOs.find(p=>p.id===id)
      if (!po) continue
      const { btns } = repGetApprovalButtons(po)
      let validBtn
      if (actionType === 'approve') validBtn = btns.find(b => b.action === 'submit' || b.action === 'approve' || b.action === 'resubmit')
      else if (actionType === 'reject') validBtn = btns.find(b => b.action === 'reject')
      else if (actionType === 'revoke') validBtn = btns.find(b => b.action === 'revoke')
      if (validBtn) { await repHandleApproval(po, validBtn.action, reason, { skipReload: true, skipToast: true }); acted++ }
      else skipped++
    }
    setRepSelected([])
    await loadRepPOs(selectedProject.id)
    setRepBulkBusy(false)
    showToast(skipped ? `تم تنفيذ ${acted} — تم تجاهل ${skipped} (مش صلاحيتك في مرحلتهم الحالية)` : `تم تنفيذ ${acted} بنجاح`)
  }

  const openConvert = (po) => {
    setConvertForm({
      contract_no: '',
      contractor_name: po.vendor || '',
      contract_value: po.total_exc_vat || '',
      vat_pct: po.vat_pct || 15,
      contract_date: po.issue_date || '',
      retention_pct: 10,
      advance_pct: 0,
      payment_terms: 30,
      scope: po.notes || '',
      cost_code_id: po.cost_code_id || '',
    })
    setConvertPO(po)
  }

  const handleConvert = async () => {
    if (!convertForm.contract_no || !convertForm.contractor_name) {
      showToast('Contract No و Contractor Name مطلوبان', 'error'); return
    }
    setConverting(true)
    const cv = parseFloat(convertForm.contract_value) || 0
    const vat = parseFloat(convertForm.vat_pct) || 0
    const { error } = await supabase.from('subcontracts').insert({
      project_id: selectedProject.id,
      po_id: convertPO.id,
      contract_no: convertForm.contract_no,
      contractor_name: convertForm.contractor_name,
      contract_value: cv,
      vat_pct: vat,
      contract_inc_vat: cv * (1 + vat / 100),
      contract_date: convertForm.contract_date || null,
      retention_pct: parseFloat(convertForm.retention_pct) || 0,
      advance_pct: parseFloat(convertForm.advance_pct) || 0,
      payment_terms: parseInt(convertForm.payment_terms) || 30,
      scope: convertForm.scope || null,
      cost_code_id: convertForm.cost_code_id || null,
      currency: 'SAR',
      status: 'draft',
    })
    setConverting(false)
    if (error) { showToast('خطأ: ' + error.message, 'error'); return }
    setConvertedIds(prev => ({ ...prev, [convertPO.id]: convertForm.contract_no }))
    setConvertPO(null)
    showToast('تم تحويل الـ PO إلى عقد ✓')
  }

  const toggleExpand = async (po) => {
    const id = po.id
    setExpandedRows(prev => {
      const isOpen = !!prev[id]
      if (isOpen) return { ...prev, [id]: false }
      return { ...prev, [id]: true }
    })
    if (!poLines[id]) {
      const { data } = await supabase.from('po_line_items').select('id,po_id,ref_code,sub_code,description,cost_code_id,unit,qty,unit_rate,amount_exc_vat,amount_inc_vat,addendum_no').eq('po_id', id).order('created_at')
      setPoLines(prev => ({ ...prev, [id]: data||[] }))
    }
  }

  const showToast = (msg, type='success') => { setToast({message:msg,type}); setTimeout(()=>setToast(null),4000) }

  useEffect(() => {
    const _pc = getCached('projects_list_c')
    if (_pc) setProjects(_pc)
    else supabase.from('projects').select('id,name,currency,organizations(name)').order('name').then(({data})=>{ setProjects(data||[]); if(data) setCached('projects_list_c',data) })
    // cost_codes and analysis_codes are loaded per-project in the useEffect below
  }, [])

  const loadPOs = useCallback(async (projectId) => {
    setLoading(true)
    const [posResult, linesResult, subContractsResult] = await Promise.all([
      supabase.from('purchase_orders').select('*,cost_codes(code)').eq('project_id', projectId).order('created_at', { ascending: false }),
      supabase.from('po_line_items').select('id,po_id,ref_code,sub_code,description,cost_code_id,unit,qty,unit_rate,amount_exc_vat,amount_inc_vat,purchase_orders!inner(project_id)').eq('purchase_orders.project_id', projectId).order('created_at'),
      // Same reasoning as loadRepPOs' eqContractsData — approval_stage/
      // status alone can't distinguish "draft PO with a real linked
      // Contract" from "brand-new never-approved PO" after a Revoke.
      supabase.from('subcontracts').select('po_id').eq('project_id', projectId),
    ])
    if(posResult.error) showToast('Failed to load: '+posResult.error.message,'error')
    else {
      setPOs(posResult.data||[])
      const grouped = {}
      ;(posResult.data||[]).forEach(p => { grouped[p.id] = [] })
      ;(linesResult.data||[]).forEach(l => { if(grouped[l.po_id] !== undefined) grouped[l.po_id].push(l) })
      setPoLines(grouped)
      setContractPoIds(new Set((subContractsResult.data || []).map(c => c.po_id).filter(Boolean)))
    }
    setLoading(false)
    invalidateBudgetCache() // Phase PO-3: any PO write flow ends by calling loadPOs() (7 existing call sites) — recompute Budget vs Commitment automatically, with zero extra wiring per call site.
  }, [invalidateBudgetCache])

  useEffect(() => {
    if (!selectedProject?.id) return
    loadPOs(selectedProject.id)
    supabase.from('cost_codes').select('id,code,description,level,parent_id').eq('project_id', selectedProject.id).eq('is_active', true).order('code').then(({data})=>{
      const all = data||[]
      setCostCodes(all)
      // Build Sub Codes list (level 2) with resolved ancestry: parent Cost Code (level 1),
      // and if the parent is actually a Resource Code (level 4), resolve its own parent too.
      const byId = {}
      all.forEach(c => { byId[c.id] = c })
      const subs = all.filter(c => parseInt(c.level) === 2).map(sub => {
        const parent = byId[sub.parent_id]
        let costCode = null, resourceCode = null
        if (parent && parseInt(parent.level) === 4) {
          resourceCode = parent
          costCode = byId[parent.parent_id] || null
        } else if (parent && parseInt(parent.level) === 1) {
          costCode = parent
        }
        return {
          ...sub,
          costCodeId: costCode?.id || null,
          costCode: costCode?.code || '—',
          costCodeDescription: costCode?.description || '',
          resourceCode: resourceCode?.code || null,
          resourceCodeDescription: resourceCode?.description || null,
        }
      })
      setSubCodesList(subs)
    })
    supabase.from('analysis_codes').select('*').eq('project_id', selectedProject.id).order('code').then(({data})=>setAnalysisCodes(data||[]))
  }, [selectedProject?.id, loadPOs])

  // Live Forecast — via resolveCurrentForecastState(), the official Source
  // of Truth for "current effective EAC" (used identically by Dashboard.jsx
  // and CostControl.jsx). Re-fetched on project change or budgetRefreshTick,
  // matching Budget/Commitment's own refresh cadence, so all three (Budget,
  // Commitment, Forecast) stay in sync after any PO write.
  // buildForecastNodeIndex() flattens the returned Cost Code -> resourceRows
  // -> children tree into a flat Map<code, node> — reused as-is rather than
  // re-implementing tree traversal here (same SSOT principle as reusing
  // resolveDirectKey/resolveIndirectKey from budgetVsCommitment.js).
  useEffect(() => {
    if (!selectedProject?.id) return
    let cancelled = false
    resolveCurrentForecastState(selectedProject.id, 'direct').then(resolvedTree => {
      if (cancelled) return
      const index = buildForecastNodeIndex(resolvedTree)
      const map = {}
      for (const [code, node] of index.entries()) {
        const k = normSC(code)
        if (!k) continue
        map[k] = { resolvedEac: node.resolvedEac, isOwner: node.isOwner, hasEffectiveForecast: node.hasEffectiveForecast }
      }
      setForecastByResource(map)
    })
    return () => { cancelled = true }
  }, [selectedProject?.id, budgetRefreshTick])

  // Phase PO-3: project-wide Budget vs Commitment recompute. Runs once per
  // project selection and again whenever invalidateBudgetCache() bumps
  // budgetRefreshTick (triggered automatically by loadPOs() after every
  // save/approve/reject/delete). This replaces the old per-PO-open inline
  // query (which re-fetched the ENTIRE project's po_line_items on every
  // single "open details" click) with a single project-level computation
  // that all open detail panels then read from directly.
  useEffect(() => {
    if (!selectedProject?.id) return
    let cancelled = false
    ;(async () => {
      const maps = await getCachedCostCodeMaps(selectedProject.id)

      // ── DIRECT ──────────────────────────────────────────────────
      const { data: tRows } = await supabase.from('tender_direct_items')
        .select('resource_code, row_type, qty, unit_rate')
        .eq('project_id', selectedProject.id)
        .in('row_type', ['material', 'labour', 'subcontract', 'equipment'])
      const directBudgetMap = {}
      const directBudgetValueMap = {} // Card 3: Budget Value (SAR) = qty × unit_rate, same resource_code key/rows as directBudgetMap (Qty) -- NOT total_budget (unreliable/zero, confirmed by inspection). Material and Equipment share this exact same aggregation, no separate logic.
      ;(tRows || []).forEach(r => {
        const k = normSC(r.resource_code)
        if (!k) return
        directBudgetMap[k] = (directBudgetMap[k] || 0) + (parseFloat(r.qty) || 0)
        directBudgetValueMap[k] = (directBudgetValueMap[k] || 0) + ((parseFloat(r.qty) || 0) * (parseFloat(r.unit_rate) || 0))
      })

      const { data: directLines } = await supabase.from('po_line_items')
        .select('sub_code, qty, amount_exc_vat, purchase_orders!inner(project_id, status, cost_type)')
        .eq('purchase_orders.project_id', selectedProject.id)
        .in('purchase_orders.status', ['approved', 'closed'])
      const directCommMap = {}
      const directCommValueMap = {} // Card 2: Committed Value (exc VAT), same Resource Code key, same status filter -- separate from directCommMap (qty basis, Card 1)
      const resolution = {}
      ;(directLines || []).filter(l => isDirectPO(l.purchase_orders)).forEach(l => {
        const r = resolveDirectKey(l.sub_code, maps)
        if (r.key) {
          directCommMap[r.key] = (directCommMap[r.key] || 0) + (parseFloat(l.qty) || 0)
          directCommValueMap[r.key] = (directCommValueMap[r.key] || 0) + (parseFloat(l.amount_exc_vat) || 0)
        }
      })

      // ── INDIRECT ────────────────────────────────────────────────
      const { data: iRows } = await supabase.from('tender_indirect_items')
        .select('cost_code, tender_budget')
        .eq('project_id', selectedProject.id)
      const indirectBudgetMap = {}
      ;(iRows || []).forEach(r => {
        const k = normSC(r.cost_code)
        if (!k) return
        indirectBudgetMap[k] = (indirectBudgetMap[k] || 0) + (parseFloat(r.tender_budget) || 0)
      })

      const { data: indirectLines } = await supabase.from('po_line_items')
        .select('sub_code, cost_code_id, amount_exc_vat, purchase_orders!inner(project_id, status, cost_type)')
        .eq('purchase_orders.project_id', selectedProject.id)
        .eq('purchase_orders.cost_type', 'indirect')
        .in('purchase_orders.status', ['approved', 'closed'])
      const indirectCommMap = {}
      ;(indirectLines || []).forEach(l => {
        const r = resolveIndirectKey(l.cost_code_id, l.sub_code, maps)
        if (r.key) indirectCommMap[r.key] = (indirectCommMap[r.key] || 0) + (parseFloat(l.amount_exc_vat) || 0)
      })

      // Per-line resolution map (for both Direct and Indirect lines),
      // used by the display to group/label each line's Resource Code /
      // Cost Code / Legacy / Unknown status without re-deriving it there.
      ;(directLines || []).filter(l => isDirectPO(l.purchase_orders)).forEach(l => {
        // direct lines don't carry their own id in this select -- resolved per-line at display time instead via resolveDirectKey(l.sub_code, maps)
      })

      if (cancelled) return
      setBudgetQtys({ direct: directBudgetMap, indirect: indirectBudgetMap })
      setBudgetValueByResource(directBudgetValueMap)
      setCommittedQtys({ direct: directCommMap, indirect: indirectCommMap })
      setCommittedValueByResource(directCommValueMap) // Card 2: Direct only (Indirect Value comparison not in this scope's approved design)
    })()
    return () => { cancelled = true }
  }, [selectedProject?.id, budgetRefreshTick])

  const handleProjectSelect = (p) => {
    setSelectedProject(p); setPOs([]); setSearch(''); setTypeFilter('all'); setStatusFilter('all')
    if(p) loadPOs(p.id)
  }

  const filtered = pos.filter(p => {
    const matchSearch = !search ||
      p.po_number?.toLowerCase().includes(search.toLowerCase()) ||
      p.vendor?.toLowerCase().includes(search.toLowerCase()) ||
      p.sub_code?.toLowerCase().includes(search.toLowerCase())
    const matchType = typeFilter==='all' || p.po_type===typeFilter
    const matchStatus = statusFilter==='all' || p.status===statusFilter
    // Sub code filter: check if any line item has this sub_code (uses cached poLines)
    const matchAnalysis = analysisFilter==='all' || (() => {
      const lines = poLines[p.id] || []
      return lines.some(l => (l.sub_code||'').toUpperCase() === analysisFilter.toUpperCase())
    })()
    return matchSearch && matchType && matchStatus && matchAnalysis
  })

  // Stats — based on filtered POs
  // Pipeline stage counts (from all POs, not filtered)
  const stageCounts = {
    all: pos.length,
    draft: pos.filter(p=>p.status==='draft').length,
    approved: pos.filter(p=>p.status==='approved').length,
    closed: pos.filter(p=>p.status==='closed').length,
    cancelled: pos.filter(p=>p.status==='cancelled').length,
  }

  // Apply pipeline filter on top of search/type filters
  const pipelineFiltered = pipelineStage === 'all' ? filtered : filtered.filter(p => p.status === pipelineStage)

  // Re-check horizontal overflow whenever the visible row count changes
  // (filter/search/pipeline tab switch) or the window resizes -- the
  // floating nav must appear/disappear based on actual overflow, never
  // a fixed assumption.
  useEffect(() => {
    updatePoScrollState()
    const el = poTableScrollRef.current
    if (!el) return
    const ro = new ResizeObserver(updatePoScrollState)
    ro.observe(el)
    if (poTableCardRef.current) ro.observe(poTableCardRef.current)
    window.addEventListener('resize', updatePoScrollState)
    window.addEventListener('scroll', updatePoScrollState, true)
    return () => { ro.disconnect(); window.removeEventListener('resize', updatePoScrollState); window.removeEventListener('scroll', updatePoScrollState, true) }
  }, [pipelineFiltered.length, updatePoScrollState])

  const stats = {
    total: pipelineFiltered.length,
    totalExcVat: pipelineFiltered.reduce((s,p)=>s+(parseFloat(p.total_exc_vat)||0),0),
    totalIncVat: pipelineFiltered.reduce((s,p)=>s+(parseFloat(p.total_inc_vat)||0),0),
    issued: pipelineFiltered.reduce((s,p)=>s+(parseFloat(p.issued_value)||0),0),
    paid: pipelineFiltered.reduce((s,p)=>s+(parseFloat(p.paid_value)||0),0),
  }

  const handleSave = async (form, totalExcVat, totalIncVat, currencyInfo) => {
    if(!form.po_number || !form.vendor) return showToast('PO Number and Vendor are required','error')
    if(!form.lines?.length || !form.lines.some(l=>l.description)) return showToast('Add at least one line item','error')
    const multiplier = currencyInfo?.multiplier || 1
    // Currency Audit Trail: capture the PRE-edit stored currency/rate
    // before any UPDATE, so the audit comment can state exactly what
    // changed. Only relevant for an edit (form.id present); a brand-new
    // PO has no "before" state to compare against.
    let priorCurrencySnapshot = null
    if (form.id) {
      const { data: priorRow } = await supabase.from('purchase_orders')
        .select('currency, exchange_rate_applied').eq('id', form.id).single()
      priorCurrencySnapshot = priorRow || null
    }
    // Currency architecture: total_exc_vat/total_inc_vat remain the
    // ORIGINAL transaction-currency amounts (currencyInfo.originalExcVat/
    // originalIncVat) -- never the pre-converted SAR figures previously
    // passed in as totalExcVat/totalIncVat, which this page never
    // actually consumed for storage. project_currency_amount is the new,
    // separate, explicitly-converted field. An existing PO being edited
    // keeps its own already-applied historical rate (form.exchange_rate_applied
    // was never re-resolved live for an existing `po`, per the VAT-parity
    // rule already applied to exchange_rate_applied's own useEffect).
    const originalExc = currencyInfo?.originalExcVat ?? totalExcVat
    const rateApplied = parseFloat(form.exchange_rate_applied) || 1
    const projectCurrencyAmount = +(originalExc * rateApplied).toFixed(2)

    let poId = form.id
    if(form.id) {
      // Edit: never overwrite issued/delivered/paid/poc — updated by Store & Finance
      const { error } = await supabase.from('purchase_orders').update({
        po_number: form.po_number, vendor: form.vendor,
        po_type: form.po_type, cost_type: form.cost_type,
        logistics_type: form.logistics_type || 'oceanarium',
        sub_code: form.sub_code||null, contract_ref: form.contract_ref||null,
        cost_code_id: form.cost_code_id||null,
        issue_date: form.issue_date||null, status: form.status,
        vat_pct: parseFloat(form.vat_pct)||15,
        total_exc_vat: originalExc, total_inc_vat: currencyInfo?.originalIncVat ?? totalIncVat,
        currency: form.currency || 'SAR',
        exchange_rate_applied: rateApplied,
        project_currency_amount: projectCurrencyAmount,
        currency_multiplier: multiplier,
        notes: form.notes||null,
      }).eq('id',form.id)
      if(error) return showToast('Update failed: '+error.message,'error')
    } else {
      const { data, error } = await supabase.from('purchase_orders').insert([{
        project_id: selectedProject.id,
        po_number: form.po_number, vendor: form.vendor,
        po_type: form.po_type, cost_type: form.cost_type,
        logistics_type: form.logistics_type || 'oceanarium',
        sub_code: form.sub_code||null, contract_ref: form.contract_ref||null,
        cost_code_id: form.cost_code_id||null,
        original_po_id: form.original_po_id || null,
        issue_date: form.issue_date||null, status: form.status,
        vat_pct: parseFloat(form.vat_pct)||15,
        total_exc_vat: originalExc, total_inc_vat: currencyInfo?.originalIncVat ?? totalIncVat,
        currency: form.currency || 'SAR',
        exchange_rate_applied: rateApplied,
        project_currency_amount: projectCurrencyAmount,
        currency_multiplier: multiplier,
        notes: form.notes||null,
        delivered_value: 0, issued_value: 0, paid_value: 0, poc: 0,
        approval_stage: 'draft',
      }]).select()
      if(error) return showToast('Insert failed: '+error.message,'error')
      poId = data[0].id
    }

    // Save line items — delete only after lines ready
    if(poId) {
      const lines = form.lines.filter(l=>l.description).map(l=>({
        po_id: poId,
        ref_code: l.ref_code||null, sub_code: l.sub_code||null,
        description: l.description,
        cost_code_id: l.cost_code_id||null, boq_item_id: null,
        unit: l.unit||null,
        qty: parseFloat(l.qty)||0,
        unit_rate: (parseFloat(l.unit_rate)||0) * multiplier,
        amount_inc_vat: (parseFloat(l.amount_inc_vat)||(parseFloat(l.qty||0)*parseFloat(l.unit_rate||0)*(1+(parseFloat(form.vat_pct)||15)/100))) * multiplier,
      }))
      if(lines.length > 0) {
        await supabase.from('po_line_items').delete().eq('po_id', poId)
        const { error } = await supabase.from('po_line_items').insert(lines)
        if(error) return showToast('Lines save failed: '+error.message,'error')
      }
    }

    // Log audit trail
    const action = form.id ? 'edited' : 'created'
    const actorName = (await supabase.auth.getUser()).data?.user?.email || 'Unknown'
    // Currency Audit Trail: only surface a currency-specific comment when
    // this save actually established or changed the transaction currency
    // /exchange rate -- never a comment implying a rate change occurred
    // when it didn't (e.g. editing only the vendor name on a same-currency
    // PO stays silent on currency, exactly as before this change).
    const newCurrency = form.currency || 'SAR'
    let currencyComment = null
    if (!form.id) {
      if (newCurrency !== (selectedProject?.currency || 'SAR')) {
        currencyComment = `Currency: ${newCurrency} @ rate ${rateApplied} (Project currency: ${selectedProject?.currency || 'SAR'})`
      }
    } else if (priorCurrencySnapshot && (priorCurrencySnapshot.currency !== newCurrency || parseFloat(priorCurrencySnapshot.exchange_rate_applied) !== rateApplied)) {
      currencyComment = `Currency changed: ${priorCurrencySnapshot.currency || 'SAR'} @ ${priorCurrencySnapshot.exchange_rate_applied ?? 1} → ${newCurrency} @ ${rateApplied}`
    }
    // eslint-disable-next-line no-undef
    await logAudit(poId, 'purchase_order', action, actorName, currencyComment, null, form.status, selectedProject.id)

    showToast(form.id ? 'PO updated' : 'PO created successfully')
    setModal(null)
    loadPOs(selectedProject.id)
    if (form.id) {
      supabase.from('po_line_items').select('id,po_id,ref_code,sub_code,description,cost_code_id,unit,qty,unit_rate,amount_exc_vat,amount_inc_vat,addendum_no').eq('po_id', form.id).order('created_at')
        .then(({data}) => setPoLines(prev => ({...prev, [form.id]: data||[]})))
    }
  }

  const handleDelete = async (poId) => {
    if(!window.confirm('Delete this PO and all its line items?')) return
    await supabase.from('quotations').update({ po_id: null, status: 'submitted', is_recommended: false }).eq('po_id', poId)
    await supabase.from('po_line_items').delete().eq('po_id',poId)
    const { error } = await supabase.from('purchase_orders').delete().eq('id',poId)
    if(error) showToast('Delete failed: '+error.message,'error')
    else { showToast('PO deleted'); loadPOs(selectedProject.id) }
  }

  // Real authorization source -- can() mirrors has_permission() exactly,
  // same hook pattern as IPC.jsx.
  const { can } = useProjectAuth(selectedProject?.id)
  // Real authenticated user's display name for audit fields
  // (cc_approved_by/dir_approved_by/created_by/logAudit), replacing the
  // removed temporary Role Selector's ROLE_LABELS[tempRole] placeholder.
  const { profile } = useAuth()
  const currentUserName = profile?.full_name || profile?.email || 'Unknown User'

  // APPROVAL_LIMITS / getRequiredApprover now imported from
  // lib/approvalLimits.js (single shared source, per the approved
  // decision) — the local definitions previously here were an exact
  // duplicate of the Equipment path's REP_APPROVAL_LIMITS above.
  const ROLE_LABELS = { site_engineer:'Site Engineer', cost_control:'Cost Control', procurement:'Procurement', director:'Director' }

  const handlePOApproval = async (po, action, reason = '', { skipReload = false, skipToast = false } = {}) => {
    const stage = po.approval_stage || 'draft'
    const amount = parseFloat(po.total_exc_vat) || 0
    const requiredApprover = getRequiredApprover(amount)
    let update = {}

    if (action === 'submit') {
      update = { approval_stage: 'cost_control' }
    } else if (action === 'approve') {
      if (stage === 'cost_control') {
        if (requiredApprover === 'cost_control') {
          // ≤ 1M → approved directly
          update = { approval_stage: 'approved', status: 'approved', cc_approved_by: currentUserName, cc_approved_at: new Date().toISOString(), pending_addendum_no: null }
        } else {
          // > 1M → goes to director
          update = { approval_stage: 'director', cc_approved_by: currentUserName, cc_approved_at: new Date().toISOString() }
        }
      } else if (stage === 'director') {
        update = { approval_stage: 'approved', status: 'approved', dir_approved_by: currentUserName, dir_approved_at: new Date().toISOString(), pending_addendum_no: null }
      }
    } else if (action === 'reject') {
      update = { approval_stage: 'rejected', status: 'rejected', rejection_reason: reason }
    } else if (action === 'resubmit') {
      update = { approval_stage: 'draft', status: 'draft', rejection_reason: null }
    } else if (action === 'revoke') {
      update = { approval_stage: 'draft', status: 'draft', cc_approved_by: null, cc_approved_at: null, dir_approved_by: null, dir_approved_at: null }
    }

    if (!Object.keys(update).length) return
    const { error } = await supabase.from('purchase_orders').update(update).eq('id', po.id)
    if (error) { showToast('فشل: ' + error.message, 'error'); return }
    // Addendum PO fully approved: this IS the moment a Contract Addendum
    // is created — not before. original_po_id marks this PO as an
    // Addendum (set at creation time by openAddendumPO()); reaching
    // 'approved' here means it has been through the SAME approval
    // workflow as any normal PO (submit → cost_control → [approved |
    // director]). Per the approved workflow, the permanent
    // subcontract_addendums record — and the contract_value update — is
    // written HERE, once, not pre-recorded at creation time (unlike the
    // prior session's approach, which this replaces entirely).
    //
    // valueChange = this Addendum PO's own full value — it is a NEW,
    // separate commercial transaction added on top of the existing
    // contract (not a delta against the original PO's value, which
    // remains its own unrelated, already-settled transaction).
    if (update.approval_stage === 'approved' && po.original_po_id) {
      const { data: contract } = await supabase.from('subcontracts')
        .select('id, contract_value').eq('po_id', po.original_po_id).maybeSingle()
      if (contract) {
        const valueChange = parseFloat(po.total_exc_vat) || 0
        const newContractValue = (parseFloat(contract.contract_value) || 0) + valueChange
        const { data: lastAddendum } = await supabase.from('subcontract_addendums')
          .select('addendum_no').eq('contract_id', contract.id).eq('contract_type', 'material')
          .order('addendum_no', { ascending: false }).limit(1).maybeSingle()
        const nextAddendumNo = (lastAddendum?.addendum_no || 0) + 1
        await supabase.from('subcontract_addendums').insert({
          contract_id: contract.id, contract_type: 'material', addendum_no: nextAddendumNo,
          description: `Addendum PO ${po.po_number} (fully approved)`,
          value_change: valueChange, new_contract_value: newContractValue,
          addendum_date: new Date().toISOString().slice(0, 10),
          created_by: currentUserName, addendum_po_id: po.id,
        })
        await supabase.from('subcontracts').update({ contract_value: newContractValue }).eq('id', contract.id)
      }
    }
    await logAudit(po.id, 'purchase_order', action, currentUserName, reason, po.approval_stage, update.approval_stage, selectedProject.id)
    if (!skipToast) showToast('تم التحديث')
    if (!skipReload) loadPOs(selectedProject.id)
  }

  function poToggleSelect(id) {
    setPoSelected(prev => prev.includes(id) ? prev.filter(x=>x!==id) : [...prev, id])
  }
  function poToggleSelectAll() {
    const ids = pipelineFiltered.map(p=>p.id)
    setPoSelected(prev => prev.length === ids.length ? [] : ids)
  }

  async function poBulkAction(actionType) {
    if (poBulkBusy || !poSelected.length) return
    setPoBulkBusy(true)

    if (actionType === 'delete') {
      if (!window.confirm(`حذف ${poSelected.length} PO محدد؟`)) { setPoBulkBusy(false); return }
      await supabase.from('po_line_items').delete().in('po_id', poSelected)
      await supabase.from('purchase_orders').delete().in('id', poSelected)
      showToast(`تم حذف ${poSelected.length} PO`)
      setPoSelected([])
      await loadPOs(selectedProject.id)
      setPoBulkBusy(false)
      return
    }

    let reason = ''
    if (actionType === 'reject') {
      reason = prompt('سبب الرفض (لكل الـPOs المحددة) — مطلوب، لن يتم الرفض بدون سبب:') || ''
      if (reason === '') { setPoBulkBusy(false); showToast('تم الإلغاء — لم يتم إدخال سبب الرفض', 'error'); return }
    }
    if (actionType === 'revoke') {
      if (!window.confirm(`Revoke approval for ${poSelected.length} PO(s) and return them to Draft?`)) { setPoBulkBusy(false); return }
    }

    let acted = 0, skipped = 0
    for (const id of poSelected) {
      const po = pos.find(p=>p.id===id)
      if (!po) continue
      const { btns } = getPOApprovalButtons(po)
      let validBtn
      if (actionType === 'approve') validBtn = btns.find(b => b.action === 'submit' || b.action === 'approve' || b.action === 'resubmit')
      else if (actionType === 'reject') validBtn = btns.find(b => b.action === 'reject')
      else if (actionType === 'revoke') validBtn = btns.find(b => b.action === 'revoke')
      if (validBtn) { await handlePOApproval(po, validBtn.action, reason, { skipReload: true, skipToast: true }); acted++ }
      else skipped++
    }
    setPoSelected([])
    await loadPOs(selectedProject.id)
    setPoBulkBusy(false)
    showToast(skipped ? `تم تنفيذ ${acted} — تم تجاهل ${skipped} (مش صلاحيتك في مرحلتهم الحالية)` : `تم تنفيذ ${acted} بنجاح`)
  }

  const getPOApprovalButtons = (po) => {
    const stage = po.approval_stage || 'draft'
    const amount = parseFloat(po.total_exc_vat) || 0
    const requiredApprover = getRequiredApprover(amount)
    const btns = []
    if (stage === 'draft' && can('PO.Submit')) {
      btns.push({ label: 'Submit for Approval', action: 'submit', color: 'bg-blue-600 text-white' })
    }
    if (stage === 'cost_control' && can('PO.ApproveCostControl')) {
      btns.push({ label: '✓ Approve', action: 'approve', color: 'bg-green-600 text-white' })
      btns.push({ label: '✗ Reject', action: 'reject', color: 'bg-red-100 text-red-700' })
    }
    if (stage === 'director' && can('PO.ApproveDirector')) {
      btns.push({ label: '✓ Final Approve', action: 'approve', color: 'bg-green-600 text-white' })
      btns.push({ label: '✗ Reject', action: 'reject', color: 'bg-red-100 text-red-700' })
    }
    if (stage === 'rejected' && can('PO.Submit')) {
      btns.push({ label: '↻ Resubmit', action: 'resubmit', color: 'bg-blue-600 text-white' })
    }
    if (stage === 'approved') {
      const finalApprover = po.dir_approved_by ? 'director' : 'cost_control'
      if ((finalApprover === 'director' && can('PO.ApproveDirector')) || (finalApprover === 'cost_control' && can('PO.ApproveCostControl'))) {
        btns.push({ label: '↺ Revoke Approval', action: 'revoke', color: 'bg-amber-100 text-amber-800' })
      }
    }
    return { btns, requiredApprover }
  }

  const [uploadPreview, setUploadPreview] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [dragOver, setDragOver] = useState(false)

  const downloadTemplate = () => {
    const wb = XLSX.utils.book_new()
    // Match Oceanarium format exactly
    const rows = [
      // Row 1,2: empty spacer rows (to match original format)
      ['','','','','','','','','','','','','','','',''],
      ['','','','','','','','','','','','','','','',''],
      // Row 3: Headers
      ['Sub.code','Ref.Code','Ser.','Cost Code','Cost Code Desc.','Cost Type','PO Type','Logistics / Oceanarium','PO Number (per Item)','PO Date','Supplier Name','Description','QTY','Unit','Unit Price','Total Exc. VAT','Total Inc. VAT'],
      // Sample data rows
      ['Civil.po','REF-001',1,'31.001','Concrete Works','Direct','standard','Ocn','2500001','2025-01-15','ABC Concrete Co.','Supply of Ready Mix Concrete C30',100,'M3',250,25000,28750],
      ['Civil.po','REF-002',2,'31.001','Concrete Works','Direct','standard','Ocn','2500001','2025-01-15','ABC Concrete Co.','Supply of Formwork',500,'M2',45,22500,25875],
      ['Eqp.po','REF-003',1,'50.001','Equipment Costs','Indirect','complex','Ocn','2500002','2025-02-01','XYZ Equipment Ltd.','Excavator 320D Monthly Rent',3,'MONTH',25000,75000,86250],
    ]
    const ws = XLSX.utils.aoa_to_sheet(rows)
    XLSX.utils.book_append_sheet(wb, ws, 'POs')
    XLSX.writeFile(wb, 'purchase_orders_template.xlsx')
  }

  const parsePoExcel = (file, bulkVatPct = 15) => new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'array' })
        const scMap = {}
        subCodesList.forEach(s => { scMap[s.code.trim().toLowerCase()] = s })
        const validPoTypes = ['standard','complex']
        let lineLevelMismatch = 0
        let lineCostCodeConflict = 0

        // Sheet 1: Headers
        const headersSheet = wb.Sheets[wb.SheetNames[0]]
        const headerRows = XLSX.utils.sheet_to_json(headersSheet, { defval: '' })

        // Sheet 2: Lines
        const linesSheet = wb.Sheets[wb.SheetNames[1]]
        const lineRows = linesSheet ? XLSX.utils.sheet_to_json(linesSheet, { defval: '' }) : []

        const pos = headerRows.map(row => {
          const l = {}
          Object.keys(row).forEach(k => { l[k.toLowerCase().replace(/[\s\/\.\-]+/g,'_')] = row[k] })
          const poType = String(l.po_type||'standard').trim().toLowerCase()
          const poLines = lineRows.filter(lr => String(lr.po_number||'').trim() === String(l.po_number||'').trim())
          const parsedLines = poLines.map(lr => {
            const ll = {}
            Object.keys(lr).forEach(k => { ll[k.toLowerCase().replace(/[\s\/\.\-]+/g,'_')] = lr[k] })
            const qty = parseFloat(ll.qty||0)||0
            const rate = parseFloat(ll.unit_rate||ll.rate||0)||0
            const rawSubCode = String(ll.sub_code||'').trim()
            const rawCostCode = String(ll.cost_code||'').trim()
            const sub = rawSubCode ? scMap[rawSubCode.toLowerCase()] : null
            if (rawSubCode && !sub) {
              const anyMatch = costCodes.find(c=>c.code.trim().toLowerCase()===rawSubCode.toLowerCase())
              if (anyMatch) lineLevelMismatch++
              return null // Sub Code doesn't exist at all in the tree — reject this line
            }
            if (rawCostCode && sub && sub.costCode.trim().toLowerCase() !== rawCostCode.toLowerCase()) {
              // Cost Code written doesn't match this Sub Code's actual parent in the tree — reject
              lineCostCodeConflict++
              return null
            }
            return {
              ref_code: String(ll.ref_code||'').trim()||null,
              sub_code: sub?.code || rawSubCode || null,
              description: String(ll.description||ll.desc||'').trim(),
              unit: String(ll.unit||'').trim()||null,
              qty, unit_rate: rate,
              cost_code_id: sub?.costCodeId || null,
            }
          }).filter(ll => ll && ll.description)

          const totalExcVat = parsedLines.reduce((s,ll)=>s+(ll.qty*ll.unit_rate),0)
          const vatPct = parseFloat(l.vat_pct ?? bulkVatPct) || bulkVatPct
          const totalIncVat = totalExcVat * (1 + vatPct/100)

          const rawHeaderSubCode = String(l.sub_code||'').trim()
          const headerSub = rawHeaderSubCode ? scMap[rawHeaderSubCode.toLowerCase()] : null
          if (rawHeaderSubCode && !headerSub) {
            const anyMatch = costCodes.find(c=>c.code.trim().toLowerCase()===rawHeaderSubCode.toLowerCase())
            if (anyMatch) lineLevelMismatch++
          }

          return {
            po_number: String(l.po_number||'').trim(),
            vendor: String(l.vendor||'').trim(),
            po_type: validPoTypes.includes(poType) ? poType : 'standard',
            cost_type: ['direct','indirect'].includes(String(l.cost_type||'direct').toLowerCase()) ? String(l.cost_type).toLowerCase() : 'direct',
            sub_code: headerSub?.code || rawHeaderSubCode || null,
            cost_code_id: headerSub?.costCodeId || null,
            contract_ref: String(l.contract_ref||'').trim()||null,
            issue_date: l.issue_date||null,
            status: ['draft','approved','closed','cancelled'].includes(String(l.status||'draft').toLowerCase()) ? String(l.status).toLowerCase() : 'draft',
            vat_pct: vatPct,
            notes: String(l.notes||'').trim()||null,
            total_exc_vat: totalExcVat,
            total_inc_vat: totalIncVat,
            lines: parsedLines,
          }
        }).filter(p => p.po_number && p.vendor)
        if (lineLevelMismatch > 0 || lineCostCodeConflict > 0) parsePoExcel._lastMismatchCount = lineLevelMismatch + lineCostCodeConflict
        resolve(pos)
      } catch(err) { reject(err) }
    }
    reader.onerror = reject
    reader.readAsArrayBuffer(file)
  })


  // ── Custom Parser for Oceanarium PO Format ─────────────────
  // Format: Single sheet, headers on Row 3, grouped by PO Number
  // Columns: Sub.code, Ref.Code, Ser., Cost Code, Cost Code Desc, Cost Type,
  //          Project, PO Number, PO Date, Supplier Name, Description,
  //          QTY, Unit, Unit Price, Total Exc. VAT, Total Inc. VAT
  const parseOceanariumExcel = (file, bulkVatPct = 15) => new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'array', cellDates: true })
        const ws = wb.Sheets[wb.SheetNames[0]]
        const scMap = {}
        subCodesList.forEach(s => { scMap[s.code.trim().toLowerCase()] = s })
        let oceanMismatch = 0
        let oceanCostCodeConflict = 0

        // Read all rows as array (raw)
        const allRows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null })

        // Find header row (contains 'PO Number' or 'Supplier Name')
        let headerRowIdx = -1
        for (let i = 0; i < Math.min(10, allRows.length); i++) {
          const row = allRows[i]
          if (row && row.some(c => String(c||'').toLowerCase().includes('supplier') || String(c||'').toLowerCase().includes('po number'))) {
            headerRowIdx = i
            break
          }
        }
        if (headerRowIdx === -1) { reject(new Error('Cannot find header row')); return }

        const headers = allRows[headerRowIdx].map(h => String(h||'').trim().toLowerCase()
          .replace('po number (per item)', 'po_number')
          .replace('po date', 'po_date')
          .replace('supplier name', 'vendor')
          .replace('cost code/resource code', 'cost_code')
          .replace('cost code desc.', 'cost_code_desc')
          .replace('cost type', 'cost_type')
          .replace('total exc. vat', 'total_exc_vat')
          .replace('total inc. vat', 'total_inc_vat')
          .replace('unit price', 'unit_rate')
          .replace('ref.code', 'ref_code')
          .replace('sub.code', 'sub_code')
          .replace('ser.', 'ser')
          .replace('qty', 'qty')
          .replace('unit', 'unit')
          .replace('description', 'description')
          .replace('logistics / oceanarium', 'logistics_type')
          .replace('logistics/oceanarium', 'logistics_type')
          .replace(/[\s\/\.\-]+/g, '_')
        )

        // Parse data rows
        const dataRows = allRows.slice(headerRowIdx + 1)
          .filter(row => row && row.some(c => c !== null && c !== ''))
          .map(row => {
            const obj = {}
            headers.forEach((h, i) => { if(h) obj[h] = row[i] })
            return obj
          })
          .map(row => ({ ...row, po_number: String(row.po_number||'').trim().replace(/\.0$/, '') }))
          .filter(row => row.po_number && row.vendor && row.description)

        // Group by PO Number
        const poMap = new Map()
        dataRows.forEach(row => {
          const poNum = String(row.po_number||'').trim().replace(/\.0$/, '')
          if (!poMap.has(poNum)) {
            // Determine po_type from cost_type
            const ct = String(row.cost_type||'').trim().toLowerCase()
            const rawType = String(row.po_type||row.type||'').trim().toLowerCase()
            const poType = rawType === 'complex' ? 'complex' : rawType === 'equipment' ? 'equipment' : rawType === 'service' ? 'service' : 'standard'
            // Parse date
            let issueDate = null
            if (row.po_date instanceof Date) {
              issueDate = row.po_date.toISOString().split('T')[0]
            } else if (row.po_date) {
              const raw = row.po_date
              // Excel serial number → JS date
              if (typeof raw === 'number') {
                const d = new Date(Math.round((raw - 25569) * 86400 * 1000))
                issueDate = d.toISOString().split('T')[0]
              } else {
                const s = String(raw).trim()
                // Already ISO format YYYY-MM-DD
                if (/^\d{4}-\d{2}-\d{2}/.test(s)) issueDate = s.substring(0,10)
                // DD/MM/YYYY
                else if (/^\d{2}\/\d{2}\/\d{4}/.test(s)) {
                  const [d,m,y] = s.split('/')
                  issueDate = `${y}-${m.padStart(2,'0')}-${d.padStart(2,'0')}`
                }
              }
            }
              const rawLogistics = String(row.logistics_type||'').trim().toLowerCase()
              const logisticsType = rawLogistics === 'log' ? 'logistics'
                                  : rawLogistics === 'ocn' ? 'oceanarium'
                                  : rawLogistics === 'eqp' ? 'eqp'
                                  : rawLogistics || 'oceanarium'
              const rawHeaderSub = row.sub_code ? String(row.sub_code).trim() : ''
              const headerSub = rawHeaderSub ? scMap[rawHeaderSub.toLowerCase()] : null
              if (rawHeaderSub && !headerSub) {
                const anyMatch = costCodes.find(c=>c.code.trim().toLowerCase()===rawHeaderSub.toLowerCase())
                if (anyMatch) oceanMismatch++
              }
            poMap.set(poNum, {
              po_number: poNum,
              vendor: String(row.vendor||'').trim(),
              po_type: poType,
              cost_type: ct === 'indirect' ? 'indirect' : 'direct',
              issue_date: issueDate,
              // Fixed: was hardcoded 'approved', bypassing the entire
              // approval workflow (Submit -> Cost Control -> Director)
              // for every Bulk Upload PO regardless of its actual
              // approval_stage (which defaults to 'draft' at the
              // database level, since this object never sets it).
              // Newly imported POs must start identical to a manually
              // created PO: draft/draft, requiring Submit like any
              // other PO.
              status: 'draft',
              vat_pct: bulkVatPct,
              logistics_type: logisticsType,
              sub_code: headerSub?.code || rawHeaderSub || null,
              cost_code_id: headerSub?.costCodeId || null,
              notes: null,
              total_exc_vat: 0,
              total_inc_vat: 0,
              lines: []
            })
          }
          const po = poMap.get(poNum)
          const qty = parseFloat(row.qty||0)||0
          const rate = parseFloat(row.unit_rate||0)||0
          const excVat = parseFloat(row.total_exc_vat||0)||(qty*rate)
          const incVat = parseFloat(row.total_inc_vat||0)||(excVat*1.15)
          const rawLineSub = row.sub_code ? String(row.sub_code).trim() : ''
          const rawLineCostCode = row.cost_code ? String(row.cost_code).trim() : ''
          const lineSub = rawLineSub ? scMap[rawLineSub.toLowerCase()] : null
          if (rawLineSub && !lineSub) {
            const anyMatch = costCodes.find(c=>c.code.trim().toLowerCase()===rawLineSub.toLowerCase())
            if (anyMatch) oceanMismatch++
            return // Sub Code doesn't exist at all in the tree — skip this line
          }
          if (rawLineCostCode && lineSub && lineSub.costCode.trim().toLowerCase() !== rawLineCostCode.toLowerCase()) {
            oceanCostCodeConflict++
            return // Cost Code written doesn't match this Sub Code's actual parent — skip this line
          }
          po.lines.push({
            ref_code: row.ref_code ? String(row.ref_code).trim() : null,
            sub_code: lineSub?.code || rawLineSub || null,
            description: String(row.description||'').trim(),
            unit: row.unit ? String(row.unit).trim() : null,
            qty, unit_rate: rate,
            cost_code_id: lineSub?.costCodeId || null,
          })
          po.total_exc_vat += excVat
          po.total_inc_vat += incVat
        })

        const pos = Array.from(poMap.values()).filter(p => p.po_number && p.vendor && p.lines.length > 0)
        if (oceanMismatch > 0 || oceanCostCodeConflict > 0) parseOceanariumExcel._lastMismatchCount = oceanMismatch + oceanCostCodeConflict
        resolve(pos)
      } catch(err) { reject(err) }
    }
    reader.onerror = reject
    reader.readAsArrayBuffer(file)
  })

  const handleFileDrop = async (file) => {
    if(!file || !selectedProject) return
    const ext = file.name.split('.').pop().toLowerCase()
    if(!['xlsx','xls'].includes(ext)) return showToast('Excel files only (.xlsx)','error')
    try {
      // Auto-detect: Oceanarium format has single sheet with 'Supplier Name' in first 5 rows
      let rows = []
      const ab = await file.arrayBuffer()
      const wb2 = XLSX.read(ab, { type: 'array' })
      const firstSheet = XLSX.utils.sheet_to_json(wb2.Sheets[wb2.SheetNames[0]], { header: 1, defval: null })
      const isOcean = wb2.SheetNames.length === 1 &&
        firstSheet.slice(0, 6).some(row => row && row.some(c => String(c||'').toLowerCase().includes('supplier name')))
      // Resolved once for the whole file: every row in a single upload
      // belongs to the same selectedProject.
      let bulkVatPct = 15
      if (selectedProject?.id) {
        const { data: vatRow } = await supabase.rpc('get_project_vat_config', { p_project_id: selectedProject.id })
        const r = vatRow?.[0]
        if (r?.vat_treatment === 'standard' && r.vat_rate != null) bulkVatPct = r.vat_rate
        else if (r?.vat_treatment === 'zero_rated') bulkVatPct = 0
      }
      if (isOcean) {
        rows = await parseOceanariumExcel(file, bulkVatPct)
      } else {
        rows = await parsePoExcel(file, bulkVatPct)
      }
      if(!rows.length) return showToast('No valid POs found','error')
      setUploadPreview(rows)
      const mismatchCount = (parsePoExcel._lastMismatchCount || 0) + (parseOceanariumExcel._lastMismatchCount || 0)
      if (mismatchCount > 0) {
        showToast(`⚠ تم تجاهل ${mismatchCount} صف — الكود مش Sub Code فعلي، أو Cost Code المكتوب مش مطابق للـ Sub Code في الشجرة`, 'error')
        parsePoExcel._lastMismatchCount = 0
        parseOceanariumExcel._lastMismatchCount = 0
      }
    } catch(e) { showToast('Parse error: '+e.message,'error') }
  }

  const handleBulkUpload = async () => {
    if(!uploadPreview?.length) return
    if (!can('PO.Create')) {
      showToast('Bulk upload is only available to Procurement.', 'error')
      return
    }
    const poNumbers = uploadPreview.map(po => po.po_number).filter(Boolean)
    let existing = []
    if (poNumbers.length) {
      const { data } = await supabase
        .from('purchase_orders')
        .select('id, po_number, approval_stage')
        .eq('project_id', selectedProject.id)
        .in('po_number', poNumbers)
      existing = data || []
      const blocked = existing.filter(e => !['draft','rejected'].includes(e.approval_stage || 'draft'))
      if (blocked.length) {
        showToast(`Upload blocked — these PO numbers are already submitted/approved and can't be re-uploaded: ${blocked.map(b=>b.po_number).join(', ')}`, 'error')
        return
      }
    }
    setUploading(true)
    let success = 0, failed = 0
    const errors = []
    const existingMap = {}
    ;(existing || []).forEach(e => { existingMap[e.po_number] = e })
    for(const po of uploadPreview) {
      const { lines, ...poData } = po
      const payload = { ...poData, project_id: selectedProject.id, logistics_type: poData.logistics_type||'oceanarium', delivered_value:0, issued_value:0, paid_value:0, poc:0 }
      const existingPO = existingMap[po.po_number]
      let poId

      if (existingPO) {
        payload.status = 'draft'; payload.approval_stage = 'draft'
        const { error } = await supabase.from('purchase_orders').update(payload).eq('id', existingPO.id)
        if (error) {
          console.error('PO update error:', error.message, JSON.stringify(payload).substring(0,300))
          errors.push(`${po.po_number}: ${error.message}`)
          failed++
          continue
        }
        poId = existingPO.id
        await supabase.from('po_line_items').delete().eq('po_id', poId)
      } else {
        const { data, error } = await supabase.from('purchase_orders').insert([payload]).select()
        if(error) {
          console.error('PO insert error:', error.message, JSON.stringify(payload).substring(0,300))
          errors.push(`${po.po_number}: ${error.message}`)
          failed++
          continue
        }
        poId = data[0].id
      }

      if(lines?.length) {
        const lineItems = lines.map(l => ({
          po_id: poId,
          ref_code: l.ref_code||null,
          sub_code: l.sub_code||null,
          description: l.description||'',
          unit: l.unit||null,
          qty: l.qty||0,
          unit_rate: l.unit_rate||0,
          cost_code_id: l.cost_code_id||null,
          amount_inc_vat: (l.qty||0) * (l.unit_rate||0) * (1 + (parseFloat(po.vat_pct)||15) / 100)
        }))
        const { error: lineErr } = await supabase.from('po_line_items').insert(lineItems)
        if(lineErr) console.error('Lines insert error:', lineErr.message)
      }
      success++
    }
    setUploading(false)
    setUploadPreview(null)
    if(errors.length) {
      showToast(`${success} imported, ${failed} failed: ${errors[0]}`, 'error')
    } else {
      showToast(`${success} POs imported successfully`, 'success')
    }
    loadPOs(selectedProject.id)
  }

  return (
    <div style={{minHeight:'100vh',background:'#F3F4F6',fontFamily:'inherit'}}>
      {/* ── Page Header ── */}
      <div style={{background:'#fff',borderBottom:'1px solid #E5E7EB',padding:'18px 24px 14px',display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:16}}>
        <div>
          <h1 style={{fontSize:20,fontWeight:800,color:'#0F1017',letterSpacing:'-.02em'}}>Purchase Orders</h1>
          <p style={{fontSize:11.5,color:'#6B7280',marginTop:3}}>
            {selectedProject ? `${selectedProject.name} · Material · Equipment · Subcontract` : 'Material · Subcontract · Equipment'}
          </p>
        </div>
        {selectedProject && (
          <div style={{display:'flex',gap:8,alignItems:'center'}}>
            {activeTab === 'purchase_orders' && (
              <>
                <button onClick={downloadTemplate}
                  style={{display:'inline-flex',alignItems:'center',gap:6,padding:'5px 12px',borderRadius:6,fontSize:12,fontWeight:600,border:'1px solid #E5E7EB',background:'#fff',color:'#6B7280',cursor:'pointer',fontFamily:'inherit'}}>
                  <DownloadIcon/> Template
                </button>
                {can('PO.Create') && (
                  <label style={{display:'inline-flex',alignItems:'center',gap:6,padding:'5px 12px',borderRadius:6,fontSize:12,fontWeight:600,border:'1px solid #E5E7EB',background:'#fff',color:'#6B7280',cursor:'pointer'}}>
                    <UploadIcon/> Bulk Upload
                    <input type="file" accept=".xlsx,.xls" style={{display:'none'}} onChange={e=>handleFileDrop(e.target.files[0])}/>
                  </label>
                )}
              </>
            )}
            {activeTab === 'purchase_orders' && can('PO.Create') && (
              <button onClick={()=>{ if(window.confirm('⚠️ POs should be created from RFQ Award. Create manually anyway?')) setModal('new') }}
                style={{display:'inline-flex',alignItems:'center',gap:6,padding:'5px 14px',borderRadius:6,fontSize:12,fontWeight:600,border:'1px solid #D97706',background:'#FFFBEB',color:'#92400E',cursor:'pointer',fontFamily:'inherit'}}>
                <PlusIcon/> New PO (Manual)
              </button>
            )}
          </div>
        )}
      </div>

      <div style={{padding:'20px 24px',display:'flex',flexDirection:'column',gap:16}}>
        {/* Tabs */}
        {selectedProject && (
          <div style={{display:'flex',gap:0,background:'#fff',border:'1px solid #E5E7EB',borderRadius:10,overflow:'hidden'}}>
            {[
              { key:'purchase_orders', label:'Purchase Orders', icon:<path d="M2 2h1.5l1.8 7h7l1.7-5H5.3M7 13.5a1 1 0 100 0M11 13.5a1 1 0 100 0"/> },
              { key:'rental_equipment', label:'Rental Equipment PO', icon:<path d="M2 12l2-5h3l1-2h3l2 4h1.5M5 12.5a1.5 1.5 0 100 0M11 12.5a1.5 1.5 0 100 0"/> },
              { key:'analysis_search', label:'Search', icon:<path d="M6.5 2a4.5 4.5 0 100 9 4.5 4.5 0 000-9zM9.5 9.5L14 14"/> },
            ].map(t => (
              <button key={t.key} onClick={() => setActiveTab(t.key)}
                style={{flex:1,padding:'11px 20px',fontSize:13,fontWeight:700,border:'none',borderBottom: activeTab===t.key ? '2px solid #1A7A4A' : '2px solid transparent',background: activeTab===t.key ? 'rgba(26,122,74,.04)' : '#fff',color: activeTab===t.key ? '#1A7A4A' : '#6B7280',cursor:'pointer',fontFamily:'inherit',transition:'all .12s',display:'flex',alignItems:'center',justifyContent:'center',gap:7}}>
                <svg viewBox="0 0 16 16" style={{width:14,height:14,flexShrink:0}} fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">{t.icon}</svg>
                {t.label}
              </button>
            ))}
          </div>
        )}

        {/* Project Selector */}
        <div style={{background:'#fff',border:'1px solid #E5E7EB',borderRadius:10,padding:16}}>
          <label style={{display:'block',fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.08em',color:'#6B7280',marginBottom:10}}>Select Project</label>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:10}}>
            {projects.map(p=>(
              <button key={p.id} onClick={()=>handleProjectSelect(p)}
                style={{textAlign:'left',padding:'10px 14px',borderRadius:8,border:`1px solid ${selectedProject?.id===p.id?'#1A7A4A':'#E5E7EB'}`,background:selectedProject?.id===p.id?'rgba(26,122,74,.06)':'#fff',cursor:'pointer',fontFamily:'inherit',transition:'all .12s',outline:selectedProject?.id===p.id?'2px solid rgba(26,122,74,.2)':'none',outlineOffset:1}}>
                <p style={{fontWeight:700,color:'#0F1017',fontSize:13}}>{p.name}</p>
                <p style={{fontSize:11,color:'#9CA3AF',marginTop:2}}>{p.organizations?.name}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Upload Preview */}
        {activeTab === 'purchase_orders' && uploadPreview && (
          <div className="bg-[#EDE7DA] rounded-2xl border border-[#DDD9D0] overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 bg-amber-50 border-b border-[#E8E4DC]">
              <div>
                <p className="text-sm font-semibold text-amber-800">Preview — {uploadPreview.length} POs ready to import</p>
                <p className="text-xs text-amber-600 mt-0.5">Total lines: {uploadPreview.reduce((s,p)=>s+p.lines.length,0)} · Total value: {uploadPreview.reduce((s,p)=>s+(p.total_exc_vat||0),0).toLocaleString('en-US',{maximumFractionDigits:0})} (Exc.VAT)</p>
              </div>
              <div className="flex gap-2">
                <button onClick={()=>setUploadPreview(null)} className="px-3 py-1.5 text-xs border border-[#DDD9D0] rounded-lg text-[#6B655C] hover:bg-[#F5F1EA]">Cancel</button>
                <button onClick={handleBulkUpload} disabled={uploading}
                  className="px-4 py-1.5 text-xs font-medium text-white bg-[#1C1A18] rounded-lg hover:bg-[#2E2C29] disabled:opacity-50">
                  {uploading?'Importing...':'Import '+uploadPreview.length+' POs'}
                </button>
              </div>
            </div>
            <div className="overflow-auto max-h-48">
              <table className="w-full text-xs">
                <thead className="bg-[#F5F1EA] sticky top-0">
                  <tr>{['PO Number','Vendor','Type','Cost Type','Status','Lines','Total Exc.VAT'].map(h=><th key={h} className="text-left px-3 py-2 font-medium text-[#6B655C]">{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-[#E8E4DC]">
                  {uploadPreview.map((po,i)=>(
                    <tr key={i} className="hover:bg-[#F5F1EA]">
                      <td className="px-3 py-2 font-mono text-[#1A7A4A]">{po.po_number}</td>
                      <td className="px-3 py-2 text-[#2E2C29]">{po.vendor}</td>
                      <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full border text-xs ${poTypeColors[po.po_type]||''}`}>{po.po_type}</span></td>
                      <td className="px-3 py-2">{po.cost_type}</td>
                      <td className="px-3 py-2">{po.status}</td>
                      <td className="px-3 py-2 text-center">{po.lines.length}</td>
                      <td className="px-3 py-2 text-right font-medium text-[#1A7A4A]">{fmt(po.total_exc_vat)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {selectedProject && activeTab === 'purchase_orders' && (
          <>
            {/* Stats */}
            <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:12}}>
              <div style={{background:'#fff',border:'1px solid #E5E7EB',borderRadius:10,padding:'14px 18px'}}>
                <div style={{fontSize:24,fontWeight:800,color:'#0F1017'}}>{stats.total}</div>
                <div style={{fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.08em',color:'#6B7280',marginTop:4}}>Total POs</div>
              </div>
              <div style={{background:'#0F1017',borderRadius:10,padding:'14px 18px'}}>
                <div style={{fontSize:18,fontWeight:800,color:'#fff',fontVariantNumeric:'tabular-nums'}}>{fmtM(stats.totalExcVat)}</div>
                <div style={{fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.08em',color:'rgba(255,255,255,.4)',marginTop:4}}>Total Exc.VAT</div>
              </div>
              <div style={{background:'#fff',border:'1px solid #E5E7EB',borderRadius:10,padding:'14px 18px'}}>
                <div style={{fontSize:18,fontWeight:700,color:'#6B7280',fontVariantNumeric:'tabular-nums'}}>{fmtM(stats.totalIncVat)}</div>
                <div style={{fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.08em',color:'#9CA3AF',marginTop:4}}>Total Inc.VAT</div>
              </div>
              <div style={{background:'#1A7A4A',borderRadius:10,padding:'14px 18px'}}>
                <div style={{fontSize:18,fontWeight:800,color:'#fff',fontVariantNumeric:'tabular-nums'}}>{fmtM(stats.issued)}</div>
                <div style={{fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.08em',color:'rgba(255,255,255,.5)',marginTop:4}}>Issued (Actual)</div>
              </div>
              <div style={{background:'#D97706',borderRadius:10,padding:'14px 18px'}}>
                <div style={{fontSize:18,fontWeight:800,color:'#fff',fontVariantNumeric:'tabular-nums'}}>{fmtM(stats.paid)}</div>
                <div style={{fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.08em',color:'rgba(255,255,255,.6)',marginTop:4}}>Paid (Cash Out)</div>
              </div>
            </div>

            {/* Pipeline View */}
            <div style={{display:'flex',gap:0,background:'#fff',border:'1px solid #E5E7EB',borderRadius:10,overflow:'hidden'}}>
              {[
                {key:'all',     label:'All Items',  count:stageCounts.all,      sub:null},
                {key:'draft',   label:'Draft',      count:stageCounts.draft,    sub:stageCounts.draft > 0 ? `${stageCounts.draft} pending` : null, subColor:'#DC2626'},
                {key:'approved',label:'Approved',   count:stageCounts.approved, sub:null},
                {key:'closed',  label:'Closed',     count:stageCounts.closed,   sub:null},
                {key:'cancelled',label:'Cancelled', count:stageCounts.cancelled,sub:null},
              ].map((stage, i, arr) => (
                <div key={stage.key}
                  onClick={() => setPipelineStage(stage.key)}
                  style={{
                    flex:1, display:'flex', alignItems:'center', gap:10, padding:'10px 14px',
                    cursor:'pointer', position:'relative',
                    borderRight: i < arr.length-1 ? '1px solid #E5E7EB' : 'none',
                    background: pipelineStage===stage.key ? 'rgba(26,122,74,.06)' : '#fff',
                    borderBottom: pipelineStage===stage.key ? '2px solid #1A7A4A' : '2px solid transparent',
                    transition:'all .12s',
                  }}>
                  <div>
                    <div style={{fontSize:9.5,fontWeight:700,textTransform:'uppercase',letterSpacing:'.07em',color:'#6B7280'}}>{stage.label}</div>
                    <div style={{fontSize:22,fontWeight:800,color:pipelineStage===stage.key?'#1A7A4A':'#0F1017',lineHeight:1.1}}>{stage.count}</div>
                    {stage.sub && <div style={{fontSize:10,fontWeight:600,color:stage.subColor||'#D97706'}}>{stage.sub}</div>}
                  </div>
                  {i < arr.length-1 && (
                    <span style={{position:'absolute',right:-8,top:'50%',transform:'translateY(-50%)',color:'#D1D5DB',fontSize:13,zIndex:2}}>›</span>
                  )}
                </div>
              ))}
            </div>

            {/* Filters */}
            <div style={{display:'flex',alignItems:'center',gap:8,padding:'10px 0',flexWrap:'wrap'}}>
              <div style={{display:'flex',alignItems:'center',gap:7,padding:'5px 10px',background:'#fff',border:'1px solid #D1D5DB',borderRadius:6,flex:1,minWidth:200}}>
                <SearchIcon/>
                <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search PO number, vendor, sub code..."
                  style={{border:'none',background:'none',outline:'none',fontSize:12,color:'#0F1017',width:'100%'}}/>
              </div>
              <select value={typeFilter} onChange={e=>setTypeFilter(e.target.value)}
                style={{padding:'5px 10px',border:'1px solid #D1D5DB',borderRadius:6,fontSize:12,color:'#0F1017',background:'#fff',cursor:'pointer'}}>
                <option value="all">All Types</option>
                {[...new Set(pos.map(p=>p.po_type).filter(Boolean))].sort().map(t=>(
                  <option key={t} value={t}>{t.charAt(0).toUpperCase()+t.slice(1)}</option>
                ))}
              </select>
              <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)}
                style={{padding:'5px 10px',border:'1px solid #D1D5DB',borderRadius:6,fontSize:12,color:'#0F1017',background:'#fff',cursor:'pointer'}}>
                <option value="all">All Status</option>
                {['draft','approved','closed','cancelled'].map(s=><option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
              </select>
              <select value={analysisFilter} onChange={async e=>{
                const val = e.target.value
                setAnalysisFilter(val)
                if(val !== 'all' && selectedProject?.id) {
                  // One query: get all lines with this sub_code for this project
                  const { data } = await supabase.from('po_line_items')
                    .select('id,po_id,qty,unit_rate,amount_exc_vat,amount_inc_vat,description,ref_code,sub_code,cost_code_id,unit')
                    .eq('sub_code', val)
                  if(data) {
                    // Group by po_id and merge into cache
                    const grouped = {}
                    data.forEach(l => {
                      if(!grouped[l.po_id]) grouped[l.po_id] = []
                      grouped[l.po_id].push(l)
                    })
                    // Mark POs with no matching lines as empty array
                    pos.forEach(p => { if(!grouped[p.po_id] && !poLines[p.id]) grouped[p.id] = [] })
                    setPoLines(prev => ({...prev, ...grouped}))
                  }
                }
              }}
                style={{padding:'5px 10px',border:'1px solid #D1D5DB',borderRadius:6,fontSize:12,color:'#0F1017',background:'#fff',cursor:'pointer'}}>
                <option value="all">All Sub Codes</option>
                {subCodesList.map(sc=>(
                  <option key={sc.code} value={sc.code}>{sc.code}</option>
                ))}
              </select>
              <button onClick={async()=>{
                const ids = filtered.map(p=>p.id)
                const newExpanded = {}
                for(const id of ids) newExpanded[id] = true
                setExpandedRows(newExpanded)
                const missing = filtered.filter(p=>!poLines[p.id])
                for(const p of missing){
                  const {data} = await supabase.from('po_line_items').select('id,po_id,ref_code,sub_code,description,cost_code_id,unit,qty,unit_rate,amount_exc_vat,amount_inc_vat,addendum_no').eq('po_id',p.id).order('created_at')
                  setPoLines(prev=>({...prev,[p.id]:data||[]}))
                }
              }} style={{padding:'5px 10px',fontSize:11,fontWeight:600,border:'1px solid #E5E7EB',background:'#fff',color:'#6B7280',borderRadius:6,cursor:'pointer',whiteSpace:'nowrap'}}>
                ▼ Expand All
              </button>
              <StorageIndicator/>
              <button onClick={()=>setExpandedRows({})} style={{padding:'5px 10px',fontSize:11,fontWeight:600,border:'1px solid #E5E7EB',background:'#fff',color:'#6B7280',borderRadius:6,cursor:'pointer',whiteSpace:'nowrap'}}>
                ▶ Collapse
              </button>
              <span style={{fontSize:11,color:'#9CA3AF',marginLeft:'auto'}}>{filtered.length} POs</span>
              {poSelected.length > 0 && (
                <>
                  <select value={poBulkActionType} onChange={e=>setPoBulkActionType(e.target.value)}
                    style={{padding:'5px 10px',border:'1px solid #D1D5DB',borderRadius:6,fontSize:12,background:'#fff',cursor:'pointer'}}>
                    <option value="approve">{can('PO.Submit') ? 'Submit / Resubmit' : 'Approve'}</option>
                    {(can('PO.ApproveCostControl') || can('PO.ApproveDirector')) && <option value="reject">Reject</option>}
                    {can('PO.ApproveDirector') && <option value="revoke">Revoke</option>}
                    <option value="delete">Delete</option>
                  </select>
                  <button onClick={()=>poBulkAction(poBulkActionType)} disabled={poBulkBusy}
                    style={{padding:'5px 12px',fontSize:12,fontWeight:600,borderRadius:6,border:'none',cursor:'pointer',color:'#fff',opacity:poBulkBusy?0.6:1,
                      background: poBulkActionType==='delete' ? '#DC2626' : poBulkActionType==='reject' ? '#D97706' : poBulkActionType==='revoke' ? '#D97706' : '#1A7A4A'}}>
                    {poBulkBusy ? '⏳ جاري التنفيذ...' : `تنفيذ (${poSelected.length})`}
                  </button>
                </>
              )}
            </div>

            {/* PO Table */}
            <div ref={poTableCardRef} style={{background:'#fff',border:'1px solid #E5E7EB',borderRadius:10,position:'relative'}}>
              {loading ? (
                <div style={{display:'flex',alignItems:'center',justifyContent:'center',padding:'64px',gap:10,color:'#9CA3AF'}}>
                  <div style={{width:24,height:24,border:'3px solid #E5E7EB',borderTopColor:'#1A7A4A',borderRadius:'50%',animation:'spin 1s linear infinite'}}/>Loading...
                </div>
              ) : filtered.length === 0 ? (
                <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:64,color:'#9CA3AF'}}>
                  <p style={{fontSize:14}}>No POs yet</p>
                  <p style={{fontSize:12,marginTop:4}}>Create your first Purchase Order</p>
                </div>
              ) : (
                <>
                {/* Single dedicated vertical+horizontal scroll container
                    for the whole table (header + body together), same
                    proven pattern as IPC's Material/Equipment tables
                    (overflow:auto + bounded maxHeight). This replaces
                    two earlier attempts that both failed: (1) thead
                    position:sticky nested inside the body's own
                    overflow-x-auto wrapper -- broke because that
                    wrapper became its own nearest scrolling ancestor,
                    so sticky never resolved against the page; (2) a
                    second detached header table synced via
                    transform+clip-path -- clip-path combined with a
                    sticky-only <thead> (no <tbody> in that table)
                    produced an unstable computed height in some
                    browsers, clipping the header to invisible instead
                    of just not sticking. Housing thead+tbody in one
                    table inside one overflow:auto box (not the page)
                    is the only version of this that has proven
                    reliable, mirroring IPC exactly. */}
                <div ref={poTableScrollRef} onScroll={updatePoScrollState} style={{overflow:'auto', maxHeight:'70vh', borderRadius:10}}>
                  <table className="text-sm" style={{width:'100%',minWidth:1550}}>
                    <colgroup>{poColWidths.map((w,i)=><col key={i} style={{width:w}}/>)}</colgroup>
                    <thead className="bg-[#EDE7DA] border-b border-[#DDD9D0]" style={{position:'sticky',top:0,zIndex:5}}>
                      <tr>
                        <th className="px-4 py-3" style={{background:'#EDE7DA'}}>
                          <input type="checkbox" checked={poSelected.length===pipelineFiltered.length && pipelineFiltered.length>0} onChange={poToggleSelectAll} />
                        </th>
                        {['PO Number','Vendor','Type','Cost Type','Sub Code','Cost Code','Resource Code','Date','Stage','Status','Exc.VAT','Inc.VAT',''].map(h=>(
                        <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase whitespace-nowrap" style={{background:'#EDE7DA'}}>{h}</th>
                      ))}</tr>
                    </thead>
                    <tbody className="divide-y divide-[#E8E4DC]">
                      {pipelineFiltered.map(po=>(
                        <tr key={po.id} className="hover:bg-[#F5F1EA] cursor-pointer" onClick={()=>toggleExpand(po)}>
                          <td className="px-4 py-3" onClick={e=>e.stopPropagation()}>
                            <input type="checkbox" checked={poSelected.includes(po.id)} onChange={()=>poToggleSelect(po.id)} />
                          </td>
                          <td className="px-4 py-3 font-mono text-xs font-semibold text-[#1A7A4A] flex items-center gap-2">
                            <ChevronIcon open={!!expandedRows[po.id]}/>
                            {po.po_number}
                          </td>
                          <td className="px-4 py-3">
                            <div style={{fontSize:12.5,fontWeight:600,color:'#0F1017'}}>{po.vendor}</div>
                            {po.vat_pct && <div style={{fontSize:10,color:'#9CA3AF',marginTop:1}}>VAT {po.vat_pct}%</div>}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${poTypeColors[po.po_type]||''}`}>
                              {po.po_type}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs text-[#6B655C]">{po.cost_type}</td>
                          <td className="px-4 py-3 text-xs font-mono text-[#1A7A4A] font-semibold">{po.sub_code||'—'}</td>
                          <td className="px-4 py-3 text-xs font-mono text-[#6B655C]">{subCodesList.find(s=>s.code===po.sub_code)?.costCode || po.cost_codes?.code || '—'}</td>
                          <td className="px-4 py-3 text-xs font-mono text-purple-700">{subCodesList.find(s=>s.code===po.sub_code)?.resourceCode || '—'}</td>
                          <td className="px-4 py-3 text-xs text-[#6B655C]">{po.issue_date||'—'}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${stageColors[po.approval_stage||'draft']||''}`}>
                              {po.approval_stage||'draft'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span title={po.status==='rejected' && po.rejection_reason ? `سبب الرفض: ${po.rejection_reason}` : undefined}
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${statusColors[po.status]||''}`}>
                              {po.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-[#1A7A4A]">{fmt(po.total_exc_vat)}</td>
                          <td className="px-4 py-3 text-right text-[#6B655C] text-xs">{fmt(po.total_inc_vat)}</td>

                          <td className="px-4 py-3 text-right" onClick={e=>e.stopPropagation()}>
                            <div className="flex items-center justify-end gap-2">
                              {/* Approval buttons */}
                              {(() => {
                                const { btns } = getPOApprovalButtons(po)
                                return btns.map(btn => (
                                  <button key={btn.action} onClick={() => {
                                    if (btn.action === 'reject') {
                                      const reason = prompt('Rejection reason:')
                                      if (reason !== null) handlePOApproval(po, 'reject', reason)
                                    } else {
                                      handlePOApproval(po, btn.action)
                                    }
                                  }}
                                    style={{padding:'3px 8px',fontSize:10.5,fontWeight:600,borderRadius:4,cursor:'pointer',border:'none'}}
                                    className={btn.color}>
                                    {btn.label}
                                  </button>
                                ))
                              })()}
                              {po.po_type === 'complex' && (
                                convertedIds[po.id] ? (
                                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 border border-emerald-200 whitespace-nowrap">
                                    ✓ {convertedIds[po.id]}
                                  </span>
                                ) : (
                                  <button onClick={()=>openConvert(po)}
                                    className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#DDD9D0] text-[#1A7A4A] border border-[#DDD9D0] hover:bg-indigo-200 whitespace-nowrap">
                                    → Convert
                                  </button>
                                )
                              )}
                              <button onClick={()=>setDetailPanel(po)}
                                style={{padding:'3px 8px',fontSize:10.5,fontWeight:600,border:'1px solid #E5E7EB',background:'#fff',color:'#6B7280',borderRadius:4,cursor:'pointer'}}>
                                View
                              </button>
                              <button onClick={async ()=>{
                                setLinesModal(po)
                                setSelectedCardKey(null)
                                // Phase PO-3: Budget vs Commitment is now computed once at
                                // project level (see the useEffect above) and read directly
                                // by the display below via budgetQtys.direct/.indirect and
                                // committedQtys.direct/.indirect -- this click only needs to
                                // fetch this PO's own line items for display.
                                const {data} = await supabase.from('po_line_items').select('id,po_id,ref_code,sub_code,description,cost_code_id,unit,qty,unit_rate,amount_exc_vat,amount_inc_vat,addendum_no').eq('po_id',po.id).order('created_at')
                                setPoLines(prev=>({...prev,[po.id]:data||[]}))
                                // budgetRates (per-line unit rate reference, sourced from
                                // tender_direct_items) is unrelated to the Budget vs
                                // Commitment aggregation redesigned here -- left untouched,
                                // still keyed by the raw sub_code-derived code columns as before.
                                if ((data||[]).length && selectedProject?.id) {
                                  const {data: tRows} = await supabase.from('tender_direct_items')
                                    .select('budget_material_code,labour_code,budget_sc_code,budget_equipment_code,row_type,unit_rate')
                                    .eq('project_id',selectedProject.id)
                                    .in('row_type',['material','labour','subcontract','equipment'])
                                  const rateMap = {}
                                  ;(tRows||[]).forEach(r => {
                                    let sc = null
                                    if (r.row_type==='material')    sc = r.budget_material_code
                                    else if (r.row_type==='labour') sc = r.labour_code
                                    else if (r.row_type==='subcontract') sc = r.budget_sc_code
                                    else if (r.row_type==='equipment')   sc = r.budget_equipment_code
                                    if (!sc) return
                                    if (r.unit_rate) rateMap[sc] = parseFloat(r.unit_rate)
                                  })
                                  setBudgetRates(rateMap)
                                }
                              }}
                                style={{padding:'3px 8px',fontSize:10.5,fontWeight:600,border:'1px solid #E8F0FE',background:'#EFF6FF',color:'#2563EB',borderRadius:4,cursor:'pointer'}}>
                                Lines
                              </button>
                              <button onClick={()=>setModal(po)}
                                style={{padding:'3px 8px',fontSize:10.5,fontWeight:600,border:'1px solid #E5E7EB',background:'#fff',color:'#6B7280',borderRadius:4,cursor:'pointer'}}>
                                Edit
                              </button>
                              {(po.approval_stage || 'draft') === 'draft' && !po.original_po_id && contractPoIds.has(po.id) && can('PO.Create') && (
                                <button onClick={()=>openAddendumPO(po)}
                                  style={{padding:'3px 8px',fontSize:10.5,fontWeight:600,border:'1px solid #FDBA74',background:'#FFF7ED',color:'#C2410C',borderRadius:4,cursor:'pointer'}}>
                                  + Addendum
                                </button>
                              )}
                              <button onClick={()=>setPrintPO(po.id)}
                                style={{padding:'3px 8px',fontSize:10.5,fontWeight:600,border:'1px solid #DBEAFE',background:'#EFF6FF',color:'#1B3A6B',borderRadius:4,cursor:'pointer'}}>
                                🖨 Print
                              </button>
                              <DocButton entityId={po.id} entityType="purchase_order" projectId={po.project_id} count={docCounts[po.id] || 0}/>
                              <button onClick={()=>handleDelete(po.id)} className="text-red-400 hover:text-red-600"><TrashIcon/></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-[#F5F1EA] border-t-2 border-[#DDD9D0]">
                      <tr>
                        <td colSpan={8} className="px-4 py-3 text-xs font-bold text-[#6B7280] uppercase">Total</td>
                        <td className="px-4 py-3 text-right text-sm font-bold text-[#1A7A4A]">{fmt(filtered.reduce((s,p)=>s+(parseFloat(p.total_exc_vat)||0),0))}</td>
                        <td className="px-4 py-3 text-right text-sm font-bold text-[#6B655C]">{fmt(filtered.reduce((s,p)=>s+(parseFloat(p.total_inc_vat)||0),0))}</td>
                        <td className="px-4 py-3 text-right text-sm font-bold text-emerald-700">{fmt(filtered.reduce((s,p)=>s+(parseFloat(p.issued_value)||0),0))}</td>
                        <td className="px-4 py-3 text-right text-sm font-bold text-amber-700">{fmt(filtered.reduce((s,p)=>s+(parseFloat(p.paid_value)||0),0))}</td>
                        <td/>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                {poScrollState.hasOverflow && (
                  <div style={{
                    position:'fixed', bottom:0, left:poNavLeftRight.left, right:poNavLeftRight.right, zIndex:50,
                    display:'flex', alignItems:'center', gap:8,
                    padding:'6px 12px', background:'rgba(255,255,255,0.97)',
                    borderTop:'1px solid #E5E7EB', backdropFilter:'blur(2px)',
                  }}>
                    <button
                      onClick={()=>poScrollBy(-1)}
                      disabled={poScrollState.atStart}
                      aria-label="Scroll table left"
                      style={{
                        width:24, height:24, borderRadius:6, border:'1px solid #E5E7EB',
                        background:poScrollState.atStart?'#F9FAFB':'#fff', color:poScrollState.atStart?'#D1D5DB':'#6B7280',
                        cursor:poScrollState.atStart?'default':'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, flexShrink:0,
                      }}>‹</button>
                    <input
                      type="range" min={0} max={1000} step={1}
                      value={Math.round(poScrollState.ratio*1000)}
                      onChange={e=>poScrollToRatio(Number(e.target.value)/1000)}
                      aria-label="Scroll table horizontally"
                      style={{flex:1, height:4, accentColor:'#1B3A6B', cursor:'pointer'}}
                    />
                    <button
                      onClick={()=>poScrollBy(1)}
                      disabled={poScrollState.atEnd}
                      aria-label="Scroll table right"
                      style={{
                        width:24, height:24, borderRadius:6, border:'1px solid #E5E7EB',
                        background:poScrollState.atEnd?'#F9FAFB':'#fff', color:poScrollState.atEnd?'#D1D5DB':'#6B7280',
                        cursor:poScrollState.atEnd?'default':'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, flexShrink:0,
                      }}>›</button>
                  </div>
                )}
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* ── Detail Panel ── */}
      {detailPanel && (
        <div style={{position:'fixed',top:0,right:0,width:460,height:'100vh',background:'#fff',borderLeft:'1px solid #E5E7EB',zIndex:250,transform:'translateX(0)',boxShadow:'-4px 0 24px rgba(0,0,0,.08)',display:'flex',flexDirection:'column'}}>
          <div style={{padding:'16px 18px',borderBottom:'1px solid #E5E7EB',display:'flex',alignItems:'flex-start',justifyContent:'space-between',flexShrink:0}}>
            <div>
              <div style={{fontSize:14,fontWeight:700,color:'#0F1017'}}>{detailPanel.po_number}</div>
              <div style={{fontSize:11,color:'#6B7280',marginTop:2}}>{detailPanel.vendor} · {detailPanel.issue_date||'—'}</div>
            </div>
            <button onClick={()=>setDetailPanel(null)}
              style={{width:28,height:28,borderRadius:6,border:'1px solid #E5E7EB',background:'#fff',color:'#6B7280',display:'flex',alignItems:'center',justifyContent:'center',fontSize:16,cursor:'pointer'}}>
              ✕
            </button>
          </div>
          <div style={{flex:1,overflowY:'auto',padding:18}}>
            <div style={{marginBottom:16}}>
              <div style={{fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.08em',color:'#6B7280',marginBottom:8,paddingBottom:6,borderBottom:'1px solid #E5E7EB'}}>Purchase Order Details</div>
              {[
                ['PO Number', <span style={{fontFamily:'monospace',fontSize:12,fontWeight:700,color:'#1A7A4A'}}>{detailPanel.po_number}</span>],
                ['Vendor', detailPanel.vendor],
                ['Status', <span style={{padding:'2px 8px',borderRadius:20,fontSize:10,fontWeight:700,background:detailPanel.status==='approved'?'#DCFCE7':detailPanel.status==='draft'?'#F9FAFB':'#F3F4F6',color:detailPanel.status==='approved'?'#14532D':detailPanel.status==='draft'?'#374151':'#374151',border:`1px solid ${detailPanel.status==='approved'?'#BBF7D0':'#E5E7EB'}`}}>{detailPanel.status}</span>],
                ['Issue Date', detailPanel.issue_date||'—'],
                ['PO Type', detailPanel.po_type],
                ['Cost Type', detailPanel.cost_type],
                ['Sub Code', <span style={{fontFamily:'monospace',fontSize:11}}>{detailPanel.sub_code||'—'}</span>],
                ['Cost Code', <span style={{fontFamily:'monospace',fontSize:11,color:'#1A7A4A'}}>{subCodesList.find(s=>s.code===detailPanel.sub_code)?.costCode || '—'}</span>],
                ...(subCodesList.find(s=>s.code===detailPanel.sub_code)?.resourceCode ? [
                  ['Resource Code', <span style={{fontFamily:'monospace',fontSize:11,color:'#7C3AED'}}>{subCodesList.find(s=>s.code===detailPanel.sub_code)?.resourceCode}</span>],
                ] : []),
                ['VAT %', (detailPanel.vat_pct||15)+'%'],
                ['Total Exc. VAT', <span style={{fontSize:15,fontWeight:800,color:'#0F1017'}}>{fmt(detailPanel.total_exc_vat)}</span>],
                ['Total Inc. VAT', <span style={{fontSize:15,fontWeight:800,color:'#0F1017'}}>{fmt(detailPanel.total_inc_vat)}</span>],
              ].map(([label, value], i) => (
                <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'6px 0',fontSize:12.5}}>
                  <span style={{color:'#6B7280'}}>{label}</span>
                  <span style={{fontWeight:600,color:'#0F1017'}}>{value}</span>
                </div>
              ))}
            </div>
            {detailPanel.approval_stage === 'rejected' && detailPanel.rejection_reason && (
              <div style={{padding:'10px 12px',background:'#FEF2F2',border:'1px solid #FECACA',borderRadius:6,fontSize:12,color:'#991B1B',marginBottom:16}}>
                <span style={{fontWeight:600}}>سبب الرفض: </span>{detailPanel.rejection_reason}
              </div>
            )}
            {detailPanel.notes && (
              <div style={{padding:'10px 12px',background:'#FFFBEB',border:'1px solid #FDE68A',borderRadius:6,fontSize:12,color:'#92400E',marginBottom:16}}>
                <span style={{fontWeight:600}}>Notes: </span>{detailPanel.notes}
              </div>
            )}

            <div style={{display:'flex',gap:8,marginTop:8}}>
              <button onClick={()=>{setModal(detailPanel);setDetailPanel(null)}}
                style={{flex:1,padding:'8px',borderRadius:6,border:'1px solid #E5E7EB',background:'#fff',color:'#0F1017',fontSize:12,fontWeight:600,cursor:'pointer'}}>
                Edit PO
              </button>
              <button onClick={()=>setPrintPO(detailPanel.id)}
                style={{padding:'8px 14px',borderRadius:6,border:'1px solid #1B3A6B',background:'#1B3A6B',color:'#fff',fontSize:12,fontWeight:600,cursor:'pointer'}}>
                🖨 Print
              </button>
              <button onClick={()=>{handleDelete(detailPanel.id);setDetailPanel(null)}}
                style={{padding:'8px 14px',borderRadius:6,border:'1px solid #FECACA',background:'#FEF2F2',color:'#DC2626',fontSize:12,fontWeight:600,cursor:'pointer'}}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      {detailPanel && <div onClick={()=>setDetailPanel(null)} style={{position:'fixed',inset:0,zIndex:249}}/>}

      {modal==='new' && <POModal project={selectedProject} costCodes={costCodes} subCodesList={subCodesList} analysisCodes={analysisCodes} onSave={handleSave} onClose={()=>setModal(null)} currentUserName={currentUserName} can={can}/>}
      {modal && modal!=='new' && modal.__isAddendumDraft && <POModal po={modal} project={selectedProject} costCodes={costCodes} subCodesList={subCodesList} analysisCodes={analysisCodes} onSave={handleSave} onClose={()=>setModal(null)} currentUserName={currentUserName} can={can}/>}
      {modal && modal!=='new' && !modal.__isAddendumDraft && <POModal po={modal} project={selectedProject} costCodes={costCodes} subCodesList={subCodesList} analysisCodes={analysisCodes} cachedLines={poLines[modal.id]} onSave={handleSave} onClose={()=>setModal(null)} currentUserName={currentUserName} can={can}/>}
      {printPO && <PrintDocument docType="po" docId={printPO} onClose={()=>setPrintPO(null)}/>}
      {detailPO && <PODetail po={detailPO} costCodes={costCodes} subCodesList={subCodesList} onClose={()=>setDetailPO(null)} onEdit={()=>{setModal(detailPO);setDetailPO(null)}}/>}
      {/* ── Cost Control Audit Tab (formerly "Analysis Search") ── */}
      {selectedProject && activeTab === 'analysis_search' && (
        <div style={{padding:'0 24px 24px',display:'flex',flexDirection:'column',gap:16}}>
          {/* ── Search bar ── */}
          <div style={{background:'#fff',border:'1px solid #E5E7EB',borderRadius:10,padding:16}}>
            <div style={{fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.08em',color:'#6B7280',marginBottom:10}}>Cost Control Audit</div>
            <div style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap'}}>
              <select value={auditMode} onChange={e=>{setAuditMode(e.target.value); setAuditValue(''); setAuditResult(null)}}
                style={{padding:'8px 12px',border:'1px solid #D1D5DB',borderRadius:6,fontSize:13,color:'#0F1017',background:'#fff'}}>
                <option value="sub_code">Sub Code</option>
                <option value="resource_code">Resource Code</option>
                <option value="cost_code">Cost Code (Indirect)</option>
                <option value="po_number">PO Number</option>
              </select>

              {auditMode === 'po_number' ? (
                <input value={auditValue} onChange={e=>setAuditValue(e.target.value)} placeholder="Enter PO Number..."
                  style={{padding:'8px 12px',border:'1px solid #D1D5DB',borderRadius:6,fontSize:13,color:'#0F1017',background:'#fff',minWidth:220}}/>
              ) : (
                <select value={auditValue} onChange={e=>setAuditValue(e.target.value)}
                  style={{padding:'8px 12px',border:'1px solid #D1D5DB',borderRadius:6,fontSize:13,color:'#0F1017',background:'#fff',minWidth:260}}>
                  <option value="">
                    {auditMode==='sub_code' ? '— Select Sub Code —' : auditMode==='resource_code' ? '— Select Resource Code —' : '— Select Cost Code —'}
                  </option>
                  {auditMode==='sub_code' && subCodesList.map(sc=>(
                    <option key={sc.code} value={sc.code}>{sc.code}{sc.description?` — ${sc.description}`:''}</option>
                  ))}
                  {auditMode==='resource_code' && [...new Set(subCodesList.map(s=>s.resourceCode).filter(Boolean))].sort().map(rc=>(
                    <option key={rc} value={rc}>{rc}</option>
                  ))}
                  {auditMode==='cost_code' && costCodes.filter(c=>parseInt(c.level)===1).map(cc=>(
                    <option key={cc.code} value={cc.code}>{cc.code}{cc.description?` — ${cc.description}`:''}</option>
                  ))}
                </select>
              )}

              <button onClick={runAudit} disabled={!auditValue.trim()||auditLoading}
                style={{padding:'8px 20px',borderRadius:6,border:'none',background:'#2563EB',color:'#fff',fontSize:13,fontWeight:700,cursor:'pointer',opacity:(!auditValue.trim()||auditLoading)?0.5:1}}>
                {auditLoading ? 'Searching...' : 'Search'}
              </button>
            </div>
          </div>

          {auditResult?.notFound && (
            <div style={{background:'#fff',border:'1px solid #E5E7EB',borderRadius:10,padding:32,textAlign:'center',color:'#6B7280',fontSize:13}}>
              No PO found with number <b>"{auditValue}"</b> in this project.
            </div>
          )}

          {auditResult?.po && (
            <div style={{fontSize:12,color:'#6B7280'}}>
              PO <b style={{color:'#0F1017'}}>{auditResult.po.po_number}</b> — {auditResult.po.vendor} — {auditResult.po.approval_stage||'draft'} / {auditResult.po.status} — {auditResult.po.cost_type||'direct'}
              {' '}— {auditResult.cards.length} distinct {isDirectPO(auditResult.po) ? 'Resource Code' : 'Cost Code'}{auditResult.cards.length!==1?'s':''} found on this PO
            </div>
          )}

          {auditResult?.cards?.map(card => (
            <div key={card.key || card.label} style={{display:'flex',flexDirection:'column',gap:12}}>
              {card.unmapped ? (
                <div style={{background:'#FEF3C7',border:'1px solid #FDE68A',borderRadius:10,padding:16}}>
                  <div style={{fontSize:12,fontWeight:700,color:'#92400E'}}>⚠ {card.label} — Unmapped Code</div>
                  <div style={{fontSize:12,color:'#92400E',marginTop:4}}>No matching Cost Code found in the cost code tree. Cannot reconcile against any Budget.</div>
                </div>
              ) : (
                <>
                  {/* ── Summary / Control Card ── */}
                  <div style={{background:'#fff',border:'1px solid #E5E7EB',borderRadius:10,padding:16}}>
                    <div style={{fontSize:13,fontWeight:700,color:'#0F1017',marginBottom:2}}>
                      {card.label} {card.isIndirect ? '(Indirect — Cost Code)' : '(Direct — Resource Code)'}
                    </div>
                    {card.kind === 'legacy' && (
                      <div style={{fontSize:11,color:'#B45309',marginBottom:8}}>Legacy — No Resource Code Mapping. This Sub Code's parent is a Level 1 Cost Code directly (pre-Resource-Code data) — no tender_direct_items.resource_code budget exists for it.</div>
                    )}
                    <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'6px 24px',fontSize:12,marginTop:8}}>
                      <div><span style={{color:'#6B7280'}}>Budget:</span> <b>{card.kind==='legacy' ? '—' : fmt(card.budgetTotal)}</b></div>
                      {!card.isIndirect && <div><span style={{color:'#6B7280'}}>Live Commitment Qty:</span> <b style={{color:'#D97706'}}>{fmt(card.commQty)}</b></div>}
                      <div><span style={{color:'#6B7280'}}>Live Commitment (exc VAT):</span> <b style={{color:'#D97706'}}>{fmt(card.commValue)}</b></div>
                      <div>
                        <span style={{color:'#6B7280'}}>Live Forecast:</span>{' '}
                        <b>{card.forecastNode ? fmt(card.forecastNode.resolvedEac) : 'No forecast recorded'}</b>
                        {card.forecastNode && !card.forecastNode.hasEffectiveForecast && <div style={{fontSize:9,color:'#9CA3AF'}}>Budget fallback — no forecast decision</div>}
                      </div>
                      <div><span style={{color:'#6B7280'}}>Budget Remaining:</span> <b style={{color:card.overBudget?'#DC2626':'#1A7A4A'}}>{card.budgetRemaining!=null ? fmt(card.budgetRemaining) : '—'}</b></div>
                      <div><span style={{color:'#6B7280'}}>Forecast Remaining:</span> <b style={{color:card.overForecast?'#DC2626':'#1A7A4A'}}>{card.forecastRemaining!=null ? fmt(card.forecastRemaining) : '—'}</b></div>
                    </div>
                    {(card.overBudget || card.overForecast) && (
                      <div style={{marginTop:8,display:'flex',gap:8}}>
                        {card.overBudget && <span style={{fontSize:11,fontWeight:700,color:'#DC2626'}}>⚠ Over Budget</span>}
                        {card.overForecast && <span style={{fontSize:11,fontWeight:700,color:'#DC2626'}}>⚠ Over Forecast</span>}
                      </div>
                    )}
                  </div>

                  {/* ── Commitment Evidence ── */}
                  <div style={{background:'#fff',border:'1px solid #E5E7EB',borderRadius:10,overflow:'hidden'}}>
                    <div style={{padding:'10px 16px',fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.06em',color:'#6B7280',borderBottom:'1px solid #E5E7EB'}}>
                      Commitment Evidence — {card.commLines.length} PO line{card.commLines.length!==1?'s':''}
                    </div>
                    {card.commLines.length === 0 ? (
                      <div style={{padding:20,textAlign:'center',color:'#9CA3AF',fontSize:12}}>No approved/closed PO lines resolve to this code.</div>
                    ) : (
                      <table style={{width:'100%',borderCollapse:'collapse',fontSize:12.5}}>
                        <thead style={{background:'#EFF6FF'}}>
                          <tr>
                            {['PO Number','Vendor','Status','Sub Code','Resolved Code',(card.isIndirect?'':'Qty'),'Exc. VAT'].filter(Boolean).map(h=>(
                              <th key={h} style={{textAlign:['Qty','Exc. VAT'].includes(h)?'right':'left',padding:'9px 12px',fontSize:10,fontWeight:700,textTransform:'uppercase',color:'#3B82F6',whiteSpace:'nowrap',borderBottom:'1px solid #DBEAFE'}}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {card.commLines.map((l,i)=>(
                            <tr key={l.id} style={{borderBottom:'1px solid #F3F4F6',background:i%2===0?'#fff':'#F8FAFF'}}>
                              <td style={{padding:'8px 12px',fontFamily:'monospace',fontWeight:700,color:'#1A7A4A'}}>{l.purchase_orders?.po_number||'—'}</td>
                              <td style={{padding:'8px 12px',maxWidth:140,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{l.purchase_orders?.vendor||'—'}</td>
                              <td style={{padding:'8px 12px'}}>
                                <span style={{padding:'2px 7px',borderRadius:10,fontSize:10,fontWeight:700,background:l.purchase_orders?.status==='approved'?'#DCFCE7':'#F3F4F6',color:l.purchase_orders?.status==='approved'?'#14532D':'#374151'}}>{l.purchase_orders?.status||'—'}</span>
                              </td>
                              <td style={{padding:'8px 12px',fontWeight:700,color:'#2563EB'}}>{l.sub_code||'—'}</td>
                              <td style={{padding:'8px 12px',fontFamily:'monospace',fontSize:11,color:'#6B7280'}}>{card.label}</td>
                              {!card.isIndirect && <td style={{padding:'8px 12px',textAlign:'right',fontWeight:600}}>{fmt(l.qty)}</td>}
                              <td style={{padding:'8px 12px',textAlign:'right',fontWeight:700,color:'#1A7A4A'}}>{fmt(l.amount_exc_vat)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {/* ── Budget Evidence ── */}
                  {card.kind !== 'legacy' && (
                    <div style={{background:'#fff',border:'1px solid #E5E7EB',borderRadius:10,overflow:'hidden'}}>
                      <div style={{padding:'10px 16px',fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.06em',color:'#6B7280',borderBottom:'1px solid #E5E7EB'}}>
                        Budget Evidence — {card.budgetRows.length} row{card.budgetRows.length!==1?'s':''} from {card.isIndirect?'tender_indirect_items':'tender_direct_items'}
                      </div>
                      {card.budgetRows.length === 0 ? (
                        <div style={{padding:20,textAlign:'center',color:'#9CA3AF',fontSize:12}}>No budget rows found for this code.</div>
                      ) : (
                        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12.5}}>
                          <thead style={{background:'#EFF6FF'}}>
                            <tr>
                              {(card.isIndirect ? ['Description','Cost Code','Tender Budget'] : ['Type','Description','Resource Code','Qty','Rate']).map(h=>(
                                <th key={h} style={{textAlign:['Qty','Rate','Tender Budget'].includes(h)?'right':'left',padding:'9px 12px',fontSize:10,fontWeight:700,textTransform:'uppercase',color:'#3B82F6',whiteSpace:'nowrap',borderBottom:'1px solid #DBEAFE'}}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {card.budgetRows.map((r,i)=>(
                              <tr key={r.id} style={{borderBottom:'1px solid #F3F4F6',background:i%2===0?'#fff':'#F8FAFF'}}>
                                {card.isIndirect ? (
                                  <>
                                    <td style={{padding:'8px 12px'}}>{r.description||'—'}</td>
                                    <td style={{padding:'8px 12px',fontFamily:'monospace'}}>{r.cost_code}</td>
                                    <td style={{padding:'8px 12px',textAlign:'right',fontWeight:700}}>{fmt(r.tender_budget)}</td>
                                  </>
                                ) : (
                                  <>
                                    <td style={{padding:'8px 12px',textTransform:'capitalize'}}>{r.row_type}</td>
                                    <td style={{padding:'8px 12px'}}>{r.description||'—'}</td>
                                    <td style={{padding:'8px 12px',fontFamily:'monospace'}}>{r.resource_code}</td>
                                    <td style={{padding:'8px 12px',textAlign:'right',fontWeight:700}}>{fmt(r.qty)}</td>
                                    <td style={{padding:'8px 12px',textAlign:'right'}}>{fmt(r.rate)}</td>
                                  </>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}

                  {/* ── Forecast Evidence ── */}
                  {card.kind !== 'legacy' && (
                    <div style={{background:'#fff',border:'1px solid #E5E7EB',borderRadius:10,padding:16}}>
                      <div style={{fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.06em',color:'#6B7280',marginBottom:8}}>Forecast Evidence — resolveCurrentForecastState()</div>
                      {card.forecastNode ? (
                        <div style={{fontSize:12,color:'#0F1017'}}>
                          {card.isIndirect ? 'Cost Code' : 'Resource Code'}: <b>{card.label}</b> — resolvedEac: <b>{fmt(card.forecastNode.resolvedEac)}</b>
                          {' '}— {card.forecastNode.hasEffectiveForecast
                            ? <span style={{color:'#1A7A4A',fontWeight:700}}>Real forecast decision</span>
                            : <span style={{color:'#B45309',fontWeight:700}}>Budget fallback — no forecast decision</span>}
                        </div>
                      ) : (
                        <div style={{fontSize:12,color:'#9CA3AF'}}>No Live Forecast node found for this code.</div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Lines Modal ── */}
      {linesModal && (
        <div style={{position:'fixed',inset:0,zIndex:400,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',padding:24}}>
          <div style={{background:'#fff',borderRadius:12,width:'100%',maxWidth:900,maxHeight:'85vh',display:'flex',flexDirection:'column',boxShadow:'0 20px 60px rgba(0,0,0,0.2)'}}>
            {/* Header */}
            <div style={{padding:'16px 20px',borderBottom:'1px solid #E5E7EB',display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0}}>
              <div>
                <div style={{fontSize:14,fontWeight:700,color:'#0F1017'}}>Line Items — {linesModal.po_number}</div>
                <div style={{fontSize:11,color:'#6B7280',marginTop:2}}>{linesModal.vendor} · {linesModal.issue_date||'—'} · {linesModal.status}</div>
              </div>
              <button onClick={()=>setLinesModal(null)}
                style={{width:28,height:28,borderRadius:6,border:'1px solid #E5E7EB',background:'#fff',color:'#6B7280',fontSize:16,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>✕</button>
            </div>
            {/* Body */}
            <div style={{flex:1,overflowY:'auto'}}>
              {!poLines[linesModal.id] ? (
                <div style={{padding:32,textAlign:'center',color:'#9CA3AF',fontSize:13}}>Loading...</div>
              ) : poLines[linesModal.id].length === 0 ? (
                <div style={{padding:32,textAlign:'center',color:'#9CA3AF',fontSize:13}}>No line items</div>
              ) : (
                <>
                  {/* Qty Check Summary — Phase PO-3: Direct groups by Resource
                      Code (or flags Legacy pre-Resource-Code sub codes),
                      Indirect groups by Level 1 Cost Code and compares
                      SAR amounts (ex-VAT) instead of quantities. */}
                  {(() => {
                    const isIndirect = !isDirectPO(linesModal)
                    const budgetSide = isIndirect ? (budgetQtys.indirect || {}) : (budgetQtys.direct || {})
                    const commSide = isIndirect ? (committedQtys.indirect || {}) : (committedQtys.direct || {})
                    if (!Object.keys(budgetSide).length && !Object.keys(commSide).length) return null

                    return (
                    <div style={{padding:'12px 16px',background:'#F9FAFB',borderBottom:'1px solid #E5E7EB',flexShrink:0}}>
                      <div style={{fontSize:10,fontWeight:700,textTransform:'uppercase',color:'#6B7280',marginBottom:8}}>
                        Budget vs Commitment Check {isIndirect ? '(Indirect — SAR ex-VAT)' : '(Direct — Qty)'}
                      </div>
                      <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                        {(() => {
                          // Sync resolution using the already-loaded costCodes state
                          // (avoids re-fetching cost_codes inside the display itself;
                          // the async getCachedCostCodeMaps() version is only used in
                          // the project-level useEffect above for budget/commitment
                          // aggregation, which runs independently of this render).
                          const syncMaps = buildCostCodeMaps(costCodes)
                          const resolved = (poLines[linesModal.id]||[]).map(l => ({
                            line: l,
                            r: isIndirect
                              ? resolveIndirectKey(l.cost_code_id, l.sub_code, syncMaps)
                              : resolveDirectKey(l.sub_code, syncMaps),
                          }))
                          const groupKeys = [...new Set(resolved.map(x => x.r.key || `__${x.r.kind}__${x.r.label}`))]

                          // Single-item selector (replaces "show all cards
                          // at once"). Classification pass reuses the exact
                          // same budgetSide/commSide lookups the cards use
                          // below -- no new calculation logic.
                          const groupMeta = groupKeys.map(groupKey => {
                            const group = resolved.filter(x => (x.r.key || `__${x.r.kind}__${x.r.label}`) === groupKey)
                            const first = group[0].r
                            if (first.kind === 'unknown' || first.kind === 'legacy') return { groupKey, label: first.label, kind: first.kind, over: false }
                            const b = budgetSide[first.key] || 0
                            const c = commSide[first.key] || 0
                            const v = isIndirect
                              ? group.reduce((s,x)=>s+(parseFloat(x.line.amount_exc_vat)||0),0)
                              : group.reduce((s,x)=>s+(parseFloat(x.line.qty)||0),0)
                            return { groupKey, label: first.label, kind: 'resolved', over: v > (b - c) }
                          })
                          const overCount = groupMeta.filter(g=>g.over).length
                          const withinCount = groupMeta.filter(g=>!g.over && g.kind==='resolved').length
                          const activeKey = selectedCardKey && groupKeys.includes(selectedCardKey)
                            ? selectedCardKey
                            : (groupMeta.find(g=>g.over)?.groupKey || groupMeta[0]?.groupKey || null)
                          if (activeKey !== selectedCardKey) {
                            setTimeout(() => setSelectedCardKey(activeKey), 0)
                          }
                          const visibleGroupKeys = activeKey ? [activeKey] : []

                          return [
                          <div key="rep-summary-header" style={{width:'100%',display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:8,marginBottom:8}}>
                            <div style={{fontSize:11,color:'#6B7280'}}>
                              {overCount>0 && <span style={{color:'#DC2626',fontWeight:700}}>{overCount} Over Budget</span>}
                              {overCount>0 && withinCount>0 && ' · '}
                              {withinCount>0 && <span style={{color:'#1A7A4A',fontWeight:700}}>{withinCount} Within Budget</span>}
                            </div>
                          </div>,
                          <select key="rep-summary-select" value={activeKey||''} onChange={e=>setSelectedCardKey(e.target.value)}
                            style={{padding:'6px 10px',borderRadius:6,border:'1px solid #E5E7EB',fontSize:12,fontWeight:600,color:'#0F1017',background:'#fff',marginBottom:10,minWidth:240,width:'100%'}}>
                            {groupMeta.map(g => (
                              <option key={g.groupKey} value={g.groupKey}>
                                {g.label}{g.kind==='resolved' ? (g.over ? ' • Over Budget' : ' • Within Budget') : g.kind==='legacy' ? ' • Legacy' : ' • Unmapped'}
                              </option>
                            ))}
                          </select>,
                          ...visibleGroupKeys.map(groupKey => {
                            const group = resolved.filter(x => (x.r.key || `__${x.r.kind}__${x.r.label}`) === groupKey)
                            const first = group[0].r

                            if (first.kind === 'unknown') {
                              return (
                                <div key={groupKey} style={{background:'#FEF3C7',border:'1px solid #FDE68A',borderRadius:8,padding:'8px 12px',minWidth:180}}>
                                  <div style={{fontSize:10,fontWeight:700,color:'#92400E',marginBottom:4}}>⚠ {first.label} — Unmapped Code</div>
                                  <div style={{fontSize:11,color:'#92400E'}}>No matching Cost Code found — cannot compare against budget.</div>
                                </div>
                              )
                            }
                            if (first.kind === 'legacy') {
                              const poQty = group.reduce((s,x)=>s+(parseFloat(x.line.qty)||0),0)
                              return (
                                <div key={groupKey} style={{background:'#F3F4F6',border:'1px solid #D1D5DB',borderRadius:8,padding:'8px 12px',minWidth:180}}>
                                  <div style={{fontSize:10,fontWeight:700,color:'#6B7280',marginBottom:4}}>{first.label} — <span style={{color:'#B45309'}}>Legacy — No Resource Code Mapping</span></div>
                                  <div style={{fontSize:11,color:'#6B7280'}}>This PO Qty: {fmt(poQty)}</div>
                                </div>
                              )
                            }

                            const budgetVal = budgetSide[first.key] || 0
                            const committedVal = commSide[first.key] || 0
                            const poVal = isIndirect
                              ? group.reduce((s,x)=>s+(parseFloat(x.line.amount_exc_vat)||0),0)
                              : group.reduce((s,x)=>s+(parseFloat(x.line.qty)||0),0)
                            const remaining = budgetVal - committedVal
                            const ok = poVal <= remaining

                            // Card 2: Value (SAR) -- Direct only. Live Forecast via
                            // resolveCurrentForecastState() (see import comment).
                            // fc.resolvedEac is NEVER null when a match is found --
                            // the official Forecast engine always resolves to at
                            // least Budget (bac) as a fallback when no forecast
                            // decision exists (hasEffectiveForecast: false). Shown
                            // per Option A: always display resolvedEac, labeled as
                            // a Budget fallback when hasEffectiveForecast is false,
                            // never hidden as N/A. forecastValue stays null only
                            // when this Resource Code has no matching node at all
                            // in the resolved tree (distinct from "resolved to 0").
                            const fc = !isIndirect ? forecastByResource[first.key] : null
                            const showValueCard = !isIndirect
                            const forecastValue = fc ? fc.resolvedEac : null
                            const forecastIsBudgetFallback = fc ? !fc.hasEffectiveForecast : false
                            const committedValue = committedValueByResource[first.key] || 0
                            const poValueExcVat = group.reduce((s,x)=>s+(parseFloat(x.line.amount_exc_vat)||0),0)
                            // Live Forecast - Live Commitment (both current-state,
                            // matching points in time -- the audited fix).
                            const forecastRemaining = forecastValue != null ? forecastValue - committedValue : null
                            const overForecast = forecastRemaining != null && poValueExcVat > forecastRemaining

                            return (
                              <React.Fragment key={groupKey}>
                                <div style={{background:ok?'#F0FDF4':'#FEF2F2',border:`1px solid ${ok?'#BBF7D0':'#FECACA'}`,borderRadius:8,padding:'8px 12px',minWidth:180}}>
                                  <div style={{fontSize:10,fontWeight:700,color:'#6B7280',marginBottom:4}}>{first.label} — Qty</div>
                                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:2,fontSize:11}}>
                                    <span style={{color:'#6B7280'}}>Budget Qty:</span><span style={{fontWeight:700,textAlign:'right'}}>{fmt(budgetVal)}</span>
                                    <span style={{color:'#6B7280'}}>Committed Qty:</span><span style={{fontWeight:700,color:'#D97706',textAlign:'right'}}>{fmt(committedVal)}</span>
                                    <span style={{color:'#6B7280'}}>This PO Qty:</span><span style={{fontWeight:700,color:'#1B3A6B',textAlign:'right'}}>{fmt(poVal)}</span>
                                    <span style={{color:'#6B7280'}}>Remaining:</span><span style={{fontWeight:700,color:ok?'#1A7A4A':'#DC2626',textAlign:'right'}}>{fmt(remaining)}</span>
                                  </div>
                                  <div style={{marginTop:6,fontSize:11,fontWeight:700,color:ok?'#1A7A4A':'#DC2626',textAlign:'center'}}>
                                    {ok ? '✓ Within Budget' : '⚠ Over Budget'}
                                  </div>
                                </div>

                                {showValueCard && (
                                  <div style={{background:overForecast?'#FEF2F2':'#F0FDF4',border:`1px solid ${overForecast?'#FECACA':'#BBF7D0'}`,borderRadius:8,padding:'8px 12px',minWidth:200}}>
                                    <div style={{fontSize:10,fontWeight:700,color:'#6B7280',marginBottom:4}}>{first.label} — Live Forecast (SAR)</div>
                                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:2,fontSize:11}}>
                                      <span style={{color:'#6B7280'}}>Forecast Value:</span>
                                      <span style={{textAlign:'right'}}>
                                        <span style={{fontWeight:700}}>{forecastValue!=null ? fmt(forecastValue) : 'No forecast recorded'}</span>
                                        {forecastIsBudgetFallback && <div style={{fontSize:9,color:'#9CA3AF',fontWeight:400}}>Budget fallback — no forecast decision</div>}
                                      </span>
                                      <span style={{color:'#6B7280'}}>Committed (exc VAT):</span><span style={{fontWeight:700,color:'#D97706',textAlign:'right'}}>{fmt(committedValue)}</span>
                                      <span style={{color:'#6B7280'}}>This PO (exc VAT):</span><span style={{fontWeight:700,color:'#1B3A6B',textAlign:'right'}}>{fmt(poValueExcVat)}</span>
                                      <span style={{color:'#6B7280'}}>Forecast Remaining:</span><span style={{fontWeight:700,color:forecastRemaining==null?'#9CA3AF':(overForecast?'#DC2626':'#1A7A4A'),textAlign:'right'}}>{forecastRemaining!=null ? fmt(forecastRemaining) : '—'}</span>
                                    </div>
                                    {forecastRemaining!=null && (
                                      <div style={{marginTop:6,fontSize:11,fontWeight:700,color:overForecast?'#DC2626':'#1A7A4A',textAlign:'center'}}>
                                        {overForecast ? '⚠ Over Forecast' : '✓ Within Forecast'}
                                      </div>
                                    )}
                                  </div>
                                )}

                                {showValueCard && (() => {
                                  // Card 3: Budget vs Commitment (Value, SAR). Same
                                  // resource_code key as Card 1/2, budgetValueByResource
                                  // computed from qty × unit_rate (not total_budget --
                                  // confirmed unreliable/zero). Material and Equipment
                                  // use the exact same aggregation -- no separate logic.
                                  const budgetValue = budgetValueByResource[first.key] || 0
                                  const budgetValueRemaining = budgetValue - committedValue
                                  const overBudgetValue = committedValue > budgetValue
                                  return (
                                    <div style={{background:overBudgetValue?'#FEF2F2':'#F0FDF4',border:`1px solid ${overBudgetValue?'#FECACA':'#BBF7D0'}`,borderRadius:8,padding:'8px 12px',minWidth:200}}>
                                      <div style={{fontSize:10,fontWeight:700,color:'#6B7280',marginBottom:4}}>{first.label} — Budget (SAR)</div>
                                      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:2,fontSize:11}}>
                                        <span style={{color:'#6B7280'}}>Budget Value:</span><span style={{fontWeight:700,textAlign:'right'}}>{fmt(budgetValue)}</span>
                                        <span style={{color:'#6B7280'}}>Committed (exc VAT):</span><span style={{fontWeight:700,color:'#D97706',textAlign:'right'}}>{fmt(committedValue)}</span>
                                        <span style={{color:'#6B7280'}}>This PO (exc VAT):</span><span style={{fontWeight:700,color:'#1B3A6B',textAlign:'right'}}>{fmt(poValueExcVat)}</span>
                                        <span style={{color:'#6B7280'}}>Remaining:</span><span style={{fontWeight:700,color:overBudgetValue?'#DC2626':'#1A7A4A',textAlign:'right'}}>{fmt(budgetValueRemaining)}</span>
                                      </div>
                                      <div style={{marginTop:6,fontSize:11,fontWeight:700,color:overBudgetValue?'#DC2626':'#1A7A4A',textAlign:'center'}}>
                                        {overBudgetValue ? '⚠ Over Budget' : '✓ Within Budget'}
                                      </div>
                                    </div>
                                  )
                                })()}
                              </React.Fragment>
                            )
                          })
                          ]
                        })()}
                      </div>
                    </div>
                    )
                  })()}
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:12.5}}>
                  <thead style={{background:'#F5F1EA',position:'sticky',top:0}}>
                    <tr>
                      {['Ref Code','Sub Code','Cost Code','Resource Code','Description','Unit','Qty','PO Rate','Budget Rate','Variance','Exc.VAT'].map(h=>(
                        <th key={h} style={{textAlign: ['Qty','PO Rate','Budget Rate','Variance','Exc.VAT'].includes(h) ? 'right' : 'left',padding:'10px 10px',fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.06em',color:'#6B655C',whiteSpace:'nowrap',borderBottom:'1px solid #DDD9D0'}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {poLines[linesModal.id].map((l,i)=>{
                      // Rate check: compare PO rate vs budget rate (from budget_summary ev_rate by sub_code)
                      const poRate = parseFloat(l.unit_rate) || 0
                      // Budget rate stored in budgetRates state (loaded when lines modal opens)
                      const budgetRate = budgetRates?.[l.sub_code] || null
                      const variance = budgetRate ? poRate - budgetRate : null
                      const variancePct = budgetRate && budgetRate > 0 ? (variance / budgetRate) * 100 : null
                      const rateOk = variance === null || variance <= 0
                      const subInfo = subCodesList.find(s=>s.code.trim().toUpperCase()===String(l.sub_code||'').trim().toUpperCase())
                      return (
                        <tr key={l.id} style={{borderBottom:'1px solid #F3F4F6',background:!rateOk?'#FFF7F7':i%2===0?'#fff':'#FAFAFA'}}>
                          <td style={{padding:'8px 10px',fontFamily:'monospace',fontSize:11,color:'#1B3A6B'}}>{l.ref_code||'—'}</td>
                          <td style={{padding:'8px 10px',fontSize:11,color:'#1A7A4A',fontWeight:600}}>{l.sub_code||'—'}</td>
                          <td style={{padding:'8px 10px',fontSize:11,color:'#6B655C'}}>{subInfo?.costCode || '—'}</td>
                          <td style={{padding:'8px 10px',fontSize:11,color:'#7C3AED'}}>{subInfo?.resourceCode || '—'}</td>
                          <td style={{padding:'8px 10px',color:'#0F1017',maxWidth:220,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{l.description||'—'}</td>
                          <td style={{padding:'8px 10px',color:'#6B7280'}}>{l.unit||'—'}</td>
                          <td style={{padding:'8px 10px',textAlign:'right',fontWeight:600}}>{fmt(l.qty)}</td>
                          <td style={{padding:'8px 10px',textAlign:'right',color:'#0F1017',fontWeight:600}}>{fmt(l.unit_rate)}</td>
                          <td style={{padding:'8px 10px',textAlign:'right',color:'#1A7A4A'}}>{budgetRate ? fmt(budgetRate) : '—'}</td>
                          <td style={{padding:'8px 10px',textAlign:'right',fontWeight:700,color:rateOk?'#1A7A4A':'#DC2626'}}>
                            {variance !== null ? (
                              <span>{variance > 0 ? '+' : ''}{fmt(variance)} ({variancePct > 0 ? '+' : ''}{variancePct?.toFixed(1)}%)</span>
                            ) : '—'}
                          </td>
                          <td style={{padding:'8px 10px',textAlign:'right',fontWeight:700,color:'#1A7A4A'}}>{fmt(l.amount_exc_vat)}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                  <tfoot style={{background:'#F5F1EA',borderTop:'2px solid #DDD9D0'}}>
                    <tr>
                      <td colSpan={4} style={{padding:'10px',fontSize:11,fontWeight:700,color:'#6B655C',textTransform:'uppercase'}}>Total</td>
                      <td style={{padding:'10px',textAlign:'right',fontWeight:800,fontSize:13}}>
                        {fmt(poLines[linesModal.id].reduce((s,l)=>s+(parseFloat(l.qty)||0),0))}
                      </td>
                      <td colSpan={3}/>
                      <td style={{padding:'10px',textAlign:'right',fontWeight:800,fontSize:13,color:'#1A7A4A'}}>
                        {fmt(poLines[linesModal.id].reduce((s,l)=>s+(parseFloat(l.amount_exc_vat)||0),0))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {convertPO && <ConvertModal po={convertPO} form={convertForm} setForm={setConvertForm} costCodes={costCodes} onConfirm={handleConvert} onClose={()=>setConvertPO(null)} converting={converting}/>}
      {toast && <Toast {...toast} onClose={()=>setToast(null)}/>}

      {/* ── Rental Equipment Purchase Order Tab ── */}
      {selectedProject && activeTab === 'rental_equipment' && (
        <div style={{padding:'0 24px 24px',display:'flex',flexDirection:'column',gap:16}}>
          {/* Total Commitment Card — FIXED: "Total Commitment" now means
              APPROVED commitment only, matching the official
              get_commitment_by_subcode() definition used by Dashboard/Cost
              Control (which filters status IN ('approved','closed')).
              Previously this summed ALL POs regardless of stage (including
              draft/rejected), causing this screen to disagree with the
              official Commitment figure shown elsewhere. The all-status
              total is preserved separately below as "All POs Value" so no
              information is lost, but it is no longer labeled or treated
              as "Commitment". */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(6,1fr)',gap:12}}>
            <div style={{background:'#0F1017',borderRadius:10,padding:'14px 18px'}}>
              <div style={{fontSize:18,fontWeight:800,color:'#fff',fontVariantNumeric:'tabular-nums'}}>{fmtM(repPOs.filter(p=>p.approval_stage==='approved').reduce((s,p)=>s+(parseFloat(p.total_exc_vat)||0),0))}</div>
              <div style={{fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.08em',color:'rgba(255,255,255,.4)',marginTop:4}}>Total Commitment — Approved (Exc.VAT)</div>
            </div>
            <div style={{background:'#3A3226',borderRadius:10,padding:'14px 18px'}}>
              <div style={{fontSize:18,fontWeight:800,color:'#fff',fontVariantNumeric:'tabular-nums'}}>{fmtM(repPOs.filter(p=>p.approval_stage==='approved').reduce((s,p)=>s+(parseFloat(p.total_inc_vat)||0),0))}</div>
              <div style={{fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.08em',color:'rgba(255,255,255,.4)',marginTop:4}}>Total Commitment — Approved (Inc.VAT)</div>
            </div>
            <div style={{background:'#6B7280',borderRadius:10,padding:'14px 18px'}}>
              <div style={{fontSize:18,fontWeight:800,color:'#fff',fontVariantNumeric:'tabular-nums'}}>{fmtM(repPOs.reduce((s,p)=>s+(parseFloat(p.total_exc_vat)||0),0))}</div>
              <div style={{fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.08em',color:'rgba(255,255,255,.5)',marginTop:4}}>All POs Value — Any Status (Exc.VAT)</div>
            </div>
            <div style={{background:'#fff',border:'1px solid #E5E7EB',borderRadius:10,padding:'14px 18px'}}>
              <div style={{fontSize:22,fontWeight:800,color:'#0F1017'}}>{repStageCounts.all}</div>
              <div style={{fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.08em',color:'#6B7280',marginTop:4}}>Total POs</div>
            </div>
            <div style={{background:'rgba(26,122,74,.06)',border:'1px solid rgba(26,122,74,.2)',borderRadius:10,padding:'14px 18px'}}>
              <div style={{fontSize:22,fontWeight:800,color:'#1A7A4A'}}>{repStageCounts.approved}</div>
              <div style={{fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.08em',color:'#1A7A4A',marginTop:4}}>Approved</div>
            </div>
            <div style={{background:'#FFFBEB',border:'1px solid #FDE68A',borderRadius:10,padding:'14px 18px'}}>
              <div style={{fontSize:22,fontWeight:800,color:'#D97706'}}>{repStageCounts.draft + repStageCounts.cost_control + repStageCounts.director}</div>
              <div style={{fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.08em',color:'#D97706',marginTop:4}}>Pending</div>
            </div>
          </div>

          {/* Pipeline Stage Tabs */}
          <div style={{display:'flex',background:'#fff',border:'1px solid #E5E7EB',borderRadius:10,overflow:'hidden'}}>
            {[
              { key:'all',          label:'All Items',     count:repStageCounts.all },
              { key:'draft',        label:'Draft',         count:repStageCounts.draft },
              { key:'cost_control', label:'Cost Control',  count:repStageCounts.cost_control },
              { key:'director',     label:'Director',      count:repStageCounts.director },
              { key:'approved',     label:'Approved',      count:repStageCounts.approved },
              { key:'rejected',     label:'Rejected',      count:repStageCounts.rejected },
            ].map(s => (
              <div key={s.key} onClick={()=>setRepPipelineStage(s.key)}
                style={{flex:1,padding:'11px 14px',textAlign:'center',cursor:'pointer',borderBottom: repPipelineStage===s.key?'2px solid #1A7A4A':'2px solid transparent',background: repPipelineStage===s.key?'rgba(26,122,74,.06)':'#fff'}}>
                <div style={{fontSize:9.5,fontWeight:700,textTransform:'uppercase',letterSpacing:'.07em',color:'#6B7280'}}>{s.label}</div>
                <div style={{fontSize:20,fontWeight:800,color: repPipelineStage===s.key?'#1A7A4A':'#0F1017'}}>{s.count}</div>
              </div>
            ))}
          </div>

          {/* Action Bar */}
          <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center',justifyContent:'space-between'}}>
            <div style={{display:'flex',gap:8,alignItems:'center',flex:1,minWidth:220}}>
              <div style={{position:'relative',flex:1,maxWidth:320}}>
                <input value={repSearch} onChange={e=>setRepSearch(e.target.value)} placeholder="Search PO number or vendor..."
                  style={{width:'100%',padding:'7px 12px 7px 32px',border:'1px solid #E5E7EB',borderRadius:8,fontSize:13}} />
                <div style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',color:'#9CA3AF'}}><SearchIcon/></div>
              </div>
            </div>
            <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
              {repSelected.length > 0 && (
                <>
                  <select value={repBulkActionType} onChange={e=>setRepBulkActionType(e.target.value)}
                    style={{padding:'7px 10px',border:'1px solid #E5E7EB',borderRadius:8,fontSize:13}}>
                    <option value="approve">{can('PO.Submit') ? 'Submit / Resubmit' : 'Approve'}</option>
                    {(can('PO.ApproveCostControl') || can('PO.ApproveDirector')) && <option value="reject">Reject</option>}
                    {can('PO.ApproveDirector') && <option value="revoke">Revoke</option>}
                    <option value="delete">Delete</option>
                  </select>
                  <button onClick={()=>repBulkAction(repBulkActionType)} disabled={repBulkBusy}
                    className={`px-3 py-2 text-sm text-white rounded-lg disabled:opacity-60 ${repBulkActionType==='delete' ? 'bg-red-600 hover:bg-red-700' : repBulkActionType==='reject' ? 'bg-orange-600 hover:bg-orange-700' : repBulkActionType==='revoke' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-green-600 hover:bg-green-700'}`}>
                    {repBulkBusy ? '⏳ جاري التنفيذ...' : `تنفيذ (${repSelected.length})`}
                  </button>
                </>
              )}
              <button onClick={repDownloadTemplate} className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-[#FAFAFA]">📥 Template</button>
              {can('PO.Create') && (
                <button onClick={() => setRepShowUpload(!repShowUpload)} className="px-3 py-2 text-sm border border-orange-300 text-orange-600 rounded-lg hover:bg-orange-50">↑ Upload</button>
              )}
              <button onClick={repExportToExcel} className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-[#FAFAFA]">↓ Export</button>
              <button onClick={repOpenNew} className="px-4 py-2 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600">+ New PO</button>
            </div>
          </div>

          {/* Upload Panel */}
          {repShowUpload && (
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-5">
              <h3 className="font-semibold text-orange-800 mb-2">رفع Rental Equipment PO من Excel</h3>
              <p className="text-xs text-[#6B7280] mb-3">الأعمدة المطلوبة: po number, Supplier Name, Cost Code, subcode, Quantity, unit, rate, Total exc VAT, Total inc VAT</p>
              <input ref={repFileRef} type="file" accept=".xlsx,.xls,.csv" onChange={repHandleFileUpload}
                className="text-xs text-[#6B7280] file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-orange-500 file:text-white file:text-sm mb-3" />
              {repUploadRows.length > 0 && (
                <div className="flex gap-3 items-center">
                  <button onClick={repPrepareUpload} disabled={repUploading}
                    className="px-4 py-2 bg-orange-500 text-white text-sm rounded-lg hover:bg-orange-600 disabled:opacity-60 min-w-48">
                    {repUploading ? `جاري الرفع ${repUploadProgress.done}/${repUploadProgress.total}...` : `تأكيد رفع ${repUploadRows.length} صف`}
                  </button>
                  <span className="text-xs text-[#6B7280]">تم قراءة {repUploadRows.length} صف من الملف</span>
                </div>
              )}
            </div>
          )}

          {/* Unmatched Cost Codes — manual resolution required before finishing upload */}
          {repUnmatched.length > 0 && (
            <div style={{position:'fixed',inset:0,zIndex:9999,background:'rgba(0,0,0,.5)',display:'flex',alignItems:'center',justifyContent:'center',padding:24}}>
              <div style={{background:'#fff',borderRadius:12,width:'100%',maxWidth:700,maxHeight:'85vh',display:'flex',flexDirection:'column'}}>
                <div style={{padding:'16px 24px',borderBottom:'1px solid #E5E7EB'}}>
                  <h3 style={{fontWeight:700,fontSize:16}}>⚠️ أكواد لم يتم مطابقتها تلقائيًا</h3>
                  <p style={{fontSize:12,color:'#6B7280',marginTop:4}}>اختر الكوست كود الصحيح لكل صف يدويًا قبل إتمام الرفع ({repUnmatched.length} صف متبقي)</p>
                </div>
                <div style={{flex:1,overflowY:'auto',padding:'12px 24px'}}>
                  <table style={{width:'100%',fontSize:12.5,borderCollapse:'collapse'}}>
                    <thead>
                      <tr style={{background:'#F9FAFB'}}>
                        <th style={{padding:'8px',textAlign:'left'}}>PO</th>
                        <th style={{padding:'8px',textAlign:'left'}}>Equipment</th>
                        <th style={{padding:'8px',textAlign:'left'}}>الكود في الإكسل</th>
                        <th style={{padding:'8px',textAlign:'left'}}>اختر الكوست كود الصحيح</th>
                      </tr>
                    </thead>
                    <tbody>
                      {repUnmatched.map(u => (
                        <tr key={u._uid} style={{borderBottom:'1px solid #F3F4F6'}}>
                          <td style={{padding:'8px',fontWeight:600}}>{u.po_number}</td>
                          <td style={{padding:'8px'}}>{u.equipment_type}</td>
                          <td style={{padding:'8px',fontFamily:'monospace',color:'#DC2626'}}>{u.cost_code_raw}</td>
                          <td style={{padding:'8px'}}>
                            <select onChange={e=>repResolveUnmatched(u._uid, e.target.value)} defaultValue=""
                              className="w-full border border-gray-300 rounded px-2 py-1 text-xs">
                              <option value="" disabled>اختر...</option>
                              {repCostCodes.map(c=><option key={c.id} value={c.id}>{c.code} — {c.description}</option>)}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex gap-3 p-6 border-t border-[#E5E7EB]">
                  <button onClick={()=>{ setRepUnmatched([]); setRepPendingGroups(null); setRepUploadRows([]); setRepShowUpload(false); if(repFileRef.current) repFileRef.current.value='' }}
                    className="flex-1 py-2 border border-gray-300 rounded-lg text-sm">إلغاء الرفع بالكامل</button>
                </div>
              </div>
            </div>
          )}

          {/* Table */}
          <div ref={repTableCardRef} style={{background:'#fff',border:'1px solid #E5E7EB',borderRadius:10,position:'relative'}}>
            {repLoading ? (
              <div style={{padding:40,textAlign:'center',color:'#6B7280'}}>Loading...</div>
            ) : repFilteredPOs.length === 0 ? (
              <div style={{padding:40,textAlign:'center',color:'#6B7280'}}>لا يوجد أوامر شراء معدات</div>
            ) : (
              <>
              <div ref={repTableScrollRef} onScroll={updateRepScrollState} style={{overflow:'auto', maxHeight:'70vh', borderRadius:10}}>
              <table style={{width:'100%',minWidth:1154,borderCollapse:'collapse',fontSize:13}}>
                <colgroup>{repColWidths.map((w,i)=><col key={i} style={{width:w}}/>)}</colgroup>
                <thead style={{position:'sticky',top:0,zIndex:5}}>
                  <tr style={{background:'#F9FAFB',borderBottom:'1px solid #E5E7EB'}}>
                    <th style={{padding:'10px 12px',textAlign:'left',background:'#F9FAFB'}}>
                      <input type="checkbox" checked={repSelected.length===repFilteredPOs.length && repFilteredPOs.length>0} onChange={repToggleSelectAll} />
                    </th>
                    <th style={{padding:'10px 12px',textAlign:'left',fontWeight:700,color:'#6B7280',fontSize:11,textTransform:'uppercase',background:'#F9FAFB'}}>PO Number</th>
                    <th style={{padding:'10px 12px',textAlign:'left',fontWeight:700,color:'#6B7280',fontSize:11,textTransform:'uppercase',background:'#F9FAFB'}}>Vendor</th>
                    <th style={{padding:'10px 12px',textAlign:'left',fontWeight:700,color:'#6B7280',fontSize:11,textTransform:'uppercase',background:'#F9FAFB'}}>Cost Code</th>

                    <th style={{padding:'10px 12px',textAlign:'left',fontWeight:700,color:'#6B7280',fontSize:11,textTransform:'uppercase',background:'#F9FAFB'}}>Equipment</th>
                    <th style={{padding:'10px 12px',textAlign:'right',fontWeight:700,color:'#6B7280',fontSize:11,textTransform:'uppercase',background:'#F9FAFB'}}>Total Exc.VAT</th>
                    <th style={{padding:'10px 12px',textAlign:'center',fontWeight:700,color:'#6B7280',fontSize:11,textTransform:'uppercase',background:'#F9FAFB'}}>Stage</th>
                    <th style={{padding:'10px 12px',textAlign:'right',fontWeight:700,color:'#6B7280',fontSize:11,textTransform:'uppercase',background:'#F9FAFB'}}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {repFilteredPOs.map(po => {
                    const lines = repLines[po.id] || []
                    const { btns } = repGetApprovalButtons(po)
                    const stage = po.approval_stage || 'draft'
                    return (
                      <tr key={po.id} style={{borderBottom:'1px solid #F3F4F6'}}>
                        <td style={{padding:'10px 12px'}}>
                          <input type="checkbox" checked={repSelected.includes(po.id)} onChange={()=>repToggleSelect(po.id)} />
                        </td>
                        <td style={{padding:'10px 12px',fontWeight:700}}>{po.po_number}</td>
                        <td style={{padding:'10px 12px'}}>{po.vendor}</td>
                        <td style={{padding:'10px 12px',fontFamily:'monospace',fontSize:12,color:'#6B7280'}}>
                          {(() => {
                            const codes = [...new Set(lines.map(l => repCostCodes.find(c=>c.id===l.cost_code_id)?.code).filter(Boolean))]
                            if (codes.length) return codes.join(', ')
                            return po.cost_codes?.code || '—'
                          })()}
                        </td>
                        <td style={{padding:'10px 12px',fontSize:12,color:'#6B7280'}}>
                          {lines.length ? lines.map(l=>l.equipment_type).join(', ') : '—'}
                        </td>
                        <td style={{padding:'10px 12px',textAlign:'right',fontWeight:700}}>{fmtRep(po.total_exc_vat)}</td>
                        <td style={{padding:'10px 12px',textAlign:'center'}}>
                          <span title={stage==='rejected' && po.rejection_reason ? `سبب الرفض: ${po.rejection_reason}` : undefined}
                            className={`px-2 py-1 rounded-full text-xs font-semibold ${stageColors[stage]}`}>{stage}</span>
                        </td>
                        <td style={{padding:'10px 12px',textAlign:'right'}}>
                          <div style={{display:'flex',gap:6,justifyContent:'flex-end',flexWrap:'wrap'}}>
                            {btns.map(b => (
                              <button key={b.action} onClick={()=> b.action==='reject' ? setRepRejectPO(po) : repHandleApproval(po, b.action)}
                                className={`px-2.5 py-1 text-xs rounded-lg font-medium ${b.color}`}>{b.label}</button>
                            ))}
                            {/* Create Addendum — only on the ORIGINAL PO (never on an
                                Addendum PO itself, po.original_po_id == null guards
                                that), only once Revoke has returned it to draft, and
                                only for the Procurement role, per the approved
                                workflow. This opens the SAME full POModal editor as
                                any new PO — no separate simplified form. */}
                            {stage === 'draft' && !po.original_po_id && repContractPoIds.has(po.id) && can('PO.Create') && (
                              <button onClick={()=>repOpenAddendumPO(po)} className="px-2.5 py-1 text-xs border border-orange-300 bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100">+ Addendum</button>
                            )}
                            <button onClick={()=>setRepViewPO(po)} className="px-2.5 py-1 text-xs border border-gray-300 rounded-lg hover:bg-[#FAFAFA]">View</button>
                            <button onClick={()=>{setRepLinesModal(po); setRepSelectedCardKey(null)}} className="px-2.5 py-1 text-xs border border-gray-300 rounded-lg hover:bg-[#FAFAFA]">Lines</button>
                            <button onClick={()=>setRepPrintPO(po.id)} className="px-2.5 py-1 text-xs border border-[#1B3A6B] text-[#1B3A6B] rounded-lg hover:bg-[#EFF3F9]">🖨 Print</button>
                            <button onClick={()=>repOpenEdit(po)} className="px-2.5 py-1 text-xs border border-gray-300 rounded-lg hover:bg-[#FAFAFA]">Edit</button>
                            {stage === 'draft' && (
                              <button onClick={()=>repDeletePO(po.id)} className="px-2.5 py-1 text-xs border border-red-200 text-red-600 rounded-lg hover:bg-red-50">Delete</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              </div>
              {repScrollState.hasOverflow && (
                <div style={{
                  position:'fixed', bottom:0, left:repNavLeftRight.left, right:repNavLeftRight.right, zIndex:50,
                  display:'flex', alignItems:'center', gap:8,
                  padding:'6px 12px', background:'rgba(255,255,255,0.97)',
                  borderTop:'1px solid #E5E7EB', backdropFilter:'blur(2px)',
                }}>
                  <button
                    onClick={()=>repScrollBy(-1)}
                    disabled={repScrollState.atStart}
                    aria-label="Scroll table left"
                    style={{
                      width:24, height:24, borderRadius:6, border:'1px solid #E5E7EB',
                      background:repScrollState.atStart?'#F9FAFB':'#fff', color:repScrollState.atStart?'#D1D5DB':'#6B7280',
                      cursor:repScrollState.atStart?'default':'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, flexShrink:0,
                    }}>‹</button>
                  <input
                    type="range" min={0} max={1000} step={1}
                    value={Math.round(repScrollState.ratio*1000)}
                    onChange={e=>repScrollToRatio(Number(e.target.value)/1000)}
                    aria-label="Scroll table horizontally"
                    style={{flex:1, height:4, accentColor:'#1B3A6B', cursor:'pointer'}}
                  />
                  <button
                    onClick={()=>repScrollBy(1)}
                    disabled={repScrollState.atEnd}
                    aria-label="Scroll table right"
                    style={{
                      width:24, height:24, borderRadius:6, border:'1px solid #E5E7EB',
                      background:repScrollState.atEnd?'#F9FAFB':'#fff', color:repScrollState.atEnd?'#D1D5DB':'#6B7280',
                      cursor:repScrollState.atEnd?'default':'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, flexShrink:0,
                    }}>›</button>
                </div>
              )}
              </>
            )}
          </div>

          {/* Reject Modal */}
          {repRejectPO && (
            <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.4)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999}}>
              <div style={{background:'#fff',borderRadius:12,padding:24,width:400}}>
                <h3 style={{fontWeight:700,marginBottom:12}}>سبب الرفض</h3>
                <textarea value={repRejectReason} onChange={e=>setRepRejectReason(e.target.value)} rows={3}
                  style={{width:'100%',border:'1px solid #E5E7EB',borderRadius:8,padding:8,fontSize:13}} placeholder="اكتب السبب..." />
                <div style={{display:'flex',gap:8,marginTop:16}}>
                  <button onClick={()=>{setRepRejectPO(null); setRepRejectReason('')}} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm">إلغاء</button>
                  <button onClick={async ()=>{ await repHandleApproval(repRejectPO,'reject',repRejectReason); setRepRejectPO(null); setRepRejectReason('') }}
                    className="flex-1 py-2 bg-red-600 text-white rounded-lg text-sm">تأكيد الرفض</button>
                </div>
              </div>
            </div>
          )}

          {/* New / Edit PO Modal */}
          {repModal && (() => {
            const repStage = repModal?.approval_stage || 'draft'
            const repIsLockedForProcurement = !!repModal.id && !(can('PO.Submit') ? ['draft','rejected'].includes(repStage) : can('PO.ApproveCostControl'))
            const repIsCostControlPartial = !!repModal.id && can('PO.ApproveCostControl') && !can('PO.Submit') && ['cost_control','director','approved'].includes(repStage)
            const repIsReadOnly = repIsLockedForProcurement
            return (
            <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.4)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999,padding:20}}>
              <div style={{background:'#fff',borderRadius:12,width:'100%',maxWidth:800,maxHeight:'90vh',overflow:'auto'}}>
                <div style={{padding:'16px 24px',borderBottom:'1px solid #E5E7EB',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <h3 style={{fontWeight:700,fontSize:16}}>{repModal.id ? 'Edit' : 'New'} Rental Equipment PO</h3>
                  <button onClick={()=>setRepModal(null)}><XIcon/></button>
                </div>
                <div style={{padding:24,display:'flex',flexDirection:'column',gap:16}}>
                  {repIsLockedForProcurement && (
                    <div style={{ padding:'10px 14px', background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:8, fontSize:12, color:'#991B1B', fontWeight:600 }}>
                      🔒 This PO has been submitted for approval and cannot be edited now. It will become editable again only if it is Rejected and returns to Draft status.
                    </div>
                  )}
                  {repIsCostControlPartial && (
                    <div style={{ padding:'10px 14px', background:'#EFF6FF', border:'1px solid #BFDBFE', borderRadius:8, fontSize:12, color:'#1E40AF', fontWeight:600 }}>
                      ℹ️ At this stage, you can only edit the Sub Code and Cost Code. Quantity and Rate are locked.
                    </div>
                  )}
                  <fieldset disabled={repIsReadOnly} style={{ display:'contents' }}>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                    <div>
                      <label className="block text-xs text-[#6B7280] mb-1">PO Number *</label>
                      <input value={repModal.po_number} onChange={e=>setRepModal(m=>({...m,po_number:e.target.value}))} disabled={repIsCostControlPartial}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-100" />
                    </div>
                    <div>
                      <label className="block text-xs text-[#6B7280] mb-1">Vendor / Supplier *</label>
                      <input value={repModal.vendor} onChange={e=>setRepModal(m=>({...m,vendor:e.target.value}))} disabled={repIsCostControlPartial}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-100" />
                    </div>
                    <div>
                      <label className="block text-xs text-[#6B7280] mb-1">Cost Code</label>
                      <select value={repModal.cost_code_id||''} onChange={e=>setRepModal(m=>({...m,cost_code_id:e.target.value}))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                        <option value="">—</option>
                        {repCostCodes.map(c=><option key={c.id} value={c.id}>{c.code} — {c.description}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-[#6B7280] mb-1">Issue Date</label>
                      <input type="date" value={repModal.issue_date||''} onChange={e=>setRepModal(m=>({...m,issue_date:e.target.value}))} disabled={repIsCostControlPartial}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-100" />
                    </div>
                    <div>
                      <label className="block text-xs text-[#6B7280] mb-1">VAT %</label>
                      <input type="number" value={repModal.vat_pct} onChange={e=>setRepModal(m=>({...m,vat_pct:e.target.value}))} disabled={repIsCostControlPartial}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-100" />
                    </div>
                    <div>
                      <label className="block text-xs text-[#6B7280] mb-1">Currency</label>
                      <select value={repModal.currency || selectedProject?.currency || 'SAR'}
                        onChange={e=>setRepModal(m=>({...m,currency:e.target.value}))}
                        disabled={repIsCostControlPartial || !!repModal.id}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-100">
                        {currencies.map(c => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
                      </select>
                      {repModal.currency && selectedProject?.currency && repModal.currency !== selectedProject.currency && (
                        <p className="text-xs mt-1" style={{ color:'#1A7A4A' }}>
                          Rate: 1 {repModal.currency} = {repModal.exchange_rate_applied} {selectedProject.currency}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs text-[#6B7280] mb-1">Contract Ref</label>
                      <input value={repModal.contract_ref||''} onChange={e=>setRepModal(m=>({...m,contract_ref:e.target.value}))} disabled={repIsCostControlPartial}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-100" />
                    </div>
                  </div>

                  {/* Line Items */}
                  <div>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                      <label className="text-xs font-semibold text-[#6B7280]">Equipment Line Items</label>
                      {!repIsCostControlPartial && (
                        <button onClick={repAddLine} className="text-xs text-orange-600 font-medium">+ Add Line</button>
                      )}
                    </div>
                    <table style={{width:'100%',fontSize:12,borderCollapse:'collapse'}}>
                      <thead>
                        <tr style={{background:'#F9FAFB'}}>
                          <th style={{padding:'6px 8px',textAlign:'left'}}>Sub Code</th>
                          <th style={{padding:'6px 8px',textAlign:'left'}}>Equipment Description</th>
                          <th style={{padding:'6px 8px',textAlign:'left'}}>Cost Code</th>
                          <th style={{padding:'6px 8px',textAlign:'left'}}>Cost Code Description</th>
                          <th style={{padding:'6px 8px',textAlign:'left',color:'#7C3AED'}}>Resource Code</th>
                          <th style={{padding:'6px 8px',textAlign:'left',color:'#7C3AED'}}>Resource Description</th>
                          <th style={{padding:'6px 8px',textAlign:'right'}}>Qty</th>
                          <th style={{padding:'6px 8px',textAlign:'left'}}>Unit</th>
                          <th style={{padding:'6px 8px',textAlign:'right'}}>Rate</th>
                          <th style={{padding:'6px 8px',textAlign:'right'}}>Total Exc.VAT</th>
                          <th style={{padding:'6px 8px'}}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {repModal.lines.map((line, idx) => (
                          <tr key={line._id||idx} style={{borderBottom:'1px solid #F3F4F6'}}>
                            <td style={{padding:'4px'}}>
                              <select value={line.sub_code_id||''} onChange={e=>{
                                  const sc = repSubCodes.find(s=>s.id===e.target.value)
                                  repHLine(idx,'sub_code_id',e.target.value)
                                  repHLine(idx,'equipment_type', sc ? sc.code : '')
                                  repHLine(idx,'cost_code_id', sc ? (sc.costCodeId||'') : '')
                                }}
                                className="w-full border border-gray-300 rounded px-2 py-1 text-xs disabled:bg-gray-100">
                                <option value="">— Select Sub Code —</option>
                                {repSubCodes.map(s=><option key={s.id} value={s.id}>{s.code} — {s.description}</option>)}
                              </select>
                            </td>
                            <td style={{padding:'4px'}}>
                              <input value={line.description||''} onChange={e=>repHLine(idx,'description',e.target.value)} disabled={repIsCostControlPartial}
                                placeholder="e.g. Telehandler 10T Manitou"
                                className="w-full border border-gray-300 rounded px-2 py-1 text-xs disabled:bg-gray-100" />
                              {line.addendum_no && (
                                <span className="ml-1 px-1.5 py-0.5 text-[9px] font-bold uppercase rounded bg-orange-100 text-orange-700">Addendum #{line.addendum_no}</span>
                              )}
                            </td>
                            <td style={{padding:'4px',fontFamily:'monospace',fontWeight:700,color:'#1A7A4A',fontSize:12}}>
                              {repSubCodes.find(s=>s.id===line.sub_code_id)?.costCode || repCostCodes.find(c=>c.id===line.cost_code_id)?.code || '—'}
                            </td>
                            <td style={{padding:'4px',color:'#6B7280',fontSize:11}}>
                              {repSubCodes.find(s=>s.id===line.sub_code_id)?.costCodeDescription || repCostCodes.find(c=>c.id===line.cost_code_id)?.description || '—'}
                            </td>
                            <td style={{padding:'4px',fontFamily:'monospace',color:'#7C3AED',fontSize:11}}>
                              {repSubCodes.find(s=>s.id===line.sub_code_id)?.resourceCode || '—'}
                            </td>
                            <td style={{padding:'4px',color:'#7C3AED',fontSize:11}}>
                              {repSubCodes.find(s=>s.id===line.sub_code_id)?.resourceCodeDescription || '—'}
                            </td>
                            <td style={{padding:'4px'}}>
                              <input type="number" value={line.quantity} onChange={e=>repHLine(idx,'quantity',e.target.value)} disabled={repIsCostControlPartial}
                                className="w-full border border-gray-300 rounded px-2 py-1 text-xs text-right disabled:bg-gray-100" />
                            </td>
                            <td style={{padding:'4px'}}>
                              <input list="unit-options" value={line.unit} onChange={e=>repHLine(idx,'unit',e.target.value)} disabled={repIsCostControlPartial}
                                className="w-full border border-gray-300 rounded px-2 py-1 text-xs disabled:bg-gray-100" />
                            </td>
                            <td style={{padding:'4px'}}>
                              <input type="number" value={line.rate} onChange={e=>repHLine(idx,'rate',e.target.value)} disabled={repIsCostControlPartial}
                                className="w-full border border-gray-300 rounded px-2 py-1 text-xs text-right disabled:bg-gray-100" />
                            </td>
                            <td style={{padding:'4px',textAlign:'right',fontWeight:700}}>{line.total_cost ? fmtRep(line.total_cost) : '—'}</td>
                            <td style={{padding:'4px',textAlign:'center'}}>
                              {!repIsCostControlPartial && (
                                <button onClick={()=>repRemoveLine(idx)} className="text-red-500"><TrashIcon/></button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{background:'#F5F1EA',borderTop:'2px solid #DDD9D0'}}>
                          <td colSpan={7} style={{padding:'8px',fontWeight:700,fontSize:11,textTransform:'uppercase',color:'#6B655C'}}>Total</td>
                          <td style={{padding:'8px',textAlign:'right',fontWeight:800,color:'#1A7A4A'}}>
                            {fmtRep(repModal.lines.reduce((s,l)=>s+(parseFloat(l.total_cost)||0),0))}
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                    <datalist id="unit-options">
                      <option value="Hours"/><option value="Days"/><option value="Months"/><option value="LS"/>
                    </datalist>
                  </div>

                  <div>
                    <label className="block text-xs text-[#6B7280] mb-1">Notes</label>
                    <textarea value={repModal.notes||''} onChange={e=>setRepModal(m=>({...m,notes:e.target.value}))} rows={2}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                  </div>
                  </fieldset>
                </div>
                <div className="flex gap-3 p-6 border-t border-[#E5E7EB]">
                  <button onClick={() => setRepModal(null)} className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-[#FAFAFA] text-sm">Cancel</button>
                  <button onClick={repSavePO} disabled={repIsReadOnly}
                    className="flex-1 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed">
                    {repIsReadOnly ? '🔒 Locked' : (repModal.id ? 'Update' : 'Create PO')}
                  </button>
                </div>
              </div>
            </div>
            )
          })()}

          {/* View — Slide-in Panel (same style as PO tab) */}
          {repViewPO && (
            <div style={{position:'fixed',top:0,right:0,width:460,height:'100vh',background:'#fff',borderLeft:'1px solid #E5E7EB',zIndex:250,boxShadow:'-4px 0 24px rgba(0,0,0,.08)',display:'flex',flexDirection:'column'}}>
              <div style={{padding:'16px 18px',borderBottom:'1px solid #E5E7EB',display:'flex',alignItems:'flex-start',justifyContent:'space-between',flexShrink:0}}>
                <div>
                  <div style={{fontSize:14,fontWeight:700,color:'#0F1017'}}>{repViewPO.po_number}</div>
                  <div style={{fontSize:11,color:'#6B7280',marginTop:2}}>{repViewPO.vendor} · {repViewPO.issue_date||'—'}</div>
                </div>
                <button onClick={()=>setRepViewPO(null)}
                  style={{width:28,height:28,borderRadius:6,border:'1px solid #E5E7EB',background:'#fff',color:'#6B7280',display:'flex',alignItems:'center',justifyContent:'center',fontSize:16,cursor:'pointer'}}>✕</button>
              </div>
              <div style={{flex:1,overflowY:'auto',padding:18}}>
                <div style={{marginBottom:16}}>
                  <div style={{fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.08em',color:'#6B7280',marginBottom:8,paddingBottom:6,borderBottom:'1px solid #E5E7EB'}}>Rental Equipment PO Details</div>
                  {[
                    ['PO Number', <span style={{fontFamily:'monospace',fontSize:12,fontWeight:700,color:'#1A7A4A'}}>{repViewPO.po_number}</span>],
                    ['Vendor', repViewPO.vendor],
                    ['Stage', <span style={{padding:'2px 8px',borderRadius:20,fontSize:10,fontWeight:700,background:repViewPO.approval_stage==='approved'?'#DCFCE7':'#F3F4F6',color:repViewPO.approval_stage==='approved'?'#14532D':'#374151',border:'1px solid #E5E7EB'}}>{repViewPO.approval_stage||'draft'}</span>],
                    ['Issue Date', repViewPO.issue_date||'—'],
                    ['Cost Code', repViewPO.cost_codes?.code||'—'],
                    ['VAT %', (repViewPO.vat_pct||15)+'%'],
                    ['Total Exc. VAT', <span style={{fontSize:15,fontWeight:800,color:'#0F1017'}}>{fmtRep(repViewPO.total_exc_vat)}</span>],
                    ['Total Inc. VAT', <span style={{fontSize:15,fontWeight:800,color:'#0F1017'}}>{fmtRep(repViewPO.total_inc_vat)}</span>],
                  ].map(([label, value], i) => (
                    <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'6px 0',fontSize:12.5}}>
                      <span style={{color:'#6B7280'}}>{label}</span>
                      <span style={{fontWeight:600,color:'#0F1017'}}>{value}</span>
                    </div>
                  ))}
                </div>
                {repViewPO.approval_stage === 'rejected' && repViewPO.rejection_reason && (
                  <div style={{padding:'10px 12px',background:'#FEF2F2',border:'1px solid #FECACA',borderRadius:6,fontSize:12,color:'#991B1B',marginBottom:16}}>
                    <span style={{fontWeight:600}}>سبب الرفض: </span>{repViewPO.rejection_reason}
                  </div>
                )}
                {repViewPO.notes && (
                  <div style={{padding:'10px 12px',background:'#FFFBEB',border:'1px solid #FDE68A',borderRadius:6,fontSize:12,color:'#92400E',marginBottom:16}}>
                    <span style={{fontWeight:600}}>Notes: </span>{repViewPO.notes}
                  </div>
                )}
                <div style={{display:'flex',gap:8,marginTop:8}}>
                  <button onClick={()=>{repOpenEdit(repViewPO); setRepViewPO(null)}}
                    style={{flex:1,padding:'8px',borderRadius:6,border:'1px solid #E5E7EB',background:'#fff',color:'#0F1017',fontSize:12,fontWeight:600,cursor:'pointer'}}>
                    Edit PO
                  </button>
                  <button onClick={()=>setRepPrintPO(repViewPO.id)}
                    style={{padding:'8px 14px',borderRadius:6,border:'1px solid #1B3A6B',background:'#1B3A6B',color:'#fff',fontSize:12,fontWeight:600,cursor:'pointer'}}>
                    🖨 Print
                  </button>
                  {(repViewPO.approval_stage||'draft')==='draft' && (
                    <button onClick={()=>{repDeletePO(repViewPO.id); setRepViewPO(null)}}
                      style={{padding:'8px 14px',borderRadius:6,border:'1px solid #FECACA',background:'#FEF2F2',color:'#DC2626',fontSize:12,fontWeight:600,cursor:'pointer'}}>
                      Delete
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
          {repViewPO && <div onClick={()=>setRepViewPO(null)} style={{position:'fixed',inset:0,zIndex:249}}/>}

          {/* Lines Modal */}
          {repLinesModal && (
            <div style={{position:'fixed',inset:0,zIndex:400,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',padding:24}}>
              <div style={{background:'#fff',borderRadius:12,width:'100%',maxWidth:900,maxHeight:'85vh',display:'flex',flexDirection:'column',boxShadow:'0 20px 60px rgba(0,0,0,0.2)'}}>
                <div style={{padding:'16px 20px',borderBottom:'1px solid #E5E7EB',display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0}}>
                  <div>
                    <div style={{fontSize:14,fontWeight:700,color:'#0F1017'}}>Line Items — {repLinesModal.po_number}</div>
                    <div style={{fontSize:11,color:'#6B7280',marginTop:2}}>{repLinesModal.vendor} · {repLinesModal.issue_date||'—'} · {repLinesModal.approval_stage||'draft'}</div>
                  </div>
                  <button onClick={()=>setRepLinesModal(null)}
                    style={{width:28,height:28,borderRadius:6,border:'1px solid #E5E7EB',background:'#fff',color:'#6B7280',fontSize:16,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>✕</button>
                </div>
                {(() => {
                  // Rental Equipment PO comparison cards -- same visual
                  // pattern/styling as the regular PO tab's Card 1/2/3,
                  // grouped by resource_code via repSubCodes (no
                  // Direct + resourceCode -> Resource Code comparison
                  // (unchanged). Otherwise, if the line has a costCode
                  // (from repSubCodes, same source as before) -> Indirect
                  // Cost Code comparison. Only a line with NEITHER is
                  // truly unmapped/legacy -- per explicit correction, a
                  // missing resourceCode alone is no longer "no
                  // comparison", it falls back to its Cost Code.
                  const lines = repLines[repLinesModal.id] || []
                  const scByCode = {}
                  repSubCodes.forEach(s => { scByCode[normSC(s.code)] = s })
                  const resolved = lines.map(l => {
                    const sc = scByCode[normSC(l.equipment_type)]
                    const resourceCode = sc?.resourceCode || null
                    const costCode = sc?.costCode || null
                    if (resourceCode) return { line: l, key: normSC(resourceCode), kind: 'resource', label: resourceCode }
                    if (costCode) return { line: l, key: normSC(costCode), kind: 'costcode', label: costCode }
                    return { line: l, key: null, kind: 'unknown', label: l.equipment_type || '—' }
                  })
                  const allGroupKeys = [...new Set(resolved.map(x => x.key || `__unknown__${x.label}`))]
                  const hasAnyGroup = resolved.some(x => x.key)
                  if (!hasAnyGroup) return null

                  // Classification pass (no new calculation logic -- reuses
                  // the exact same Budget/Committed/PO-value sums computed
                  // per-card below) used to build the selector options,
                  // the over/within summary, and the default selection.
                  const groupMeta = allGroupKeys.map(groupKey => {
                    const group = resolved.filter(x => (x.key || `__unknown__${x.label}`) === groupKey)
                    const first = group[0]
                    if (first.kind === 'unknown') return { groupKey, label: first.label, kind: 'unknown', over: false }
                    if (first.kind === 'costcode') {
                      const b = repIndirectBudgetByCostCode[first.key] || 0
                      const c = repIndirectCommittedByCostCode[first.key] || 0
                      const v = group.reduce((s,x)=>s+(parseFloat(x.line.total_cost)||0),0)
                      return { groupKey, label: first.label, kind: 'indirect', over: v > (b - c) }
                    }
                    const b = repBudgetQtyByResource[first.key] || 0
                    const c = repCommittedQtyByResource[first.key] || 0
                    const q = group.reduce((s,x)=>s+(parseFloat(x.line.quantity)||0),0)
                    return { groupKey, label: first.label, kind: 'direct', over: q > (b - c) }
                  })
                  const overCount = groupMeta.filter(g=>g.over).length
                  const withinCount = groupMeta.filter(g=>!g.over && g.kind!=='unknown').length

                  // Default selection: first Over Budget item, else first
                  // available item -- computed once, applied via effect-like
                  // inline check (no separate useEffect needed since this
                  // IIFE re-runs on every render of the open modal).
                  const activeKey = repSelectedCardKey && allGroupKeys.includes(repSelectedCardKey)
                    ? repSelectedCardKey
                    : (groupMeta.find(g=>g.over)?.groupKey || groupMeta[0]?.groupKey || null)
                  if (activeKey !== repSelectedCardKey) {
                    // Sync default into state without causing a render loop
                    // (only fires once per modal open, since subsequent
                    // renders will have repSelectedCardKey already set).
                    setTimeout(() => setRepSelectedCardKey(activeKey), 0)
                  }
                  const groupKeys = activeKey ? [activeKey] : []

                  return (
                    <div style={{padding:'12px 16px',background:'#F9FAFB',borderBottom:'1px solid #E5E7EB',flexShrink:0}}>
                      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:8,marginBottom:8}}>
                        <div style={{fontSize:10,fontWeight:700,textTransform:'uppercase',color:'#6B7280'}}>
                          Budget vs Commitment Check
                        </div>
                        <div style={{fontSize:11,color:'#6B7280'}}>
                          {overCount>0 && <span style={{color:'#DC2626',fontWeight:700}}>{overCount} Over Budget</span>}
                          {overCount>0 && withinCount>0 && ' · '}
                          {withinCount>0 && <span style={{color:'#1A7A4A',fontWeight:700}}>{withinCount} Within Budget</span>}
                        </div>
                      </div>
                      <select value={activeKey||''} onChange={e=>setRepSelectedCardKey(e.target.value)}
                        style={{padding:'6px 10px',borderRadius:6,border:'1px solid #E5E7EB',fontSize:12,fontWeight:600,color:'#0F1017',background:'#fff',marginBottom:10,minWidth:240}}>
                        {groupMeta.map(g => (
                          <option key={g.groupKey} value={g.groupKey}>
                            {g.label}{g.kind!=='unknown' ? (g.over ? ' • Over Budget' : ' • Within Budget') : ' • Unmapped'}
                          </option>
                        ))}
                      </select>
                      {groupKeys.length === 0 ? (
                        <div style={{fontSize:11,color:'#9CA3AF',padding:'8px 0'}}>No matching items.</div>
                      ) : (
                      <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                        {groupKeys.map(groupKey => {
                          const group = resolved.filter(x => (x.key || `__unknown__${x.label}`) === groupKey)
                          const first = group[0]
                          if (first.kind === 'unknown') {
                            const poQty = group.reduce((s,x)=>s+(parseFloat(x.line.quantity)||0),0)
                            return (
                              <div key={groupKey} style={{background:'#FEF3C7',border:'1px solid #FDE68A',borderRadius:8,padding:'8px 12px',minWidth:180}}>
                                <div style={{fontSize:10,fontWeight:700,color:'#92400E',marginBottom:4}}>⚠ {first.label} — Unmapped</div>
                                <div style={{fontSize:11,color:'#92400E'}}>This PO Qty: {fmt(poQty)}</div>
                              </div>
                            )
                          }
                          const isIndirect = first.kind === 'costcode'

                          // Direct (Resource Code) path -- unchanged from before.
                          if (!isIndirect) {
                          const budgetVal = repBudgetQtyByResource[first.key] || 0
                          const committedVal = repCommittedQtyByResource[first.key] || 0
                          const poVal = group.reduce((s,x)=>s+(parseFloat(x.line.quantity)||0),0)
                          const remaining = budgetVal - committedVal
                          const ok = poVal <= remaining

                          const fc = repForecastByResource[first.key]
                          const forecastValue = fc ? fc.resolvedEac : null
                          const forecastIsBudgetFallback = fc ? !fc.hasEffectiveForecast : false
                          const committedValue = repCommittedValueByResource[first.key] || 0
                          const poValueExcVat = group.reduce((s,x)=>s+(parseFloat(x.line.total_cost)||0),0)
                          const forecastRemaining = forecastValue != null ? forecastValue - committedValue : null
                          const overForecast = forecastRemaining != null && poValueExcVat > forecastRemaining

                          const budgetValue = repBudgetValueByResource[first.key] || 0
                          const budgetValueRemaining = budgetValue - committedValue
                          const overBudgetValue = committedValue > budgetValue

                          return (
                            <React.Fragment key={groupKey}>
                              <div style={{background:ok?'#F0FDF4':'#FEF2F2',border:`1px solid ${ok?'#BBF7D0':'#FECACA'}`,borderRadius:8,padding:'8px 12px',minWidth:180}}>
                                <div style={{fontSize:10,fontWeight:700,color:'#6B7280',marginBottom:4}}>{first.label} — Qty</div>
                                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:2,fontSize:11}}>
                                  <span style={{color:'#6B7280'}}>Budget Qty:</span><span style={{fontWeight:700,textAlign:'right'}}>{fmt(budgetVal)}</span>
                                  <span style={{color:'#6B7280'}}>Committed Qty:</span><span style={{fontWeight:700,color:'#D97706',textAlign:'right'}}>{fmt(committedVal)}</span>
                                  <span style={{color:'#6B7280'}}>This PO Qty:</span><span style={{fontWeight:700,color:'#1B3A6B',textAlign:'right'}}>{fmt(poVal)}</span>
                                  <span style={{color:'#6B7280'}}>Remaining:</span><span style={{fontWeight:700,color:ok?'#1A7A4A':'#DC2626',textAlign:'right'}}>{fmt(remaining)}</span>
                                </div>
                                <div style={{marginTop:6,fontSize:11,fontWeight:700,color:ok?'#1A7A4A':'#DC2626',textAlign:'center'}}>
                                  {ok ? '✓ Within Budget' : '⚠ Over Budget'}
                                </div>
                              </div>

                              <div style={{background:overForecast?'#FEF2F2':'#F0FDF4',border:`1px solid ${overForecast?'#FECACA':'#BBF7D0'}`,borderRadius:8,padding:'8px 12px',minWidth:200}}>
                                <div style={{fontSize:10,fontWeight:700,color:'#6B7280',marginBottom:4}}>{first.label} — Live Forecast (SAR)</div>
                                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:2,fontSize:11}}>
                                  <span style={{color:'#6B7280'}}>Forecast Value:</span>
                                  <span style={{textAlign:'right'}}>
                                    <span style={{fontWeight:700}}>{forecastValue!=null ? fmt(forecastValue) : 'No forecast recorded'}</span>
                                    {forecastIsBudgetFallback && <div style={{fontSize:9,color:'#9CA3AF',fontWeight:400}}>Budget fallback — no forecast decision</div>}
                                  </span>
                                  <span style={{color:'#6B7280'}}>Committed (exc VAT):</span><span style={{fontWeight:700,color:'#D97706',textAlign:'right'}}>{fmt(committedValue)}</span>
                                  <span style={{color:'#6B7280'}}>This PO (exc VAT):</span><span style={{fontWeight:700,color:'#1B3A6B',textAlign:'right'}}>{fmt(poValueExcVat)}</span>
                                  <span style={{color:'#6B7280'}}>Forecast Remaining:</span><span style={{fontWeight:700,color:forecastRemaining==null?'#9CA3AF':(overForecast?'#DC2626':'#1A7A4A'),textAlign:'right'}}>{forecastRemaining!=null ? fmt(forecastRemaining) : '—'}</span>
                                </div>
                                {forecastRemaining!=null && (
                                  <div style={{marginTop:6,fontSize:11,fontWeight:700,color:overForecast?'#DC2626':'#1A7A4A',textAlign:'center'}}>
                                    {overForecast ? '⚠ Over Forecast' : '✓ Within Forecast'}
                                  </div>
                                )}
                              </div>

                              <div style={{background:overBudgetValue?'#FEF2F2':'#F0FDF4',border:`1px solid ${overBudgetValue?'#FECACA':'#BBF7D0'}`,borderRadius:8,padding:'8px 12px',minWidth:200}}>
                                <div style={{fontSize:10,fontWeight:700,color:'#6B7280',marginBottom:4}}>{first.label} — Budget (SAR)</div>
                                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:2,fontSize:11}}>
                                  <span style={{color:'#6B7280'}}>Budget Value:</span><span style={{fontWeight:700,textAlign:'right'}}>{fmt(budgetValue)}</span>
                                  <span style={{color:'#6B7280'}}>Committed (exc VAT):</span><span style={{fontWeight:700,color:'#D97706',textAlign:'right'}}>{fmt(committedValue)}</span>
                                  <span style={{color:'#6B7280'}}>This PO (exc VAT):</span><span style={{fontWeight:700,color:'#1B3A6B',textAlign:'right'}}>{fmt(poValueExcVat)}</span>
                                  <span style={{color:'#6B7280'}}>Remaining:</span><span style={{fontWeight:700,color:overBudgetValue?'#DC2626':'#1A7A4A',textAlign:'right'}}>{fmt(budgetValueRemaining)}</span>
                                </div>
                                <div style={{marginTop:6,fontSize:11,fontWeight:700,color:overBudgetValue?'#DC2626':'#1A7A4A',textAlign:'center'}}>
                                  {overBudgetValue ? '⚠ Over Budget' : '✓ Within Budget'}
                                </div>
                              </div>
                            </React.Fragment>
                          )
                          }

                          // Indirect (Cost Code) fallback path -- reuses the
                          // same tender_indirect_items / commitment-by-
                          // costCode maps computed above, same convention
                          // as the original PO tab's Indirect card (value
                          // only, exc VAT, no qty concept).
                          const indBudgetVal = repIndirectBudgetByCostCode[first.key] || 0
                          const indCommittedVal = repIndirectCommittedByCostCode[first.key] || 0
                          const indPoVal = group.reduce((s,x)=>s+(parseFloat(x.line.total_cost)||0),0)
                          const indRemaining = indBudgetVal - indCommittedVal
                          const indOk = indPoVal <= indRemaining
                          return (
                            <div key={groupKey} style={{background:indOk?'#F0FDF4':'#FEF2F2',border:`1px solid ${indOk?'#BBF7D0':'#FECACA'}`,borderRadius:8,padding:'8px 12px',minWidth:200}}>
                              <div style={{fontSize:10,fontWeight:700,color:'#6B7280',marginBottom:4}}>{first.label} — Indirect (SAR)</div>
                              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:2,fontSize:11}}>
                                <span style={{color:'#6B7280'}}>Budget:</span><span style={{fontWeight:700,textAlign:'right'}}>{fmt(indBudgetVal)}</span>
                                <span style={{color:'#6B7280'}}>Committed (exc VAT):</span><span style={{fontWeight:700,color:'#D97706',textAlign:'right'}}>{fmt(indCommittedVal)}</span>
                                <span style={{color:'#6B7280'}}>This PO (exc VAT):</span><span style={{fontWeight:700,color:'#1B3A6B',textAlign:'right'}}>{fmt(indPoVal)}</span>
                                <span style={{color:'#6B7280'}}>Remaining:</span><span style={{fontWeight:700,color:indOk?'#1A7A4A':'#DC2626',textAlign:'right'}}>{fmt(indRemaining)}</span>
                              </div>
                              <div style={{marginTop:6,fontSize:11,fontWeight:700,color:indOk?'#1A7A4A':'#DC2626',textAlign:'center'}}>
                                {indOk ? '✓ Within Budget' : '⚠ Over Budget'}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                      )}
                    </div>
                  )
                })()}
                <div style={{flex:1,overflowY:'auto'}}>
                  {(repLines[repLinesModal.id]||[]).length === 0 ? (
                    <div style={{padding:32,textAlign:'center',color:'#9CA3AF',fontSize:13}}>No line items</div>
                  ) : (
                    <table style={{width:'100%',borderCollapse:'collapse',fontSize:12.5}}>
                      <thead>
                        <tr style={{background:'#F9FAFB',borderBottom:'1px solid #E5E7EB'}}>
                          <th style={{padding:'9px 12px',textAlign:'left',color:'#6B7280',fontWeight:700}}>Sub Code</th>
                          <th style={{padding:'9px 12px',textAlign:'left',color:'#6B7280',fontWeight:700}}>Sub Code Description</th>
                          <th style={{padding:'9px 12px',textAlign:'left',color:'#6B7280',fontWeight:700}}>Equipment Description</th>
                          <th style={{padding:'9px 12px',textAlign:'left',color:'#6B7280',fontWeight:700}}>Cost Code</th>
                          <th style={{padding:'9px 12px',textAlign:'left',color:'#6B7280',fontWeight:700}}>Cost Code Description</th>
                          <th style={{padding:'9px 12px',textAlign:'left',color:'#7C3AED',fontWeight:700}}>Resource Code</th>
                          <th style={{padding:'9px 12px',textAlign:'left',color:'#7C3AED',fontWeight:700}}>Resource Description</th>
                          <th style={{padding:'9px 12px',textAlign:'right',color:'#6B7280',fontWeight:700}}>Qty</th>
                          <th style={{padding:'9px 12px',textAlign:'left',color:'#6B7280',fontWeight:700}}>Unit</th>
                          <th style={{padding:'9px 12px',textAlign:'right',color:'#6B7280',fontWeight:700}}>Rate</th>
                          <th style={{padding:'9px 12px',textAlign:'right',color:'#6B7280',fontWeight:700}}>Total Exc.VAT</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(repLines[repLinesModal.id]||[]).map(l => {
                          const subInfo = repSubCodes.find(s=>s.code.trim().toUpperCase()===String(l.equipment_type||'').trim().toUpperCase())
                          return (
                          <tr key={l.id} style={{borderBottom:'1px solid #F3F4F6'}}>
                            <td style={{padding:'9px 12px',fontWeight:700,color:'#2563EB'}}>{l.equipment_type}</td>
                            <td style={{padding:'9px 12px',color:'#6B7280'}}>{subInfo?.description || '—'}</td>
                            <td style={{padding:'9px 12px',color:'#6B7280'}}>
                              {l.description || '—'}
                              {l.addendum_no && (
                                <span style={{marginLeft:6,padding:'1px 6px',fontSize:9,fontWeight:700,textTransform:'uppercase',borderRadius:4,background:'#FFEDD5',color:'#C2410C'}}>Addendum #{l.addendum_no}</span>
                              )}
                            </td>
                            <td style={{padding:'9px 12px',color:'#6B7280'}}>{subInfo?.costCode || repCostCodes.find(c=>c.id===l.cost_code_id)?.code || '—'}</td>
                            <td style={{padding:'9px 12px',color:'#6B7280'}}>{subInfo?.costCodeDescription || repCostCodes.find(c=>c.id===l.cost_code_id)?.description || '—'}</td>
                            <td style={{padding:'9px 12px',color:'#7C3AED'}}>{subInfo?.resourceCode || '—'}</td>
                            <td style={{padding:'9px 12px',color:'#7C3AED'}}>{subInfo?.resourceCodeDescription || '—'}</td>
                            <td style={{padding:'9px 12px',textAlign:'right'}}>{l.quantity}</td>
                            <td style={{padding:'9px 12px'}}>{l.unit}</td>
                            <td style={{padding:'9px 12px',textAlign:'right'}}>{l.rate}</td>
                            <td style={{padding:'9px 12px',textAlign:'right',fontWeight:700,color:'#1A7A4A'}}>{fmtRep(l.total_cost)}</td>
                          </tr>
                          )
                        })}
                      </tbody>
                      <tfoot style={{background:'#EFF6FF',borderTop:'2px solid #BFDBFE'}}>
                        <tr>
                          <td colSpan={5} style={{padding:'10px 12px',fontSize:11,fontWeight:700,color:'#3B82F6',textTransform:'uppercase'}}>Total</td>
                          <td style={{padding:'10px 12px',textAlign:'right',fontWeight:800,fontSize:13,color:'#1A7A4A'}}>
                            {fmtRep((repLines[repLinesModal.id]||[]).reduce((s,l)=>s+(parseFloat(l.total_cost)||0),0))}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Print via shared PrintDocument component */}
          {repPrintPO && <PrintDocument docType="rental_equipment" docId={repPrintPO} onClose={()=>setRepPrintPO(null)}/>}
        </div>
      )}
    </div>
  )
}
